import { useAuth } from "@/hooks/useAuthState";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableActionButton,
  ResponsiveTable,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import UserProfileIntelligence from "@/components/admin/UserProfileIntelligence";
import {
  Users,
  Trophy,
  TrendingUp,
  Calendar,
  Edit,
  Trash2,
  Plus,
  Search,
  Download,
  Ban,
  Eye,
  EyeOff,
  BarChart3,
  Activity,
  HelpCircle,
  Home,
  Share2,
  Target,
  Award,
  Map,
  Copy,
  User,
  Zap,
  Settings,
  Save,
  RotateCcw,
  Send,
  CheckCircle,
  XCircle,
  Check,
  ExternalLink,
  FileText,
  Filter,
  Menu,
  X,
  ShieldCheck,
  Shield,
  UserX,
  UserCheck,
  Upload,
  AlertTriangle,
  Globe,
  Clock,
  Phone,
  Info,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  Brain,
  MessageCircle,
  ArrowLeft,
  FileCode,
  Maximize,
  Mail,
  MapPin,
  Monitor,
  AlertCircle,
  PlayCircle,
  Sparkles,
  GitBranch,
  Facebook,
  Gift,
} from "lucide-react";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useState, useEffect, ChangeEvent, FormEvent, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { sanitizeEmailContent } from "@/lib/sanitize";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { format, subDays, subMonths, formatDistanceToNow } from "date-fns";
import { DateRange } from "react-day-picker";
import { supabase } from "@/lib/supabase";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  competitionFormSchema,
  type CompetitionForm,
  AiConversation,
  AiMessage,
  insertTermsAndConditionsSchema,
  type TermsAndConditions,
  type InsertTermsAndConditions,
  type UserSegment,
  type GlobalQuestion,
  type BrandQuestion,
  type BehavioralQuestion,
} from "@shared/schema";
import QuestionCategorizationTab, {
  CATEGORY_DEFINITIONS,
} from "@/components/admin/QuestionCategorizationTab";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

import Footer from "@/components/footer";
import { useRef } from "react";
import BulkImportPage from "@/pages/bulk-import";

// Type definitions for admin dashboard
interface Competition {
  id: number;
  title: string;
  description: string;
  category: string;
  type: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  entryCount: number;
  status: string;
  prize1?: string;
  prize2?: string;
  prize3?: string;
  prizeValue?: number;
  shareText?: string;
  bannerImageUrl?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  location?: string;
  interests?: string[];
  gender?: string;
  dateOfBirth?: string;
  createdAt: string;
  totalEntries?: number;
  marketingConsent?: boolean;
  role: string;
  totalShares?: number;
  socialShares?: any[];
  referrals?: any[];
  platformsUsed?: string[];
  competitionsShared?: number;
  lastShareDate?: string;
  isActive?: boolean;
  totalAnswers?: number;
  lastActivity?: string;
  // Enhanced login tracking fields
  lastLoginIp?: string;
  lastLoginLocation?: string;
  lastLoginTimestamp?: string;
  lastLoginDevice?: string;
  lastLoginBrowser?: string;
  lastLoginOS?: string;
  lastLoginUserAgent?: string;
  loginHistory?: any[];
}

type FacebookLeadFormRecord = {
  id: number;
  formId: string;
  pageId: string | null;
  name: string | null;
  purpose: string;
  description: string | null;
  competitionId: number | null;
  questions: Record<string, unknown> | null;
  metadata: Record<string, any> | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

type FacebookLeadSubmissionRecord = {
  id: number;
  leadgenId: string;
  formId: string | null;
  formRecordId: number | null;
  formPurpose: string | null;
  competitionId: number | null;
  pageId: string | null;
  adId: string | null;
  campaignId: string | null;
  email: string | null;
  userId: number | null;
  answers: Record<string, string> | null;
  processingStatus: "received" | "processed" | "failed" | "skipped";
  processingError: string | null;
  createdAt: string;
  processedAt: string | null;
  updatedAt: string;
  payload?: {
    field_data?: Array<{
      name: string;
      values: string[];
    }>;
  } | null;
};

interface AnalyticsData {
  facebookShares: number;
  xShares: number;
  whatsappShares: number;
  twitterShares: number;
  avgEntriesPerUser: number;
  popularCategory: string;
  activeUsersToday: number;
  totalShares: number;
  uniqueSharers: number;
  uniqueSocialSharers: number;
  uniqueReferrers: number;
}

interface PlatformData {
  platform: string;
  totalShares: number;
  uniqueSharers: number;
  competitionsShared: number;
}

interface StatsData {
  totalUsers: number;
  totalCompetitions: number;
  totalEntries: number;
  totalShares: number;
  uniqueVerifiedBetfredSignups: number;
}

interface UserShareData {
  id: number;
  userId?: number;
  platform: string;
  competitionId?: number;
  createdAt: string;
  competitionTitle?: string;
  activityType?: string;
  referredUser?: any;
}

interface DetailedAnalyticsData {
  totalUsers: number;
  totalCompetitions: number;
  totalEntries: number;
  totalShares: number;
  userGrowth: any[];
  competitionGrowth: any[];
  entryActivity: any[];
}

interface TrendsData {
  date: string;
  totalShares: number;
  facebook: number;
  x: number;
  whatsapp: number;
}
import SocialMediaManagement from "@/components/social-media-management";
import Roadmap from "@/pages/roadmap";
import { TwoFactorSetup } from "@/components/TwoFactorSetup";
import { TwoFactorVerification } from "@/components/TwoFactorVerification";
import AdminTrackingCodes from "@/pages/admin-tracking-codes";
import AdminTriggerManagement from "@/pages/admin-trigger-management";
import AdminSegmentation from "@/pages/admin-segmentation";
import { useAdminCacheManager } from "@/hooks/use-admin-cache";

// CRM Components
import CRMTemplatesTab from "@/components/crm/CRMTemplatesTab";
import CRMCampaignsTab from "@/components/crm/CRMCampaignsTab";
import CRMCriteriaTab from "@/components/crm/CRMCriteriaTab";
import CRMJourneysTab from "@/components/crm/CRMJourneysTab";
import CRMAnalyticsTab from "@/components/crm/CRMAnalyticsTab";
import CRMConversionsTab from "@/components/crm/CRMConversionsTab";
import SMSConfigurationTab from "@/components/admin/SMSConfigurationTab";
import SMSInviteTrackingTab from "@/components/admin/SMSInviteTrackingTab";
import JourneySMSTrackingTab from "@/components/admin/JourneySMSTrackingTab";
import SendEmailsTab from "@/components/crm/SendEmailsTab";
import EmailReports from "@/pages/EmailReports";

// Journey Builder Component
import JourneyBuilder from "@/pages/JourneyBuilder";

// Google Analytics Dashboard
import GoogleAnalyticsDashboard from "@/components/GoogleAnalyticsDashboard";

// AI Analysis Tab Component
const AIAnalysisTab = () => {
  const { toast } = useToast();
  const [selectedAnalysisType, setSelectedAnalysisType] =
    useState("question");
  const [selectedQuestionId, setSelectedQuestionId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");

  // Analysis mutations
  const analyzeQuestionMutation = useMutation({
    mutationFn: async ({
      questionId,
      questionType,
    }: {
      questionId: number;
      questionType: string;
    }) => {
      const response = await apiRequest("POST", "/api/analyze/question", {
        body: { questionId, questionType },
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Question analysis completed",
        description: "AI has analyzed the question successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/segments"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/analysis/question-results"],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Analysis failed",
        description: error.message || "Failed to analyze question",
        variant: "destructive",
      });
    },
  });

  const analyzeUserMutation = useMutation({
    mutationFn: async ({ userId }: { userId: number }) => {
      const response = await apiRequest("POST", "/api/analyze/user", {
        body: { targetUserId: userId },
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "User analysis completed",
        description: "AI has analyzed the user successfully",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/analysis/user-results"],
      });
    },
    onError: (error: any) => {
      toast({
        title: "User analysis failed",
        description: error.message || "Failed to analyze user",
        variant: "destructive",
      });
    },
  });

  const generateInsightsMutation = useMutation({
    mutationFn: async ({
      questionId,
      questionType,
    }: {
      questionId: number;
      questionType: string;
    }) => {
      const response = await apiRequest(
        "POST",
        "/api/analyze/question-insights",
        { body: { questionId, questionType } }
      );
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Insights generated",
        description: `Generated ${data.length} behavioral insights`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/analysis/insights"] });
    },
    onError: (error: any) => {
      toast({
        title: "Insight generation failed",
        description: error.message || "Failed to generate insights",
        variant: "destructive",
      });
    },
  });

  // Analysis result queries
  const {
    data: questionAnalyses = [],
    isLoading: questionAnalysesLoading,
  } = useQuery<any[]>({
    queryKey: ["/api/analysis/question-results"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/analysis/question-results");
      return response.json();
    },
  });

  const {
    data: questionInsights = [],
    isLoading: insightsLoading,
  } = useQuery<any[]>({
    queryKey: ["/api/analysis/insights"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/analysis/insights");
      return response.json();
    },
  });

  const { data: userAnalyses = [], isLoading: userAnalysesLoading } = useQuery<
    any[]
  >({
    queryKey: ["/api/analysis/user-results"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/analysis/user-results");
      return response.json();
    },
  });

  // Fetch questions for analysis
  // Only show questions that have at least one answer
  const {
    data: analysisQuestions,
    isLoading: questionsLoading,
  } = useQuery<any>({
    queryKey: ["/api/admin/analysis/questions-with-answers"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        "/api/admin/analysis/questions-with-answers"
      );
      return response.json();
    },
  });

  const allQuestions =
    [
      ...(analysisQuestions?.competition || []),
      ...(analysisQuestions?.global || []),
      ...(analysisQuestions?.brand || []),
      ...(analysisQuestions?.behavioral || []),
    ] || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <MessageCircle className="h-6 w-6" />
          AI Analysis
        </h2>
        <p className="text-gray-600 mt-1">
          Generate insights from user responses for better campaigns
        </p>
      </div>

      {/* Analysis Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-blue-600" />
            Analysis Controls
          </CardTitle>
          <CardDescription>
            Trigger AI-powered analysis to understand user behavior and generate
            segments
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs
            value={selectedAnalysisType}
            onValueChange={setSelectedAnalysisType}
          >
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="question">Question Analysis</TabsTrigger>
              <TabsTrigger value="user">Individual User</TabsTrigger>
              <TabsTrigger value="insights">Generate Insights</TabsTrigger>
            </TabsList>

            <TabsContent value="question" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="question-select">Select Question</Label>
                  <Select
                    value={selectedQuestionId}
                    onValueChange={setSelectedQuestionId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a question to analyze" />
                    </SelectTrigger>
                    <SelectContent>
                      {questionsLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading questions...
                        </SelectItem>
                      ) : (
                        allQuestions.map((question) => (
                          <SelectItem
                            key={`${question.type}-${question.id}`}
                            value={`${question.id}|${question.type}`}
                          >
                            [{question.type.toUpperCase()}]{" "}
                            {question.questionText.substring(0, 60)}...
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={() => {
                      if (selectedQuestionId) {
                        const [questionId, questionType] =
                          selectedQuestionId.split("|");
                        analyzeQuestionMutation.mutate({
                          questionId: Number(questionId),
                          questionType,
                        });
                      }
                    }}
                    disabled={
                      analyzeQuestionMutation.isPending || !selectedQuestionId
                    }
                    className="w-full"
                    data-testid="button-analyze-question"
                  >
                    {analyzeQuestionMutation.isPending ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <HelpCircle className="h-4 w-4 mr-2" />
                        Analyze Question
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Question Analysis</AlertTitle>
                <AlertDescription>
                  AI will analyze what this question measures, identify response
                  patterns, and provide insights about user behavior.
                </AlertDescription>
              </Alert>
            </TabsContent>

            <TabsContent value="user" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="user-id">User ID</Label>
                  <Input
                    id="user-id"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    placeholder="Enter user ID to analyze"
                    type="number"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Analyze individual user's responses and assign to segments
                  </p>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={() => {
                      if (selectedUserId) {
                        analyzeUserMutation.mutate({
                          userId: Number(selectedUserId),
                        });
                      }
                    }}
                    disabled={analyzeUserMutation.isPending || !selectedUserId}
                    className="w-full"
                    data-testid="button-analyze-user"
                  >
                    {analyzeUserMutation.isPending ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <User className="h-4 w-4 mr-2" />
                        Analyze User
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Individual User Analysis</AlertTitle>
                <AlertDescription>
                  AI will profile this specific user based on their responses
                  and assign them to appropriate segments.
                </AlertDescription>
              </Alert>
            </TabsContent>

            <TabsContent value="insights" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="insights-question-select">
                    Select Question for Insights
                  </Label>
                  <Select
                    value={selectedQuestionId}
                    onValueChange={setSelectedQuestionId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a question to generate insights" />
                    </SelectTrigger>
                    <SelectContent>
                      {questionsLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading questions...
                        </SelectItem>
                      ) : (
                        allQuestions.map((question) => (
                          <SelectItem
                            key={`insight-${question.type}-${question.id}`}
                            value={`${question.id}|${question.type}`}
                          >
                            [{question.type.toUpperCase()}]{" "}
                            {question.questionText.substring(0, 60)}...
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={() => {
                      if (selectedQuestionId) {
                        const [questionId, questionType] =
                          selectedQuestionId.split("|");
                        generateInsightsMutation.mutate({
                          questionId: Number(questionId),
                          questionType,
                        });
                      }
                    }}
                    disabled={
                      generateInsightsMutation.isPending || !selectedQuestionId
                    }
                    className="w-full"
                    data-testid="button-generate-insights"
                  >
                    {generateInsightsMutation.isPending ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <TrendingUp className="h-4 w-4 mr-2" />
                        Generate Insights
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Behavioral Insights</AlertTitle>
                <AlertDescription>
                  AI will analyze answer patterns for this question and generate
                  actionable behavioral insights.
                </AlertDescription>
              </Alert>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Question Analysis Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-purple-600" />
            Question Analysis Results
          </CardTitle>
          <CardDescription>
            AI insights about what questions measure and response patterns
          </CardDescription>
        </CardHeader>
        <CardContent>
          {questionAnalysesLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <div className="animate-spin h-6 w-6 border-2 border-purple-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                <p className="text-gray-600">Loading question analyses...</p>
              </div>
            </div>
          ) : questionAnalyses && questionAnalyses.length > 0 ? (
            <div className="space-y-4">
              {questionAnalyses.map((analysis: any, index: number) => (
                <Card
                  key={analysis.id}
                  className="border-l-4 border-l-purple-500"
                >
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg">
                          {analysis.questionText}
                        </CardTitle>
                        <Badge variant="outline" className="mt-1">
                          {analysis.questionType.toUpperCase()}
                        </Badge>
                      </div>
                      <Badge variant="secondary">
                        {analysis.confidence}% confidence
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-1">
                          What this question measures:
                        </h4>
                        <p className="text-sm text-gray-600">
                          {analysis.purpose ||
                            "AI analysis of question purpose"}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-1">
                          Response patterns:
                        </h4>
                        <p className="text-sm text-gray-600">
                          {analysis.patterns ||
                            "Identified response patterns and trends"}
                        </p>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>
                          Analyzed:{" "}
                          {new Date(analysis.analyzedAt).toLocaleDateString()}
                        </span>
                        <span>Question ID: {analysis.questionId}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <HelpCircle className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="font-medium text-gray-900 mb-2">
                No Question Analyses Yet
              </h3>
              <p className="text-gray-600 mb-4">
                Run question analysis to understand what your questions measure
              </p>
              <Button
                onClick={() => setSelectedAnalysisType("question")}
                variant="outline"
              >
                <Plus className="h-4 w-4 mr-2" />
                Analyze Questions
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generated Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-orange-600" />
            Behavioral Insights
          </CardTitle>
          <CardDescription>
            AI-generated insights from question response patterns
          </CardDescription>
        </CardHeader>
        <CardContent>
          {insightsLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <div className="animate-spin h-6 w-6 border-2 border-orange-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                <p className="text-gray-600">Loading insights...</p>
              </div>
            </div>
          ) : questionInsights && questionInsights.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {questionInsights.map((insight: any, index: number) => (
                <Card
                  key={insight.id}
                  className="border-l-4 border-l-orange-500"
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center justify-between">
                      <span>{insight.insightType}</span>
                      <Badge variant="secondary">
                        {insight.confidence}% confidence
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">
                        {insight.description}
                      </p>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <h5 className="text-xs font-medium text-gray-800 mb-1">
                          Key Finding:
                        </h5>
                        <p className="text-xs text-gray-700">
                          {insight.keyFinding ||
                            "Behavioral pattern identified"}
                        </p>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>
                          Generated:{" "}
                          {new Date(insight.generatedAt).toLocaleDateString()}
                        </span>
                        <span>Question: {insight.questionId}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <TrendingUp className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="font-medium text-gray-900 mb-2">
                No Insights Generated Yet
              </h3>
              <p className="text-gray-600 mb-4">
                Generate behavioral insights from question response patterns
              </p>
              <Button
                onClick={() => setSelectedAnalysisType("insights")}
                variant="outline"
              >
                <Plus className="h-4 w-4 mr-2" />
                Generate Insights
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Individual User Analysis Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-teal-600" />
            User Analysis Results
          </CardTitle>
          <CardDescription>
            Individual user profiles and segment assignments
          </CardDescription>
        </CardHeader>
        <CardContent>
          {userAnalysesLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <div className="animate-spin h-6 w-6 border-2 border-teal-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                <p className="text-gray-600">Loading user analyses...</p>
              </div>
            </div>
          ) : userAnalyses && userAnalyses.length > 0 ? (
            <div className="space-y-4">
              {userAnalyses.map((userAnalysis: any, index: number) => (
                <Card
                  key={userAnalysis.userId}
                  className="border-l-4 border-l-teal-500"
                >
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">
                          User #{userAnalysis.userId}
                        </CardTitle>
                        <p className="text-sm text-gray-600">
                          {userAnalysis.username || "Unknown User"}
                        </p>
                      </div>
                      <Badge variant="secondary">
                        {userAnalysis.confidence}% match confidence
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Assigned Segments:
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {userAnalysis.segments &&
                          userAnalysis.segments.length > 0 ? (
                            userAnalysis.segments.map(
                              (segment: any, idx: number) => (
                                <Badge
                                  key={idx}
                                  variant="outline"
                                  className="text-xs"
                                >
                                  {segment.name} ({segment.confidence}%)
                                </Badge>
                              )
                            )
                          ) : (
                            <span className="text-sm text-gray-500">
                              No segments assigned
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-1">
                          Profile Summary:
                        </h4>
                        <p className="text-sm text-gray-600">
                          {userAnalysis.profileSummary ||
                            "AI-generated user profile based on responses"}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-1">
                          Key Characteristics:
                        </h4>
                        <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                          {userAnalysis.characteristics
                            ? JSON.stringify(
                                userAnalysis.characteristics
                              ).substring(0, 150) + "..."
                            : "Demographics, interests, and behavioral patterns identified"}
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>
                          Analyzed:{" "}
                          {new Date(
                            userAnalysis.analyzedAt
                          ).toLocaleDateString()}
                        </span>
                        <span>
                          Response Count: {userAnalysis.responseCount || 0}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <User className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="font-medium text-gray-900 mb-2">
                No User Analyses Yet
              </h3>
              <p className="text-gray-600 mb-4">
                Analyze individual users to understand their profiles and assign
                segments
              </p>
              <Button
                onClick={() => setSelectedAnalysisType("user")}
                variant="outline"
              >
                <Plus className="h-4 w-4 mr-2" />
                Analyze Users
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Social Shares Analytics Component
const SocialSharesAnalyticsTab = () => {
  const { toast } = useToast();

  // Fetch analytics data with proper typing
  const { data: analyticsData, isLoading: analyticsLoading } =
    useQuery<AnalyticsData>({
      queryKey: ["/api/admin/social-shares/analytics"],
      refetchInterval: 30000, // Refresh every 30 seconds
    });

  const { data: platformData, isLoading: platformLoading } = useQuery<
    PlatformData[]
  >({
    queryKey: ["/api/admin/social-shares/by-platform"],
    refetchInterval: 30000,
  });

  const { data: topSharers, isLoading: sharersLoading } = useQuery<User[]>({
    queryKey: ["/api/admin/social-shares/top-sharers"],
    refetchInterval: 30000,
  });

  const { data: trendsData, isLoading: trendsLoading } = useQuery<TrendsData[]>(
    {
      queryKey: ["/api/admin/social-shares/trends"],
      refetchInterval: 30000,
    }
  );

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showUserShares, setShowUserShares] = useState(false);

  const { data: userShares, isLoading: userSharesLoading } = useQuery<
    UserShareData[]
  >({
    queryKey: ["/api/admin/social-shares/user", selectedUser?.id],
    enabled: !!selectedUser?.id,
  });

  const isLoading =
    analyticsLoading || platformLoading || sharersLoading || trendsLoading;

  // Platform colors for charts
  const platformColors = {
    facebook: "#1877F2",
    twitter: "#1DA1F2",
    x: "#000000",
    whatsapp: "#25D366",
    instagram: "#E4405F",
    linkedin: "#0077B5",
    referrals: "#10B981",
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-gray-600">Loading social shares analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Social & Referral Analytics</h2>
          <p className="text-gray-600">
            Comprehensive viral marketing including social shares and referral
            tracking
          </p>
        </div>
        <Button onClick={() => window.location.reload()} variant="outline">
          <RotateCcw className="h-4 w-4 mr-2" />
          Refresh Data
        </Button>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Total Viral Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {analyticsData?.totalShares || 0}
            </div>
            <div className="text-xs text-gray-500 space-y-1">
              <div>Social: {analyticsData?.facebookShares || 0}</div>
              <div>Referrals: {analyticsData?.xShares || 0}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Social Media Shares
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-900">
              {analyticsData?.facebookShares || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Facebook, X, WhatsApp, etc.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Referrals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {analyticsData?.whatsappShares || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Friend referrals (+35 entries)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {analyticsData?.activeUsersToday || 0}
            </div>
            <div className="text-xs text-gray-500 space-y-1">
              <div>Social: {analyticsData?.facebookShares || 0}</div>
              <div>Referrals: {analyticsData?.whatsappShares || 0}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Unique Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-teal-600">
              {(analyticsData as any)?.uniqueSharers || 0}
            </div>
            <div className="text-xs text-gray-500 space-y-1">
              <div>
                Social: {(analyticsData as any)?.uniqueSocialSharers || 0}
              </div>
              <div>
                Referrers: {(analyticsData as any)?.uniqueReferrers || 0}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Platform Breakdown Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Viral Marketing Performance</CardTitle>
          <CardDescription>
            Social media platforms and referral breakdown with performance
            metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          {platformData && platformData.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      dataKey="totalShares"
                      data={platformData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ platform, totalShares }) =>
                        `${platform}: ${totalShares}`
                      }
                    >
                      {platformData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            platformColors[entry.platform?.toLowerCase()] ||
                            "#8884d8"
                          }
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value, name) => [value, "Shares"]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Platform Table */}
              <div className="space-y-4">
                <h4 className="font-medium">Platform Details</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Platform</TableHead>
                      <TableHead>Shares</TableHead>
                      <TableHead>Unique Users</TableHead>
                      <TableHead>Competitions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {platformData.map((platform) => (
                      <TableRow key={platform.platform}>
                        <TableCell className="font-medium capitalize">
                          {platform.platform}
                        </TableCell>
                        <TableCell>{platform.totalShares}</TableCell>
                        <TableCell>{platform.uniqueSharers}</TableCell>
                        <TableCell>{platform.competitionsShared}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-500">
              <Share2 className="h-8 w-8 mr-2" />
              <span>No platform data available</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sharing Trends Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Sharing Trends</CardTitle>
          <CardDescription>
            Last 30 days sharing activity patterns
          </CardDescription>
        </CardHeader>
        <CardContent>
          {trendsData && trendsData.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <RechartsTooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="dailyShares"
                    stroke="#3b82f6"
                    name="Daily Shares"
                  />
                  <Line
                    type="monotone"
                    dataKey="uniqueSharers"
                    stroke="#10b981"
                    name="Unique Sharers"
                  />
                  <Line
                    type="monotone"
                    dataKey="platformsUsed"
                    stroke="#f59e0b"
                    name="Platforms Used"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-500">
              <TrendingUp className="h-8 w-8 mr-2" />
              <span>No trend data available</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Sharers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Top Sharers</CardTitle>
          <CardDescription>
            Users with the highest sharing activity and viral marketing impact
          </CardDescription>
        </CardHeader>
        <CardContent>
          {topSharers && topSharers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Total Activity</TableHead>
                  <TableHead>Social Shares</TableHead>
                  <TableHead>Referrals</TableHead>
                  <TableHead>Platforms</TableHead>
                  <TableHead>Competitions</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topSharers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{user.username}</div>
                        <div className="text-sm text-gray-500">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-xs text-gray-400">
                          {user.email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className="bg-blue-100 text-blue-800 font-bold"
                      >
                        {user.totalShares}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-center">
                        <div className="text-sm font-medium">
                          {user.socialShares || 0}
                        </div>
                        <div className="text-xs text-blue-900">social</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-center">
                        <div className="text-sm font-medium">
                          {user.referrals || 0}
                        </div>
                        <div className="text-xs text-green-600">referrals</div>
                      </div>
                    </TableCell>
                    <TableCell>{user.platformsUsed || 0}</TableCell>
                    <TableCell>{user.competitionsShared || 0}</TableCell>
                    <TableCell className="text-sm">
                      {formatSADateTime(user.lastShareDate)}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          console.log("View Details clicked for user:", user);
                          setSelectedUser(user);
                          setShowUserShares(true);
                        }}
                        data-testid={`view-details-${user.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-500">
              <Users className="h-8 w-8 mr-2" />
              <span>No sharing activity found</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Shares Details Modal */}
      <Dialog open={showUserShares} onOpenChange={setShowUserShares}>
        <DialogContent className="!max-w-[90vw] !w-[90vw] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedUser?.username}'s Viral Marketing Activity
            </DialogTitle>
            <DialogDescription>
              Detailed history of social shares and referrals for{" "}
              {selectedUser?.firstName} {selectedUser?.lastName}
            </DialogDescription>
          </DialogHeader>

          {userSharesLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
              <span className="ml-2">Loading user shares...</span>
            </div>
          ) : userShares &&
            Array.isArray(userShares) &&
            userShares.length > 0 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-sm text-gray-600">Total Activity</div>
                  <div className="text-lg font-bold">
                    {selectedUser?.totalShares}
                  </div>
                  <div className="text-xs text-gray-500">
                    Social: {selectedUser?.socialShares || 0} | Referrals:{" "}
                    {selectedUser?.referrals || 0}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Platforms Used</div>
                  <div className="text-lg font-bold">
                    {selectedUser?.platformsUsed}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">
                    Competitions Shared
                  </div>
                  <div className="text-lg font-bold">
                    {selectedUser?.competitionsShared}
                  </div>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Activity Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userShares.map((activity) => (
                    <TableRow key={`${activity.activityType}-${activity.id}`}>
                      <TableCell className="text-sm">
                        {formatSADateTime(activity.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="capitalize"
                          style={{
                            borderColor:
                              activity.activityType === "referral"
                                ? "#10B981"
                                : platformColors[
                                    activity.platform?.toLowerCase()
                                  ],
                            color:
                              activity.activityType === "referral"
                                ? "#10B981"
                                : platformColors[
                                    activity.platform?.toLowerCase()
                                  ],
                          }}
                        >
                          {activity.activityType === "referral"
                            ? "Referral"
                            : activity.platform}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {activity.activityType === "referral" ? (
                          <div>
                            <div className="font-medium">
                              {activity.competitionTitle}
                            </div>
                            {activity.referredUser && (
                              <div className="text-xs text-gray-500">
                                Email: {activity.referredUser.email}
                              </div>
                            )}
                          </div>
                        ) : (
                          activity.competitionTitle
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-gray-500">
              <div className="text-center">
                <Share2 className="h-6 w-6 mx-auto mb-2" />
                <span>No viral marketing activity found for this user</span>
                {selectedUser && (
                  <div className="text-xs mt-2 text-gray-400">
                    User ID: {selectedUser.id} | Username:{" "}
                    {selectedUser.username}
                    <br />
                    Expected referrals: {selectedUser.referrals || 0} | Social
                    shares: {selectedUser.socialShares || 0}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admin profile form schema
const adminProfileSchema = z.object({
  firstName: z
    .string()
    .min(2, "First name must be at least 2 characters")
    .optional(),
  lastName: z
    .string()
    .min(2, "Last name must be at least 2 characters")
    .optional(),
  phone: z.string().optional(),
  location: z.string().optional(),
  interests: z.string().optional(),
  gender: z
    .enum(["male", "female", "other", "prefer_not_to_say", ""])
    .optional(),
  dateOfBirth: z.string().optional(),
  marketingConsent: z.boolean().optional(),
});

type AdminProfileForm = z.infer<typeof adminProfileSchema>;

// SA Timezone utility functions
const formatSADate = (date: Date | string | null): string => {
  if (!date) return "";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return dateObj.toLocaleDateString("en-ZA", {
    timeZone: "Africa/Johannesburg",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const formatSADateTime = (date: Date | string | null): string => {
  if (!date) return "";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return dateObj.toLocaleString("en-ZA", {
    timeZone: "Africa/Johannesburg",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatSADateTimeShort = (date: Date | string | null): string => {
  if (!date) return "";
  const dateObj = typeof date === "string" ? new Date(date) : date;

  // Format in SA timezone
  const options: Intl.DateTimeFormatOptions = {
    timeZone: "Africa/Johannesburg",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  const formatted = new Intl.DateTimeFormat("en-ZA", options).format(dateObj);
  return formatted.replace(",", ","); // Ensure clean formatting
};

const formatRelativeTime = (date: Date | string | null): string => {
  if (!date) return "Unknown";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) {
    return "Unknown";
  }
  return formatDistanceToNow(dateObj, { addSuffix: true });
};

const extractSATime = (date: Date | string | null): string => {
  if (!date) return "";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return dateObj.toLocaleTimeString("en-ZA", {
    timeZone: "Africa/Johannesburg",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

// Function to determine competition status
const getCompetitionStatus = (competition: any) => {
  // Use the same database timezone logic for consistency
  const now = new Date();
  const endDateOnly = competition.endDate
    ? new Date(competition.endDate).toISOString().split("T")[0]
    : null;
  const endTimeStr = competition.endTime || "23:59";

  if (!endDateOnly) {
    return competition.isActive ? "Active" : "Inactive";
  }

  // Create end datetime in SA timezone (GMT+2)
  const endDateTime = new Date(`${endDateOnly}T${endTimeStr}:59+02:00`);

  if (endDateTime < now) {
    return "Ended";
  }
  return competition.isActive ? "Active" : "Inactive";
};

// Function to get status badge styling
const getStatusBadgeStyle = (status: string) => {
  switch (status) {
    case "Active":
      return "bg-green-100 text-green-800";
    case "Ended":
      return "bg-red-100 text-red-800";
    case "Inactive":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

// Function to get status badge variant
const getStatusBadgeVariant = (status: string) => {
  switch (status) {
    case "Active":
      return "default" as const;
    case "Ended":
      return "destructive" as const;
    case "Inactive":
      return "secondary" as const;
    default:
      return "secondary" as const;
  }
};

// Bulk Upload Component
function BulkUploadComponent({ onClose }: { onClose: () => void }) {
  const { toast } = useToast();
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [uploadResults, setUploadResults] = useState<any>(null);

  // Download template
  const downloadTemplate = async () => {
    try {
      const response = await apiRequest(
        "GET",
        "/api/admin/competitions/template"
      );

      if (!response.ok) {
        throw new Error("Failed to download template");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "competition_template.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({ title: "Template downloaded successfully!" });
    } catch (error) {
      toast({
        title: "Download failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  // Parse CSV file
  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text) return;

      try {
        // Simple CSV parser (handles quoted fields)
        const lines = text.trim().split("\n");
        const headers = lines[0]
          .split(",")
          .map((h) => h.replace(/"/g, "").trim());
        const data = lines.slice(1).map((line) => {
          const values = [];
          let inQuotes = false;
          let currentValue = "";

          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === "," && !inQuotes) {
              values.push(currentValue.trim());
              currentValue = "";
            } else {
              currentValue += char;
            }
          }
          values.push(currentValue.trim());

          const row: any = {};
          headers.forEach((header, index) => {
            row[header] = values[index]?.replace(/"/g, "") || "";
          });
          return row;
        });

        setCsvData(data);
        toast({ title: `Parsed ${data.length} competitions from CSV` });
      } catch (error) {
        toast({
          title: "CSV parsing failed",
          description: "Please check your CSV format",
          variant: "destructive",
        });
      }
    };
    reader.readAsText(file);
  };

  // Upload competitions
  const uploadCompetitions = async () => {
    if (csvData.length === 0) {
      toast({ title: "No data to upload", variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      const response = await apiRequest(
        "POST",
        "/api/admin/competitions/bulk-upload",
        {
          body: {
            competitions: csvData,
          },
        }
      );
      const result = await response.json();

      setUploadResults(result);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/competitions"] });

      toast({
        title: "Upload completed!",
        description: `${result.summary.successful} created, ${result.summary.failed} failed`,
      });
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Step 1: Download Template */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Step 1: Download Template
          </CardTitle>
          <CardDescription>
            Get the CSV template with the correct format and sample data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={downloadTemplate} className="w-full">
            <Download className="h-4 w-4 mr-2" />
            Download CSV Template
          </Button>
        </CardContent>
      </Card>

      {/* Step 2: Upload CSV */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Step 2: Upload CSV File
          </CardTitle>
          <CardDescription>
            Upload your completed CSV file with competition data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="cursor-pointer"
            />
          </div>

          {csvData.length > 0 && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  ✅ Successfully parsed {csvData.length} competitions from CSV
                </p>
              </div>

              {/* Preview */}
              <div className="max-h-60 overflow-y-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Prize</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>End Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {csvData.slice(0, 5).map((comp, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          {comp.title}
                        </TableCell>
                        <TableCell>{comp.prize}</TableCell>
                        <TableCell>{comp.category}</TableCell>
                        <TableCell>{comp.startDate}</TableCell>
                        <TableCell>{comp.endDate}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {csvData.length > 5 && (
                  <div className="p-2 text-center text-sm text-gray-500 border-t">
                    ... and {csvData.length - 5} more competitions
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 3: Upload */}
      {csvData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Step 3: Create Competitions
            </CardTitle>
            <CardDescription>
              Upload the parsed competitions to your database
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={uploadCompetitions}
              disabled={isUploading}
              className="w-full"
            >
              {isUploading
                ? "Uploading..."
                : `Create ${csvData.length} Competitions`}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Upload Results */}
      {uploadResults && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {uploadResults.summary.successful > 0 ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              Upload Results
            </CardTitle>
            <CardDescription>{uploadResults.message}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {uploadResults.summary.successful}
                  </div>
                  <div className="text-sm text-gray-600">Successful</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {uploadResults.summary.failed}
                  </div>
                  <div className="text-sm text-gray-600">Failed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {uploadResults.summary.total}
                  </div>
                  <div className="text-sm text-gray-600">Total</div>
                </div>
              </div>

              {/* Detailed Results */}
              <div className="max-h-60 overflow-y-auto">
                {uploadResults.results.map((result: any, idx: number) => (
                  <div
                    key={idx}
                    className={`p-3 border-l-4 mb-2 ${
                      result.status === "success"
                        ? "border-green-500 bg-green-50"
                        : "border-red-500 bg-red-50"
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{result.title}</div>
                        <div className="text-sm text-gray-600">
                          {result.message}
                        </div>
                        {result.questionsCreated > 0 && (
                          <div className="text-xs text-green-600 mt-1">
                            ✓ {result.questionsCreated} questions created
                          </div>
                        )}
                        {result.questionErrors &&
                          result.questionErrors.length > 0 && (
                            <div className="text-xs text-orange-600 mt-1">
                              ⚠ Question errors:{" "}
                              {result.questionErrors.join(", ")}
                            </div>
                          )}
                        {result.id && (
                          <div className="text-xs text-gray-500">
                            ID: {result.id}
                          </div>
                        )}
                      </div>
                      <Badge
                        variant={
                          result.status === "success"
                            ? "default"
                            : "destructive"
                        }
                      >
                        {result.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Betfred API Testing tab component
function BetfredAPITestingTab() {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [testResults, setTestResults] = useState<any[]>([]);
  const [isTestRunning, setIsTestRunning] = useState(false);

  const testEmailMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await apiRequest("POST", "/api/admin/betfred-api-test", {
        body: { email },
      });
      return response.json();
    },
    onSuccess: (data: any) => {
      setTestResults((prev) => [data, ...prev.slice(0, 9)]); // Keep last 10 results
      toast({
        title: "API Test Successful",
        description: `Email ${data.email} tested successfully`,
      });
    },
    onError: (error: any) => {
      const errorData = {
        success: false,
        email,
        message: error?.message || "API test failed",
        timestamp: new Date().toISOString(),
      };
      setTestResults((prev) => [errorData, ...prev.slice(0, 9)]);
      toast({
        title: "API Test Failed",
        description: error?.message || "Failed to test Betfred API",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsTestRunning(false);
    },
  });

  const handleTestEmail = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter an email address to test",
        variant: "destructive",
      });
      return;
    }

    setIsTestRunning(true);
    testEmailMutation.mutate(email.trim());
  };

  const clearResults = () => {
    setTestResults([]);
    toast({
      title: "Results Cleared",
      description: "Test results have been cleared",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-gray-900">
          Betfred API Testing
        </h2>
        <p className="text-gray-600 mt-1">
          Test the Betfred Customer Email Check API endpoint manually
        </p>
      </div>

      {/* API Test Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Email Verification Test
          </CardTitle>
          <CardDescription>
            Enter an email address to test against the Betfred Customer Email
            Check API
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleTestEmail} className="space-y-4">
            <div className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email to test (e.g., user@example.com)"
                  disabled={isTestRunning}
                  data-testid="input-test-email"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="submit"
                  disabled={isTestRunning}
                  data-testid="button-test-api"
                  className="flex items-center gap-2"
                >
                  {isTestRunning ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4" />
                      Test API
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>

          {testResults.length > 0 && (
            <div className="flex justify-between items-center pt-4">
              <h3 className="text-lg font-semibold">Test Results</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={clearResults}
                data-testid="button-clear-results"
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Clear Results
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="space-y-4">
          {testResults.map((result, index) => (
            <Card
              key={`${result.timestamp}-${index}`}
              className={`${
                result.success
                  ? "border-green-200 bg-green-50"
                  : "border-red-200 bg-red-50"
              }`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {result.success ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    <CardTitle className="text-base">
                      Test Result - {result.email}
                    </CardTitle>
                  </div>
                  <Badge variant={result.success ? "default" : "destructive"}>
                    {result.success ? "SUCCESS" : "FAILED"}
                  </Badge>
                </div>
                <CardDescription className="text-xs">
                  {new Date(result.timestamp).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {result.success ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-600">
                          Email
                        </Label>
                        <p className="text-sm font-mono bg-gray-100 p-2 rounded">
                          {result.result?.email}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-600">
                          Registered Status
                        </Label>
                        <p className="text-sm">
                          <Badge
                            variant={
                              result.result?.isRegistered
                                ? "default"
                                : "secondary"
                            }
                          >
                            {result.result?.isRegistered
                              ? "REGISTERED"
                              : "NOT REGISTERED"}
                          </Badge>
                        </p>
                      </div>
                      {result.result?.registrationDate && (
                        <div>
                          <Label className="text-sm font-medium text-gray-600">
                            Registration Date
                          </Label>
                          <p className="text-sm">
                            {new Date(
                              result.result.registrationDate
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {result.result?.lastBetDate && (
                        <div>
                          <Label className="text-sm font-medium text-gray-600">
                            Last Bet Date
                          </Label>
                          <p className="text-sm">
                            {new Date(
                              result.result.lastBetDate
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {result.result?.lastDepositDate && (
                        <div>
                          <Label className="text-sm font-medium text-gray-600">
                            Last Deposit Date
                          </Label>
                          <p className="text-sm">
                            {new Date(
                              result.result.lastDepositDate
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {result.result?.lastLoginDate && (
                        <div>
                          <Label className="text-sm font-medium text-gray-600">
                            Last Login Date
                          </Label>
                          <p className="text-sm">
                            {new Date(
                              result.result.lastLoginDate
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Raw Response Collapsible */}
                    <details className="bg-gray-100 p-3 rounded">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                        View Raw API Response
                      </summary>
                      <pre className="text-xs mt-2 overflow-auto max-h-40 bg-white p-2 rounded border">
                        {JSON.stringify(result.result?.rawResponse, null, 2)}
                      </pre>
                    </details>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div>
                      <Label className="text-sm font-medium text-red-600">
                        Error Message
                      </Label>
                      <p className="text-sm bg-red-100 p-2 rounded border border-red-200">
                        {result.message}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* API Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            API Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-sm font-medium text-gray-600">
              Endpoint
            </Label>
            <p className="text-sm font-mono bg-gray-100 p-2 rounded">
              GET https://loyalty.betfred.co.za/api/CustomerEmailCheck?email=
              {"{email}"}
            </p>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-600">Purpose</Label>
            <p className="text-sm text-gray-700">
              This endpoint checks if an email address is registered with
              Betfred and returns customer activity data
            </p>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-600">
              Response Fields
            </Label>
            <div className="text-sm text-gray-700 space-y-1">
              <p>
                <code className="bg-gray-200 px-1 rounded">Email</code> - The
                checked email address
              </p>
              <p>
                <code className="bg-gray-200 px-1 rounded">Registered</code> -
                Boolean indicating if user is registered
              </p>
              <p>
                <code className="bg-gray-200 px-1 rounded">
                  RegistrationDate
                </code>{" "}
                - When the user registered (if applicable)
              </p>
              <p>
                <code className="bg-gray-200 px-1 rounded">LastBetDate</code> -
                User's last betting activity (if applicable)
              </p>
              <p>
                <code className="bg-gray-200 px-1 rounded">
                  LastDepositDate
                </code>{" "}
                - User's last deposit (if applicable)
              </p>
              <p>
                <code className="bg-gray-200 px-1 rounded">LastLoginDate</code>{" "}
                - User's last login (if applicable)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Facebook Lead Forms tab component
function FacebookLeadFormsTab() {
  const { toast } = useToast();
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] =
    useState<FacebookLeadFormRecord | null>(null);
  const [signupPageId, setSignupPageId] = useState("");

  const statusClassMap: Record<string, string> = {
    received: "bg-blue-100 text-blue-800",
    processed: "bg-green-100 text-green-800",
    failed: "bg-red-100 text-red-800",
    skipped: "bg-amber-100 text-amber-800",
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return "—";
    try {
      return format(new Date(value), "PPpp");
    } catch {
      return value;
    }
  };

  const formatRelativeTime = (value?: string | null) => {
    if (!value) return "—";
    try {
      return formatDistanceToNow(new Date(value), { addSuffix: true });
    } catch {
      return "—";
    }
  };

  const buildFieldMap = (submission: FacebookLeadSubmissionRecord) => {
    const map: Record<string, string> = {};
    if (!submission.payload || !Array.isArray(submission.payload.field_data)) {
      return map;
    }

    submission.payload.field_data.forEach((field) => {
      const valueArray = Array.isArray(field.values) ? field.values : [];
      const value =
        valueArray.length > 0 ? String(valueArray[0] ?? "").trim() : "";
      map[field.name] = value;
    });

    return map;
  };

  const {
    data: signupForms = [],
    isLoading: loadingForms,
    refetch: refetchSignupForms,
  } = useQuery<FacebookLeadFormRecord[]>({
    queryKey: ["facebook-lead-forms", "signup"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        "/api/facebook-lead-ads/forms?purpose=signup"
      );
      const data = await response.json();
      return (data?.forms ?? []) as FacebookLeadFormRecord[];
    },
  });

  const {
    data: leadSubmissions = [],
    isLoading: loadingLeadSubmissions,
    isFetching: fetchingLeadSubmissions,
    refetch: refetchLeadSubmissions,
  } = useQuery<FacebookLeadSubmissionRecord[]>({
    queryKey: ["facebook-lead-submissions", selectedForm?.formId],
    queryFn: async () => {
      if (!selectedForm?.formId) return [];
      const response = await apiRequest(
        "GET",
        `/api/facebook-lead-ads/forms/${selectedForm.formId}/submissions?limit=100`
      );
      const data = await response.json();
      return (data?.submissions ?? []) as FacebookLeadSubmissionRecord[];
    },
    enabled: leadDialogOpen && !!selectedForm?.formId,
    staleTime: 10_000,
  });

  const createSignupFormMutation = useMutation({
    mutationFn: async (payload: { pageId?: string }) => {
      const response = await apiRequest(
        "POST",
        "/api/facebook-lead-ads/create-signup-form",
        {
          body: payload,
        }
      );
      return response.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: "Signup form ready",
        description: data?.formId
          ? `Facebook form ${data.formId} is now available`
          : "Facebook signup form created successfully",
      });
      setSignupPageId("");
      queryClient.invalidateQueries({
        queryKey: ["facebook-lead-forms", "signup"],
      });
      refetchSignupForms();
    },
    onError: (error: any) => {
      toast({
        title: "Unable to create signup form",
        description:
          error?.message ||
          "Please verify your Facebook configuration and try again.",
        variant: "destructive",
      });
    },
  });

  const handleCreateSignupForm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await createSignupFormMutation.mutateAsync({
      pageId: signupPageId.trim() ? signupPageId.trim() : undefined,
    });
  };

  const copyToClipboard = (text: string, label: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: `${label} copied to clipboard`,
    });
  };

  const orderedForms = [...signupForms].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const totalForms = orderedForms.length;
  const activeForms = orderedForms.filter((form) => form.isActive).length;
  const latestForm = orderedForms[0];
  const lastCreatedLabel = latestForm
    ? formatRelativeTime(latestForm.createdAt)
    : "—";
  const latestFormUrl =
    (latestForm?.metadata?.formUrl as string | undefined) ?? undefined;

  const isLoadingLeads = loadingLeadSubmissions || fetchingLeadSubmissions;

  const handleViewLeads = (form: FacebookLeadFormRecord) => {
    setSelectedForm(form);
    setLeadDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-gray-900">
          Facebook Signup Leads
        </h2>
        <p className="text-gray-600 mt-1">
          Manage the dedicated Facebook signup form and review captured leads.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Signup Form Management
          </CardTitle>
          <CardDescription>
            Generate or refresh the standalone Facebook Lead form used to
            onboard new users.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateSignupForm} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="signupPageId">Facebook Page ID</Label>
                <Input
                  id="signupPageId"
                  type="text"
                  placeholder="Leave blank to use the default configured page"
                  value={signupPageId}
                  onChange={(event) => setSignupPageId(event.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Provide a Page ID if you need to override the default value
                  set in your environment variables.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                type="submit"
                disabled={createSignupFormMutation.isPending}
              >
                {createSignupFormMutation.isPending
                  ? "Creating form..."
                  : "Create / Refresh Signup Form"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => refetchSignupForms()}
                disabled={loadingForms}
              >
                Refresh Forms
              </Button>
            </div>
          </form>

          {latestForm && (
            <div className="mt-6 space-y-2 text-sm text-muted-foreground">
              <div>
                <span className="font-medium text-gray-700">Current form:</span>{" "}
                {latestForm.name || "Facebook Signup Form"} ({latestForm.formId}
                )
              </div>
              <div>
                <span className="font-medium text-gray-700">Last created:</span>{" "}
                {formatDateTime(latestForm.createdAt)} ({lastCreatedLabel})
              </div>
              {latestFormUrl && (
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                    {latestFormUrl}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(latestFormUrl, "Form URL")}
                    className="h-7 w-7 p-0"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Forms</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalForms}</div>
            <p className="text-xs text-muted-foreground">
              Signup forms registered in the platform
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Forms</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {activeForms}
            </div>
            <p className="text-xs text-muted-foreground">
              Currently enabled to receive leads
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Created</CardTitle>
            <Clock className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {lastCreatedLabel}
            </div>
            <p className="text-xs text-muted-foreground">
              Most recent signup form generation
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Signup Forms</CardTitle>
          <CardDescription>
            View details for each configured Facebook Lead signup form.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingForms ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : orderedForms.length === 0 ? (
            <div className="text-center py-12">
              <ExternalLink className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Signup Forms Yet
              </h3>
              <p className="text-gray-600 mb-4">
                Use the form above to create your dedicated Facebook signup lead
                form.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Purpose
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Form ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Page ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orderedForms.map((form) => {
                    const formUrl =
                      (form.metadata?.formUrl as string | undefined) ??
                      undefined;
                    return (
                      <tr key={form.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <Sparkles className="h-5 w-5 text-primary/70" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {form.name || "Facebook Signup Form"}
                              </div>
                              <div className="text-xs text-gray-500">
                                ID #{form.id}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="capitalize">
                              {form.purpose}
                            </Badge>
                            <Badge
                              variant={form.isActive ? "default" : "secondary"}
                            >
                              {form.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {form.formId}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                copyToClipboard(form.formId, "Form ID")
                              }
                              className="h-6 w-6 p-0"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {form.pageId || "Default"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {formatDateTime(form.createdAt)}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewLeads(form)}
                              className="flex items-center gap-1"
                            >
                              <FileText className="h-3 w-3" />
                              View Leads
                            </Button>
                            {formUrl && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(formUrl, "_blank")}
                                className="flex items-center gap-1"
                              >
                                <ExternalLink className="h-3 w-3" />
                                View Form
                              </Button>
                            )}
                            {formUrl && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  copyToClipboard(formUrl, "Form URL")
                                }
                                className="h-7 w-7 p-0"
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={leadDialogOpen}
        onOpenChange={(open) => {
          setLeadDialogOpen(open);
          if (!open) {
            setSelectedForm(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <DialogTitle>
                  {selectedForm
                    ? selectedForm.name || "Facebook Signup Form"
                    : "Lead submissions"}
                </DialogTitle>
                <DialogDescription>
                  {selectedForm
                    ? `Form ID: ${selectedForm.formId} • Purpose: ${selectedForm.purpose}`
                    : "Select a Facebook form to view stored submissions."}
                </DialogDescription>
                {selectedForm?.pageId && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Page ID: {selectedForm.pageId}
                  </p>
                )}
                {selectedForm?.metadata?.formUrl && (
                  <div className="flex items-center gap-2 mt-2">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {String(selectedForm.metadata.formUrl)}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        copyToClipboard(
                          String(selectedForm.metadata?.formUrl),
                          "Form URL"
                        )
                      }
                      className="h-7 w-7 p-0"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
              {selectedForm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchLeadSubmissions()}
                  disabled={isLoadingLeads}
                  className="mt-1"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              )}
            </div>
          </DialogHeader>

          {selectedForm ? (
            isLoadingLeads ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
              </div>
            ) : leadSubmissions.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No submissions have been stored for this form yet. Leads
                processed after this change will appear here automatically.
              </div>
            ) : (
              <div className="border rounded-md">
                <div className="max-h-[26rem] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Received
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Email / User
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Answers &amp; Field Data
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Error / Notes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {leadSubmissions.map((submission) => {
                        const answerEntries = submission.answers
                          ? Object.entries(submission.answers)
                          : [];
                        const fieldMap = buildFieldMap(submission);
                        const fieldEntries = Object.entries(fieldMap);
                        const answerCount = answerEntries.length;
                        const competitionReference =
                          submission.competitionId ??
                          selectedForm.competitionId ??
                          null;
                        const statusClass =
                          statusClassMap[submission.processingStatus] ??
                          "bg-slate-100 text-slate-700";

                        return (
                          <tr key={submission.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 align-top">
                              <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {formatDateTime(submission.createdAt)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatRelativeTime(submission.createdAt)}
                                </span>
                                {submission.processedAt && (
                                  <span className="text-[11px] text-gray-400">
                                    Processed{" "}
                                    {formatRelativeTime(submission.processedAt)}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top">
                              <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-gray-900 break-all">
                                  {submission.email || "—"}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {submission.userId
                                    ? `User #${submission.userId}`
                                    : "User not created"}
                                </span>
                                {submission.formPurpose && (
                                  <span className="text-[11px] text-gray-400">
                                    Purpose: {submission.formPurpose}
                                  </span>
                                )}
                                {competitionReference && (
                                  <span className="text-[11px] text-gray-400">
                                    Competition #{competitionReference}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top">
                              <div className="flex flex-col gap-2">
                                <Badge className={`capitalize ${statusClass}`}>
                                  {submission.processingStatus}
                                </Badge>
                                <span className="text-xs text-gray-500">
                                  {answerCount > 0
                                    ? `${answerCount} answer${
                                        answerCount === 1 ? "" : "s"
                                      } saved`
                                    : "No competition answers stored"}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top">
                              {answerEntries.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-1">
                                  {answerEntries.map(([questionId, answer]) => (
                                    <span
                                      key={`answer-${questionId}`}
                                      className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                                    >
                                      Q{questionId}: {answer}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {fieldEntries.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {fieldEntries.map(([fieldName, value]) => (
                                    <span
                                      key={`field-${fieldName}`}
                                      className="text-xs bg-gray-50 text-gray-600 px-2 py-1 rounded border border-gray-200"
                                    >
                                      {fieldName}: {value || "—"}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                answerEntries.length === 0 && (
                                  <span className="text-xs text-gray-400">
                                    No field data captured
                                  </span>
                                )
                              )}
                            </td>
                            <td className="px-4 py-3 align-top">
                              {submission.processingError ? (
                                <span className="text-xs text-red-600">
                                  {submission.processingError}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-500">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Select a Facebook form to view stored submissions.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Facebook Connection Testing tab component
function FacebookConnectionTestingTab() {
  const { toast } = useToast();
  const [pageId, setPageId] = useState("");
  const [testResult, setTestResult] = useState<any>(null);
  const [isTestRunning, setIsTestRunning] = useState(false);

  const testConnectionMutation = useMutation({
    mutationFn: async (pageId: string) => {
      const response = await apiRequest(
        "GET",
        `/api/facebook-lead-ads/test-connection?pageId=${pageId}`
      );
      return response.json();
    },
    onSuccess: (data: any) => {
      setTestResult(data);
      if (data.success) {
        toast({
          title: "Connection Successful",
          description: `Successfully connected to ${data.page?.name}`,
        });
      } else {
        toast({
          title: "Connection Failed",
          description: data.message || "Failed to connect to Facebook",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      const errorData = {
        success: false,
        message: error?.message || "Connection test failed",
        timestamp: new Date().toISOString(),
      };
      setTestResult(errorData);
      toast({
        title: "Connection Test Failed",
        description: error?.message || "Failed to test Facebook connection",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsTestRunning(false);
    },
  });

  const handleTestConnection = (e: FormEvent) => {
    e.preventDefault();
    if (!pageId.trim()) {
      toast({
        title: "Page ID Required",
        description: "Please enter a Facebook Page ID to test",
        variant: "destructive",
      });
      return;
    }

    setIsTestRunning(true);
    testConnectionMutation.mutate(pageId.trim());
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-gray-900">
          Facebook Connection Test
        </h2>
        <p className="text-gray-600 mt-1">
          Test your Facebook Page Access Token configuration
        </p>
      </div>

      {/* Connection Test Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5" />
            Test Facebook Page Connection
          </CardTitle>
          <CardDescription>
            Enter your Facebook Page ID to verify that your access token is
            working correctly
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleTestConnection} className="space-y-4">
            <div className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="pageId">Facebook Page ID</Label>
                <Input
                  id="pageId"
                  type="text"
                  value={pageId}
                  onChange={(e) => setPageId(e.target.value)}
                  placeholder="Enter Facebook Page ID (e.g., 123456789012345)"
                  disabled={isTestRunning}
                  data-testid="input-facebook-page-id"
                />
                <p className="text-xs text-gray-500 mt-1">
                  You can find your Page ID in Facebook Page Settings under
                  "About"
                </p>
              </div>
              <div className="flex items-end pb-5">
                <Button
                  type="submit"
                  disabled={isTestRunning}
                  data-testid="button-test-facebook-connection"
                  className="flex items-center gap-2"
                >
                  {isTestRunning ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="h-4 w-4" />
                      Test Connection
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Test Result */}
      {testResult && (
        <Card
          className={`${
            testResult.success
              ? "border-green-200 bg-green-50"
              : "border-red-200 bg-red-50"
          }`}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <CardTitle className="text-base">
                  Connection Test Result
                </CardTitle>
              </div>
              <Badge variant={testResult.success ? "default" : "destructive"}>
                {testResult.success ? "SUCCESS" : "FAILED"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {testResult.success ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-600">
                      Page Name
                    </Label>
                    <p className="text-sm font-semibold text-gray-900">
                      {testResult.page?.name || "N/A"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-600">
                      Page ID
                    </Label>
                    <p className="text-sm font-mono text-gray-900">
                      {testResult.page?.id || "N/A"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-600">
                      Category
                    </Label>
                    <p className="text-sm text-gray-900">
                      {testResult.page?.category || "N/A"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-600">
                      Fan Count
                    </Label>
                    <p className="text-sm text-gray-900">
                      {testResult.page?.fanCount?.toLocaleString() || "N/A"}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium text-gray-600">
                      Page Link
                    </Label>
                    <p className="text-sm text-blue-600 break-all">
                      {testResult.page?.link || "N/A"}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium text-gray-600">
                      Access Token Status
                    </Label>
                    <p className="text-sm text-gray-900">
                      {testResult.page?.hasAccessToken
                        ? "✓ Valid"
                        : "✗ Invalid"}
                    </p>
                  </div>
                </div>
                <div className="pt-3 border-t border-green-200">
                  <p className="text-sm text-green-700">
                    ✓ {testResult.message}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-gray-600">
                    Error Message
                  </Label>
                  <p className="text-sm text-red-700">
                    {testResult.message || testResult.error || "Unknown error"}
                  </p>
                </div>
                {testResult.details && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">
                      Error Details
                    </Label>
                    <div className="text-sm text-gray-700 space-y-1">
                      {testResult.details.code && (
                        <p>
                          <strong>Code:</strong> {testResult.details.code}
                        </p>
                      )}
                      {testResult.details.type && (
                        <p>
                          <strong>Type:</strong> {testResult.details.type}
                        </p>
                      )}
                      {testResult.details.httpStatus && (
                        <p>
                          <strong>HTTP Status:</strong>{" "}
                          {testResult.details.httpStatus}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Configuration Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configuration Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-sm font-medium text-gray-600">
              Required Environment Variable
            </Label>
            <p className="text-sm font-mono bg-gray-100 p-2 rounded">
              FACEBOOK_PAGE_ACCESS_TOKEN
            </p>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-600">Purpose</Label>
            <p className="text-sm text-gray-700">
              This test verifies that your Facebook Page Access Token is valid
              and has the necessary permissions to access your page data and
              create lead forms.
            </p>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-600">
              How to Get Your Page ID
            </Label>
            <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
              <li>Go to your Facebook Page</li>
              <li>Click "About" in the left menu</li>
              <li>Scroll down to find "Page ID" or check the URL</li>
              <li>Copy the numeric ID</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// User Data Management tab component
function UserDataManagementTab({
  onViewProfile,
}: {
  onViewProfile?: (user: any) => void;
}) {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isTogglingUserStatus, setIsTogglingUserStatus] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  // Fetch paginated user data with server-side filtering and search
  const {
    data: userDataResponse,
    isLoading: loadingUserData,
    refetch: refetchUserData,
  } = useQuery<{
    data: any[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>({
    queryKey: [
      "/api/admin/user-data",
      {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        status: filterStatus,
      },
    ],
    refetchOnWindowFocus: false,
  });

  const userData = userDataResponse?.data || [];
  const totalUsers = userDataResponse?.total || 0;
  const totalPages = userDataResponse?.totalPages || 0;

  // Debug logging for rows per page
  console.log('[UserDataManagement] Current state:', {
    itemsPerPage,
    currentPage,
    totalUsers,
    totalPages,
    actualDataLength: userData.length,
    queryKey: ["/api/admin/user-data", { page: currentPage, limit: itemsPerPage, search: searchTerm, status: filterStatus }]
  });

  // Clear cache on mount to ensure fresh data
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["/api/admin/user-data"] });
  }, []);

  // Reset selections when the page of data changes
  useEffect(() => {
    setSelectedUserIds([]);
  }, [userDataResponse]);

  // Export user data mutation
  const exportUserDataMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("GET", "/api/admin/export/user-data");
      const csvContent = await response.text();
      return csvContent;
    },
    onSuccess: (csvContent) => {
      // Create and download CSV file
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `user-data-export-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Export Complete",
        description: "User data has been exported successfully!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Export Failed",
        description: error?.message || "Failed to export user data",
        variant: "destructive",
      });
    },
  });

  // Server-side pagination and filtering - no client-side filtering needed
  const paginatedUsers = userData;

  // Handler to update search and reset to page 1
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  // Handler to update filter and reset to page 1
  const handleFilterChange = (value: string) => {
    setFilterStatus(value);
    setCurrentPage(1);
  };

  // Handler to update items per page and reset to page 1
  const handleItemsPerPageChange = (value: string) => {
    const newLimit = parseInt(value);
    console.log('[UserDataManagement] Changing items per page from', itemsPerPage, 'to', newLimit);
    setItemsPerPage(newLimit);
    setCurrentPage(1);
  };

  const handleExportData = () => {
    exportUserDataMutation.mutate();
  };

  const handleViewUserDetails = (user: any) => {
    if (!user) return;
    onViewProfile?.(user);
  };

  // Toggle user status mutation
  const toggleUserStatusMutation = useMutation({
    mutationFn: async ({
      userId,
      isActive,
    }: {
      userId: number;
      isActive: boolean;
    }) => {
      const response = await apiRequest(
        "PATCH",
        `/api/admin/users/${userId}/status`,
        {
          body: {
            isActive: !isActive,
          },
        }
      );
      return response.json();
    },
    onSuccess: (data, variables) => {
      refetchUserData();
      toast({
        title: "User Status Updated",
        description: `User has been ${
          variables.isActive ? "deactivated" : "reactivated"
        } successfully`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to update user status",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsTogglingUserStatus(false);
    },
  });

  const handleToggleUserStatus = (userId: number, isActive: boolean) => {
    setIsTogglingUserStatus(true);
    toggleUserStatusMutation.mutate({ userId, isActive });
  };

  const toggleUserSelection = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const toggleSelectAllOnPage = () => {
    const selectableIds = paginatedUsers
      .filter((u) => u.isActive)
      .map((u) => u.id as number);

    const allSelected =
      selectableIds.length > 0 &&
      selectableIds.every((id) => selectedUserIds.includes(id));

    setSelectedUserIds(allSelected ? [] : selectableIds);
  };

  const bulkDeactivateMutation = useMutation({
    mutationFn: async (userIds: number[]) => {
      const activeIds = paginatedUsers
        .filter((u) => userIds.includes(u.id) && u.isActive)
        .map((u) => u.id as number);

      await Promise.all(
        activeIds.map((userId) =>
          apiRequest("PATCH", `/api/admin/users/${userId}/status`, {
            body: { isActive: false },
          }),
        ),
      );
      return activeIds.length;
    },
    onSuccess: (count) => {
      setSelectedUserIds([]);
      refetchUserData();
      toast({
        title: "Users Deactivated",
        description:
          count > 0
            ? `${count} user${count === 1 ? "" : "s"} have been deactivated.`
            : "No active users were selected.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Bulk Deactivate Failed",
        description: error?.message || "Failed to deactivate selected users",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="h-6 w-6" />
          User Data Management
        </h2>
        <p className="text-gray-600 mt-1">
          Comprehensive view of user profiles, answers, and activity data
        </p>
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div>
              <CardTitle>User Data Overview</CardTitle>
              <CardDescription>
                Search, filter, and export user information
              </CardDescription>
            </div>
            <Button
              onClick={handleExportData}
              disabled={exportUserDataMutation.isPending}
            >
              <Download className="h-4 w-4 mr-2" />
              {exportUserDataMutation.isPending
                ? "Exporting..."
                : "Export All Data"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <Label htmlFor="search">Search Users</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="search"
                  placeholder="Search by username, email, name, or mobile number..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                  data-testid="input-search"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="filter">Filter Status</Label>
              <Select value={filterStatus} onValueChange={handleFilterChange}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="rows-per-page">Rows per page</Label>
              <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                  <SelectItem value="1000">1000</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-blue-600">
                  {totalUsers}
                </div>
                <div className="text-sm text-gray-600">
                  {filterStatus === "all"
                    ? "Total Users"
                    : `${
                        filterStatus.charAt(0).toUpperCase() +
                        filterStatus.slice(1)
                      } Users`}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-green-600">
                  {userData.filter((u) => u.isActive).length}
                </div>
                <div className="text-sm text-gray-600">Active (This Page)</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-blue-900">
                  {userData.filter((u) => u.totalAnswers > 0).length}
                </div>
                <div className="text-sm text-gray-600">
                  With Answers (This Page)
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-orange-600">
                  {userData.reduce((sum, u) => sum + (u.totalAnswers || 0), 0)}
                </div>
                <div className="text-sm text-gray-600">
                  Total Answers (This Page)
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* User Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            User Data (Showing {userData.length} of {totalUsers} users)
          </CardTitle>
          {paginatedUsers.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Checkbox
                  checked={
                    paginatedUsers.some((u) => u.isActive) &&
                    paginatedUsers
                      .filter((u) => u.isActive)
                      .every((u) => selectedUserIds.includes(u.id))
                  }
                  onCheckedChange={toggleSelectAllOnPage}
                  aria-label="Select all active users on this page"
                />
                <span>Select all active users on this page</span>
              </div>
              <div className="flex items-center gap-3">
                {selectedUserIds.length > 0 && (
                  <span className="text-xs text-gray-500">
                    {selectedUserIds.length} user
                    {selectedUserIds.length === 1 ? "" : "s"} selected
                  </span>
                )}
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => bulkDeactivateMutation.mutate(selectedUserIds)}
                  disabled={
                    bulkDeactivateMutation.isPending ||
                    selectedUserIds.length === 0
                  }
                >
                  {bulkDeactivateMutation.isPending ? (
                    <span className="h-4 w-4 mr-2 rounded-full border-2 border-b-transparent border-white animate-spin" />
                  ) : (
                    <UserX className="h-4 w-4 mr-2" />
                  )}
                  Deactivate selected
                </Button>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {loadingUserData ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">Loading user data...</div>
            </div>
          ) : paginatedUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No users found matching your criteria
            </div>
          ) : (
            <>
              <ResponsiveTable
                data={paginatedUsers}
                breakpoint="mobile-md"
                cardTitle={(user) => `${user.firstName} ${user.lastName}`}
                cardSubtitle={(user) => `@${user.username} • ID: ${user.id}`}
                actions={(user) => (
                  <>
                    <TableActionButton
                      icon={<Eye className="h-4 w-4" />}
                      label="View Details"
                      onClick={() => handleViewUserDetails(user)}
                      data-testid={`button-view-user-${user.id}`}
                    />
                    <TableActionButton
                      icon={
                        user.isActive ? (
                          <UserX className="h-4 w-4" />
                        ) : (
                          <UserCheck className="h-4 w-4" />
                        )
                      }
                      label={user.isActive ? "Deactivate" : "Reactivate"}
                      variant={user.isActive ? "destructive" : "default"}
                      onClick={() =>
                        handleToggleUserStatus(user.id, user.isActive)
                      }
                      disabled={isTogglingUserStatus}
                      data-testid={`button-toggle-user-${user.id}`}
                    />
                  </>
                )}
                columns={[
                  {
                    key: "select",
                    header: "",
                    priority: "high",
                    render: (user) => (
                      <Checkbox
                        checked={selectedUserIds.includes(user.id)}
                        onCheckedChange={() => toggleUserSelection(user.id)}
                        aria-label={`Select user ${user.id}`}
                        disabled={!user.isActive}
                      />
                    ),
                  },
                  {
                    key: "userInfo",
                    header: "User Info",
                    priority: "high",
                    render: (user) => (
                      <div>
                        <div className="font-medium">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          @{user.username}
                        </div>
                        <div className="text-xs text-gray-400">
                          ID: {user.id}
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: "contact",
                    header: "Contact",
                    priority: "medium",
                    hideOnMobile: true,
                    render: (user) => (
                      <div className="text-sm">
                        <div>{user.email}</div>
                        {user.phone && (
                          <div className="text-gray-500">{user.phone}</div>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: "status",
                    header: "Status",
                    priority: "high",
                    render: (user) => (
                      <div className="space-y-1">
                        {user.isActive ? (
                          <Badge className="bg-green-100 text-green-800">
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">
                            Inactive
                          </Badge>
                        )}
                        {user.isAdmin && (
                          <Badge className="bg-blue-100 text-blue-800">
                            Admin
                          </Badge>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: "consent",
                    header: "Consent",
                    priority: "high",
                    hideOnMobile: true,
                    render: (user) => (
                      <div className="space-y-1 text-sm">
                        <div>
                          Marketing:{" "}
                          <span
                            className={
                              user.marketingConsent
                                ? "text-green-700 font-medium"
                                : "text-red-700 font-medium"
                            }
                          >
                            {user.marketingConsent ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: "activity",
                    header: "Activity",
                    priority: "medium",
                    hideOnMobile: true,
                    render: (user) => (
                      <div className="text-sm">
                        <div>Entries: {user.totalEntries || 0}</div>
                        <div>Shares: {user.totalShares || 0}</div>
                        <div className="text-xs text-gray-500">
                          Joined: {formatSADate(user.createdAt)}
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: "answers",
                    header: "Answers",
                    priority: "low",
                    hideOnMobile: true,
                    render: (user) => (
                      <div className="text-sm">
                        <div className="font-medium">
                          {user.totalAnswers || 0}
                        </div>
                      </div>
                    ),
                  },
                ]}
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * itemsPerPage + 1}-
                    {Math.min(currentPage * itemsPerPage, totalUsers)} of{" "}
                    {totalUsers} users
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage(Math.max(1, currentPage - 1))
                      }
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="flex items-center px-3 text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage(Math.min(totalPages, currentPage + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Detailed user profile view tab component
function UserProfileTab({
  user,
  onBack,
}: {
  user: any | null;
  onBack: () => void;
}) {
  const validUserId =
    user?.id && typeof user.id === "number" && !isNaN(user.id) ? user.id : null;

  const { data: userDetails, isLoading: loadingUserDetails } = useQuery<any>({
    queryKey: validUserId
      ? [`/api/admin/user-data/${validUserId}`]
      : ["no-user-selected"],
    enabled: !!validUserId,
    retry: false,
  });

  if (!user) {
    return (
      <Card>
        <CardContent className="py-16 text-center space-y-3">
          <div className="flex justify-center">
            <Users className="h-10 w-10 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            Select a user to view their profile
          </h2>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            Choose <span className="font-medium">View Details</span> from the
            User Data Management tab to open a full user profile with activity,
            verification, and competition history.
          </p>
          <Button variant="outline" onClick={onBack} className="mt-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to User Data
          </Button>
        </CardContent>
      </Card>
    );
  }

  const profile = userDetails ? { ...user, ...userDetails } : user;

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {profile.isActive ? (
            <Badge className="bg-green-100 text-green-800">Active</Badge>
          ) : (
            <Badge className="bg-red-100 text-red-800">Inactive</Badge>
          )}
          {profile.isAdmin && (
            <Badge className="bg-blue-100 text-blue-800">Admin</Badge>
          )}
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to User Data
          </Button>
        </div>
      </div>

      <UserProfileDetails
        user={user}
        userDetails={userDetails}
        loadingUserDetails={loadingUserDetails}
      />
    </div>
  );
}

function UserProfileDetails({
  user,
  userDetails,
  loadingUserDetails,
}: {
  user: any;
  userDetails?: any;
  loadingUserDetails: boolean;
}) {
  const profile = userDetails ? { ...user, ...userDetails } : user;
  const answers = Array.isArray(userDetails?.answers)
    ? (userDetails!.answers as any[])
    : Array.isArray(profile.answers)
    ? (profile.answers as any[])
    : [];
  const competitions = Array.isArray(userDetails?.competitions)
    ? (userDetails!.competitions as any[])
    : Array.isArray(profile.competitions)
    ? (profile.competitions as any[])
    : [];
  const loginHistory = Array.isArray(profile.loginHistory)
    ? (profile.loginHistory as any[])
    : [];

  return (
    <UserProfileIntelligence
      profile={profile}
      loading={loadingUserDetails}
      answers={answers}
      competitions={competitions}
      loginHistory={loginHistory}
    />
  );
}

// Fraud Detection tab component - Challenge Referral Fraud
function FraudDetectionTab() {
  const { toast } = useToast();
  const [selectedChallengeId, setSelectedChallengeId] = useState<number | null>(
    null
  );
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  // Fetch challenges with referrals
  const { data: challengesData, isLoading: loadingChallenges } = useQuery<{
    challenges: Array<{
      id: number;
      title: string;
      is_active: boolean;
      start_date: string;
      end_date: string;
      total_referrals: number;
      total_referrers: number;
    }>;
  }>({
    queryKey: ["/api/admin/fraud-detection/challenges"],
  });

  // Fetch challenge referral fraud data for selected challenge
  const {
    data: fraudData,
    isLoading: loadingFraudData,
    refetch,
  } = useQuery<{
    referrers: Array<{
      referrer_id: number;
      referrer_email: string;
      referrer_phone: string;
      referrer_first_name: string;
      referrer_last_name: string;
      referrer_ip: string;
      referrer_location: string;
      referrer_ignore_fraud?: boolean;
      referred_users: Array<{
        id: number;
        email: string;
        phone: string;
        firstName: string;
        lastName: string;
        lastLoginIp: string;
        lastLoginLocation: string;
        idNumber: string;
        createdAt: string;
        referredAt: string;
         isActive?: boolean;
         matchesReferrer: boolean;
         sharedIpCluster?: boolean;
         ipClusterSize?: number;
         ignoreFraud?: boolean;
      }>;
    }>;
    totalReferrers: number;
    totalReferrals: number;
    suspiciousReferrals: number;
  }>({
    queryKey: ["/api/admin/fraud-detection/challenge", selectedChallengeId],
    enabled: selectedChallengeId !== null,
  });

  const bulkDisableMutation = useMutation({
    mutationFn: async (userIds: number[]) => {
      const response = await apiRequest(
        "PUT",
        "/api/admin/users/status/bulk",
        {
          body: { userIds, isActive: false },
        }
      );
      return response.json();
    },
    onSuccess: (_data, userIds) => {
      toast({
        title: "Users Disabled",
        description: `${userIds.length} user${
          userIds.length === 1 ? "" : "s"
        } have been disabled`,
      });
      setSelectedUserIds([]);
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Bulk Disable Failed",
        description: error?.message || "Failed to disable selected users",
        variant: "destructive",
      });
    },
  });

  const toggleUserSelection = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleDisableSelected = () => {
    if (selectedUserIds.length === 0) {
      toast({
        title: "No Users Selected",
        description: "Select at least one suspicious user to disable.",
      });
      return;
    }
    if (
      !confirm(
        `Disable ${selectedUserIds.length} selected user${
          selectedUserIds.length === 1 ? "" : "s"
        }?\n\nThis will set their accounts to inactive.`
      )
    ) {
      return;
    }
    bulkDisableMutation.mutate(selectedUserIds);
  };

  // Auto-select first challenge if available
  if (!selectedChallengeId && challengesData?.challenges.length) {
    setSelectedChallengeId(challengesData.challenges[0].id);
  }

  if (loadingChallenges) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading challenges...</p>
        </div>
      </div>
    );
  }

  if (!challengesData?.challenges.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Challenges with Referrals
          </h3>
          <p className="text-gray-600">
            There are no challenges with referral activity to analyze
          </p>
        </CardContent>
      </Card>
    );
  }

  const selectedChallenge = challengesData.challenges.find(
    (c) => c.id === selectedChallengeId
  );

  // Count suspicious referrers (those with at least one matching referral)
  const suspiciousReferrers =
    fraudData?.referrers.filter(
      (referrer) =>
        !referrer.referrer_ignore_fraud &&
        referrer.referred_users?.some(
          (user) => user.matchesReferrer || user.sharedIpCluster
        )
    ) || [];

  const allSuspiciousUserIds =
    fraudData?.referrers.flatMap((referrer) =>
      referrer.referrer_ignore_fraud
        ? []
        : (referrer.referred_users || [])
            .filter(
              (user) =>
                (user.matchesReferrer || user.sharedIpCluster) &&
                !user.ignoreFraud &&
                user.isActive !== false
            )
            .map((user) => user.id)
    ) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Shield className="h-6 w-6 text-red-500" />
          Challenge Referral Fraud Detection
        </h2>
        <p className="text-gray-600 mt-1">
          Identify users who created fake accounts to refer themselves in
          challenges
        </p>
      </div>

      {/* Challenge Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Select Challenge
          </CardTitle>
          <CardDescription>
            Choose a challenge to analyze referral fraud patterns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select
            value={selectedChallengeId?.toString()}
            onValueChange={(value) => setSelectedChallengeId(parseInt(value))}
          >
            <SelectTrigger className="w-full" data-testid="select-challenge">
              <SelectValue placeholder="Select a challenge" />
            </SelectTrigger>
            <SelectContent>
              {challengesData.challenges.map((challenge) => (
                <SelectItem key={challenge.id} value={challenge.id.toString()}>
                  <div className="flex items-center justify-between w-full">
                    <span className="font-medium">{challenge.title}</span>
                    <div className="flex items-center gap-2 ml-4">
                      <span className="text-xs text-gray-500">
                        {challenge.total_referrers} referrers
                      </span>
                      <span className="text-xs text-gray-500">•</span>
                      <span className="text-xs text-gray-500">
                        {challenge.total_referrals} referrals
                      </span>
                      {challenge.is_active && (
                        <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                          Active
                        </span>
                      )}
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loadingFraudData && (
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2 text-gray-600">Analyzing referral patterns...</p>
          </div>
        </div>
      )}

      {/* Summary Card */}
      {!loadingFraudData && fraudData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Detection Summary - {selectedChallenge?.title}
            </CardTitle>
            <CardDescription>
              Referrers flagged for creating fake accounts with matching
              IP/location
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="text-sm text-blue-600 font-medium">
                  Total Referrers
                </div>
                <div className="text-3xl font-bold text-blue-700 mt-1">
                  {fraudData.totalReferrers}
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  Users who made referrals
                </div>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="text-sm text-red-600 font-medium">
                  Suspicious Referrers
                </div>
                <div className="text-3xl font-bold text-red-700 mt-1">
                  {suspiciousReferrers.length}
                </div>
                <div className="text-xs text-red-600 mt-1">
                  Flagged for fake account creation
                </div>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="text-sm text-amber-600 font-medium">
                  Fake Referrals
                </div>
                <div className="text-3xl font-bold text-amber-700 mt-1">
                  {fraudData.suspiciousReferrals}
                </div>
                <div className="text-xs text-amber-600 mt-1">
                  Matching referrer IP or shared IP clusters
                </div>
              </div>
            </div>
            <Button
              onClick={() => refetch()}
              variant="outline"
              className="mt-4 w-full sm:w-auto"
              data-testid="button-refresh-fraud-data"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Refresh Data
            </Button>
          </CardContent>
        </Card>
      )}

      {/* All Referrers */}
      {!loadingFraudData &&
      fraudData?.referrers &&
      fraudData.referrers.length > 0 ? (
        <div className="space-y-4">
          {allSuspiciousUserIds.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-sm text-gray-700">
                Selected suspicious users:{" "}
                <span className="font-semibold">
                  {selectedUserIds.length}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={allSuspiciousUserIds.length === 0}
                  onClick={() =>
                    setSelectedUserIds(allSuspiciousUserIds.slice())
                  }
                >
                  Select all suspicious ({allSuspiciousUserIds.length})
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={
                    selectedUserIds.length === 0 ||
                    bulkDisableMutation.isPending
                  }
                  onClick={handleDisableSelected}
                >
                  {bulkDisableMutation.isPending
                    ? "Disabling..."
                    : "Disable selected users"}
                </Button>
              </div>
            </div>
          )}
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Users className="h-5 w-5" />
            All Referrers ({fraudData.totalReferrers})
          </h3>
          {fraudData.referrers
            .filter(
              (referrer) =>
                referrer.referred_users && referrer.referred_users.length > 0
            )
            .map((referrer) => {
              const hasSuspiciousReferrals =
                referrer.referred_users?.some(
                  (user) =>
                    (user.matchesReferrer || user.sharedIpCluster) &&
                    !user.ignoreFraud
                ) || false;
              const isIgnored = !!referrer.referrer_ignore_fraud;
              const isSuspicious = hasSuspiciousReferrals && !isIgnored;
              const suspiciousCount = (
                referrer.referred_users?.filter(
                  (user) => user.matchesReferrer || user.sharedIpCluster
                ) || []
              ).length;

              // Debug: Log referrer data
              if (isSuspicious) {
                console.log("[FRAUD DEBUG] Referrer data:", {
                  id: referrer.referrer_id,
                  username: referrer.referrer_username,
                  isActive: referrer.referrer_is_active,
                  ignoreFraud: referrer.referrer_ignore_fraud,
                  isActiveType: typeof referrer.referrer_is_active,
                  allFields: Object.keys(referrer),
                });
              }

              return (
                <Card
                  key={referrer.referrer_id}
                  className={`border-l-4 ${
                    isSuspicious
                      ? "border-l-red-500 bg-red-50/30"
                      : "border-l-gray-300"
                  }`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle
                          className={`text-base font-semibold ${
                            isSuspicious ? "text-red-700" : "text-gray-700"
                          }`}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            {isSuspicious && (
                              <AlertTriangle className="h-4 w-4 text-red-600" />
                            )}
                            <span>
                              Referrer #{referrer.referrer_id}:{" "}
                              {referrer.referrer_first_name}{" "}
                              {referrer.referrer_last_name}
                            </span>
                            <span className="px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800 font-mono">
                              @{referrer.referrer_username}
                            </span>
                            {isSuspicious && (
                              <span className="px-2 py-1 rounded-full text-xs bg-red-600 text-white font-bold">
                                SUSPICIOUS
                              </span>
                            )}
                          </div>
                        </CardTitle>
                        <CardDescription className="mt-1 space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-4 w-4" />
                            <span>{referrer.referrer_email}</span>
                          </div>
                          {referrer.referrer_phone && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="h-4 w-4" />
                              <span>{referrer.referrer_phone}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-sm">
                            <Globe className="h-4 w-4" />
                            <span className="font-mono">
                              {referrer.referrer_ip || "No IP recorded"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="h-4 w-4" />
                            <span>
                              {referrer.referrer_location || "Unknown location"}
                            </span>
                          </div>
                        </CardDescription>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        {isSuspicious && (
                          <div className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-semibold">
                            {suspiciousCount} fake account
                            {suspiciousCount !== 1 ? "s" : ""}
                          </div>
                        )}
                        {!isSuspicious && hasSuspiciousReferrals && isIgnored && (
                          <div className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-semibold">
                            Ignored (manual override)
                          </div>
                        )}
                        <div
                          className={`${
                            isSuspicious
                              ? "bg-amber-100 text-amber-700"
                              : "bg-gray-100 text-gray-700"
                          } px-3 py-1 rounded-full text-xs`}
                        >
                          {referrer.referred_users.length} referral
                          {referrer.referred_users.length !== 1 ? "s" : ""}
                        </div>
                        {isSuspicious && referrer.referrer_is_active && (
                          <Button
                            onClick={async () => {
                              if (
                                !confirm(
                                  `Disqualify ${referrer.referrer_first_name} ${referrer.referrer_last_name} (@${referrer.referrer_username})?\n\nThis will:\n- Set their account to inactive\n- Exclude their referrals from leaderboards\n- Remove them from challenge completion counts`
                                )
                              ) {
                                return;
                              }
                              try {
                                await apiRequest(
                                  "PUT",
                                  `/api/admin/users/${referrer.referrer_id}/status`,
                                  {
                                    body: { isActive: false },
                                  }
                                );
                                toast({
                                  title: "User Disqualified",
                                  description: `${referrer.referrer_first_name} ${referrer.referrer_last_name} has been disqualified`,
                                });
                                refetch();
                              } catch (error) {
                                toast({
                                  title: "Error",
                                  description: "Failed to disqualify user",
                                  variant: "destructive",
                                });
                              }
                            }}
                            variant="destructive"
                            size="sm"
                            className="text-xs"
                            data-testid={`button-disqualify-${referrer.referrer_id}`}
                          >
                            <Ban className="h-3 w-3 mr-1" />
                            Disqualify
                          </Button>
                        )}
                        {hasSuspiciousReferrals && (
                          <Button
                            onClick={async () => {
                              const nextIgnore = !isIgnored;
                              if (
                                !confirm(
                                  `${nextIgnore ? "Ignore" : "Clear ignore for"} ${
                                    referrer.referrer_first_name
                                  } ${referrer.referrer_last_name} (@${
                                    referrer.referrer_username
                                  })?\n\n${
                                    nextIgnore
                                      ? "They will no longer be flagged as suspicious in fraud detection."
                                      : "They may appear as suspicious again if patterns still match."
                                  }`
                                )
                              ) {
                                return;
                              }
                              try {
                                await apiRequest(
                                  "PUT",
                                  `/api/admin/users/${referrer.referrer_id}/fraud-override`,
                                  {
                                    body: { ignore: nextIgnore },
                                  }
                                );
                                toast({
                                  title: nextIgnore
                                    ? "Referrer Ignored"
                                    : "Ignore Cleared",
                                  description: nextIgnore
                                    ? "This referrer will no longer be treated as suspicious."
                                    : "This referrer can be flagged as suspicious again.",
                                });
                                refetch();
                              } catch (error) {
                                toast({
                                  title: "Error",
                                  description:
                                    "Failed to update ignore flag for referrer",
                                  variant: "destructive",
                                });
                              }
                            }}
                            variant="outline"
                            size="sm"
                            className={`text-xs ${
                              !referrer.referrer_is_active
                                ? "opacity-60 cursor-not-allowed"
                                : ""
                            }`}
                            disabled={!referrer.referrer_is_active}
                          >
                            {isIgnored ? "Clear ignore" : "Ignore referrer"}
                          </Button>
                        )}
                        {!referrer.referrer_is_active && (
                          <div className="bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                            DISQUALIFIED
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <UserX className="h-4 w-4 text-red-600" />
                      Referred Accounts
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-3 w-8"></th>
                            <th className="text-left py-2 px-3">ID</th>
                            <th className="text-left py-2 px-3">Email</th>
                            <th className="text-left py-2 px-3">Name</th>
                            <th className="text-left py-2 px-3">Phone</th>
                            <th className="text-left py-2 px-3">IP Address</th>
                            <th className="text-left py-2 px-3">Location</th>
                            <th className="text-left py-2 px-3">ID Number</th>
                            <th className="text-left py-2 px-3">Created</th>
                            <th className="text-left py-2 px-3">Referred</th>
                            <th className="text-left py-2 px-3">Match</th>
                            <th className="text-left py-2 px-3">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(referrer.referred_users || []).map((user) => (
                            <tr
                              key={user.id}
                              className={`border-b last:border-0 hover:bg-gray-50 ${
                                user.isActive === false
                                  ? "bg-gray-50 opacity-60"
                                  : (user.matchesReferrer ||
                                      user.sharedIpCluster) &&
                                    !referrer.referrer_ignore_fraud &&
                                    !user.ignoreFraud
                                    ? "bg-red-50"
                                    : ""
                              }`}
                              data-testid={`row-fraud-user-${user.id}`}
                            >
                              <td className="py-2 px-3">
                                {user.matchesReferrer ||
                                user.sharedIpCluster ? (
                                  <Checkbox
                                    checked={selectedUserIds.includes(user.id)}
                                    onCheckedChange={() =>
                                      toggleUserSelection(user.id)
                                    }
                                    aria-label={`Select user ${user.id}`}
                                  />
                                ) : null}
                              </td>
                              <td className="py-2 px-3 font-mono text-xs">
                                {user.id}
                              </td>
                              <td className="py-2 px-3 text-xs">
                                {user.email}
                              </td>
                              <td className="py-2 px-3 text-xs">
                                {user.firstName} {user.lastName}
                              </td>
                              <td className="py-2 px-3 text-xs">
                                {user.phone || "-"}
                              </td>
                              <td className="py-2 px-3 font-mono text-xs">
                                {user.lastLoginIp || "-"}
                                {user.sharedIpCluster &&
                                  user.ipClusterSize > 1 && (
                                    <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-800 font-semibold">
                                      {user.ipClusterSize}x
                                    </span>
                                  )}
                              </td>
                              <td className="py-2 px-3 text-xs">
                                {user.lastLoginLocation || "-"}
                              </td>
                              <td className="py-2 px-3 font-mono text-xs">
                                {user.idNumber || "-"}
                              </td>
                              <td className="py-2 px-3 text-xs">
                                {new Date(user.createdAt).toLocaleDateString()}
                              </td>
                              <td className="py-2 px-3 text-xs">
                                {new Date(user.referredAt).toLocaleDateString()}
                              </td>
                              <td className="py-2 px-3">
                                {user.isActive === false ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-200 text-gray-700">
                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                    Deactivated
                                  </span>
                                ) : referrer.referrer_ignore_fraud ||
                                  user.ignoreFraud ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Ignored
                                  </span>
                                ) : user.matchesReferrer &&
                                  user.sharedIpCluster ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800 font-semibold">
                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                    Both
                                  </span>
                                ) : user.matchesReferrer ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                    Referrer
                                  </span>
                                ) : user.sharedIpCluster ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800">
                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                    IP Cluster
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    OK
                                  </span>
                                )}
                              </td>
                              <td className="py-2 px-3">
                                {user.matchesReferrer || user.sharedIpCluster ? (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="xs"
                                    className="text-[11px] px-2 py-1 h-auto"
                                    onClick={async () => {
                                      const nextIgnore = !user.ignoreFraud;
                                      if (
                                        !confirm(
                                          `${nextIgnore ? "Ignore" : "Clear ignore for"} referred account ${
                                            user.firstName
                                              ? `${user.firstName} ${user.lastName}`
                                              : user.email
                                          } (ID ${user.id})?\n\n${
                                            nextIgnore
                                              ? "This account will no longer be treated as suspicious in fraud detection."
                                              : "This account can be flagged as suspicious again if patterns still match."
                                          }`
                                        )
                                      ) {
                                        return;
                                      }
                                      try {
                                        await apiRequest(
                                          "PUT",
                                          `/api/admin/users/${user.id}/fraud-override`,
                                          {
                                            body: { ignore: nextIgnore },
                                          }
                                        );
                                        toast({
                                          title: nextIgnore
                                            ? "Account Ignored"
                                            : "Ignore Cleared",
                                          description: nextIgnore
                                            ? "This referred account will no longer be treated as suspicious."
                                            : "This referred account can be flagged again if patterns still match.",
                                        });
                                        refetch();
                                      } catch (error) {
                                        toast({
                                          title: "Error",
                                          description:
                                            "Failed to update ignore flag for referred account",
                                            variant: "destructive",
                                          });
                                        }
                                      }}
                                      disabled={user.isActive === false}
                                    >
                                    {user.ignoreFraud ? "Clear" : "Ignore"}
                                  </Button>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Suspicious Referral Activity Detected
            </h3>
            <p className="text-gray-600">
              No challenge referrals found where referred users share
              IP/location with referrer or with each other
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Betfred Management tab component
function BetfredManagementTab() {
  const { toast } = useToast();
  const [settings, setSettings] = useState({
    enabled: false,
    required: false,
    entriesAwarded: 50,
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Retry dialog state
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [selectedSignup, setSelectedSignup] = useState<any>(null);
  const [retryEmail, setRetryEmail] = useState("");

  // Fetch Betfred settings
  const { data: betfredSettings, isLoading: loadingSettings } = useQuery<{
    enabled: boolean;
    required: boolean;
    entriesAwarded: number;
  }>({
    queryKey: ["/api/admin/betfred-settings"],
  });

  // Fetch Betfred signups
  const {
    data: betfredSignups = [],
    isLoading: loadingSignups,
    refetch: refetchSignups,
  } = useQuery<any[]>({
    queryKey: ["/api/admin/betfred-signups"],
  });

  // Update local state when data is fetched
  useEffect(() => {
    if (betfredSettings) {
      setSettings(betfredSettings);
    }
  }, [betfredSettings]);

  // Save settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: async (newSettings: typeof settings) => {
      const response = await apiRequest("PUT", "/api/admin/betfred-settings", {
        body: newSettings,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/betfred-settings"],
      });
      toast({
        title: "Betfred Settings Updated",
        description: "Settings have been saved successfully!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Save Failed",
        description: error?.message || "Failed to save Betfred settings",
        variant: "destructive",
      });
    },
  });

  // Verify signup mutation (manual verification)
  const verifySignupMutation = useMutation({
    mutationFn: async (signupId: number) => {
      const response = await apiRequest(
        "POST",
        `/api/admin/betfred-signups/${signupId}/verify`
      );
      return response;
    },
    onSuccess: () => {
      refetchSignups();
      toast({
        title: "Signup Verified",
        description:
          "Betfred signup has been manually verified and entries awarded!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Verification Failed",
        description: error?.message || "Failed to verify Betfred signup",
        variant: "destructive",
      });
    },
  });

  // API verification mutation (using Betfred API)
  const apiVerifyMutation = useMutation({
    mutationFn: async (signupId: number) => {
      const response = await apiRequest(
        "POST",
        `/api/admin/betfred-signups/${signupId}/verify-now`
      );
      return response;
    },
    onSuccess: () => {
      refetchSignups();
      toast({
        title: "API Verification Complete",
        description: "Betfred API verification has been triggered!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "API Verification Failed",
        description: error?.message || "Failed to trigger API verification",
        variant: "destructive",
      });
    },
  });

  // Retry mutation for dialog
  const retryMutation = useMutation({
    mutationFn: async (data: { signupId: number; email?: string }) => {
      const response = await apiRequest(
        "POST",
        `/api/admin/betfred-signups/${data.signupId}/retry`,
        {
          body: {
            ...(data.email && { newEmail: data.email }),
          },
        }
      );
      return response.json();
    },
    onSuccess: (data) => {
      refetchSignups();
      toast({
        title: "Retry Scheduled Successfully!",
        description: data.message || "Verification retry has been scheduled.",
      });
      setRetryDialogOpen(false);
      setSelectedSignup(null);
      setRetryEmail("");
    },
    onError: (error: any) => {
      toast({
        title: "Retry Failed",
        description: error?.message || "Failed to schedule retry",
        variant: "destructive",
      });
    },
  });

  const handleSaveSettings = () => {
    saveSettingsMutation.mutate(settings);
  };

  const handleVerifySignup = (signupId: number) => {
    verifySignupMutation.mutate(signupId);
  };

  const handleApiVerify = (signupId: number) => {
    apiVerifyMutation.mutate(signupId);
  };

  const handleOpenRetryDialog = (signup: any) => {
    setSelectedSignup(signup);
    setRetryEmail("");
    setRetryDialogOpen(true);
  };

  const handleQuickRetry = () => {
    if (selectedSignup) {
      retryMutation.mutate({ signupId: selectedSignup.id });
    }
  };

  const handleRetryWithNewEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (!retryEmail.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter a new email address",
        variant: "destructive",
      });
      return;
    }
    if (selectedSignup) {
      retryMutation.mutate({
        signupId: selectedSignup.id,
        email: retryEmail.trim(),
      });
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "verified":
        return "default";
      case "pending":
        return "secondary";
      case "failed":
        return "destructive";
      default:
        return "outline";
    }
  };

  const formatMinutesAgo = (scheduledAt: string) => {
    const now = new Date();
    const scheduled = new Date(scheduledAt);
    const diffMs = now.getTime() - scheduled.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 0) return `in ${Math.abs(diffMins)} minutes`;
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return `${Math.floor(diffMins / 1440)} days ago`;
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ExternalLink className="h-6 w-6" />
          Betfred Management
        </h2>
        <p className="text-gray-600 mt-1">
          Manage Betfred signup feature and verify user signups
        </p>
      </div>

      {/* Global Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle>Global Betfred Settings</CardTitle>
          <CardDescription>
            Configure global Betfred signup feature settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="enabled">Global Feature Enabled</Label>
              <Select
                value={settings.enabled ? "true" : "false"}
                onValueChange={(value) =>
                  setSettings({ ...settings, enabled: value === "true" })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Enabled</SelectItem>
                  <SelectItem value="false">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="required">Required for Entry</Label>
              <Select
                value={settings.required ? "true" : "false"}
                onValueChange={(value) =>
                  setSettings({ ...settings, required: value === "true" })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Required</SelectItem>
                  <SelectItem value="false">Optional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="entriesAwarded">Entries Awarded</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={settings.entriesAwarded}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    entriesAwarded: parseInt(e.target.value) || 50,
                  })
                }
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleSaveSettings}
              disabled={saveSettingsMutation.isPending}
            >
              <Save className="h-4 w-4 mr-2" />
              {saveSettingsMutation.isPending ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Betfred Signups Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Betfred Signups Management
          </CardTitle>
          <CardDescription>
            Monitor verification status, API calls, and manage signups with
            30-minute delay system
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingSignups ? (
            <div className="text-center py-4">Loading signups...</div>
          ) : betfredSignups.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ExternalLink className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">No Betfred Signups Yet</p>
              <p className="text-sm">
                User signups will appear here with real-time verification
                tracking
              </p>
            </div>
          ) : (
            <>
              {/* Summary Stats */}
              <TooltipProvider>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 cursor-help">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {
                            betfredSignups.filter(
                              (s: any) => s.verificationStatus === "verified"
                            ).length
                          }
                        </div>
                        <div className="text-sm text-blue-600 dark:text-blue-400">
                          Verified
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Total number of verified Betfred signups</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 cursor-help">
                        <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                          {
                            betfredSignups.filter(
                              (s: any) => s.verificationStatus === "pending"
                            ).length
                          }
                        </div>
                        <div className="text-sm text-yellow-600 dark:text-yellow-400">
                          Pending
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Signups awaiting verification (30-minute delay system)
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 cursor-help">
                        <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                          {
                            betfredSignups.filter(
                              (s: any) => s.verificationStatus === "failed"
                            ).length
                          }
                        </div>
                        <div className="text-sm text-red-600 dark:text-red-400">
                          Failed
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Signups that failed verification through API</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 cursor-help">
                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                          {
                            new Set(
                              betfredSignups
                                .filter(
                                  (s: any) =>
                                    s.verificationStatus === "verified"
                                )
                                .map((s: any) => s.userId)
                            ).size
                          }
                        </div>
                        <div className="text-sm text-purple-600 dark:text-purple-400">
                          Unique Verified
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Number of unique users with at least one verified signup
                      </p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 cursor-help">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {betfredSignups.length}
                        </div>
                        <div className="text-sm text-green-600 dark:text-green-400">
                          Total Signups
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Total number of all Betfred signups regardless of status
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>

              {/* Calculate pagination */}
              {(() => {
                const totalItems = betfredSignups.length;
                const totalPages = Math.ceil(totalItems / itemsPerPage);
                const startIndex = (currentPage - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                const paginatedSignups = betfredSignups.slice(
                  startIndex,
                  endIndex
                );

                return (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Betfred Email</TableHead>
                          <TableHead>Competition</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Scheduled</TableHead>
                          <TableHead>API Response</TableHead>
                    <TableHead>Participants</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedSignups.map((signup: any) => (
                          <TableRow key={signup.id}>
                            <TableCell className="font-medium">
                              <div className="space-y-1">
                                <div>{signup.userName || "Unknown User"}</div>
                                <div className="text-xs text-gray-500">
                                  ID: {signup.userId}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {signup.betfredEmail}
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="font-medium">
                                  {signup.competitionTitle || "Unknown"}
                                </div>
                                <div className="text-xs text-gray-500">
                                  ID: {signup.competitionId}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={getStatusBadgeVariant(
                                  signup.verificationStatus || "unknown"
                                )}
                              >
                                {signup.verificationStatus || "unknown"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="text-sm">
                                  {signup.verificationScheduledAt
                                    ? formatSADateTime(
                                        signup.verificationScheduledAt
                                      )
                                    : "Not scheduled"}
                                </div>
                                {signup.verificationScheduledAt && (
                                  <div className="text-xs text-gray-500">
                                    {formatMinutesAgo(
                                      signup.verificationScheduledAt
                                    )}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {signup.apiResponse ? (
                                  <div className="text-xs">
                                    <div
                                      className={`font-medium ${
                                        signup.apiResponse.success
                                          ? "text-green-600"
                                          : "text-red-600"
                                      }`}
                                    >
                                      {signup.apiResponse.success
                                        ? "Success"
                                        : "Failed"}
                                    </div>
                                    {signup.apiResponse.message && (
                                      <div className="text-gray-500 max-w-[200px] truncate">
                                        {signup.apiResponse.message}
                                      </div>
                                    )}
                                    {signup.lastVerificationAttempt && (
                                      <div className="text-gray-400">
                                        {formatMinutesAgo(
                                          signup.lastVerificationAttempt
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400">
                                    No API call yet
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-center">
                                <div className="font-bold text-lg">
                                  {signup.entriesAwarded || 0}
                                </div>
                                <div className="text-xs text-gray-500">
                                  entries
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                {(signup.verificationStatus === "pending" ||
                                  !signup.verificationStatus) && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleApiVerify(signup.id)}
                                      disabled={apiVerifyMutation.isPending}
                                      title="Trigger API verification now"
                                    >
                                      <Zap className="h-4 w-4 mr-1" />
                                      {apiVerifyMutation.isPending
                                        ? "API..."
                                        : "API"}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() =>
                                        handleVerifySignup(signup.id)
                                      }
                                      disabled={verifySignupMutation.isPending}
                                      title="Manual verification (bypasses API)"
                                    >
                                      <CheckCircle className="h-4 w-4 mr-1" />
                                      {verifySignupMutation.isPending
                                        ? "Manual..."
                                        : "Manual"}
                                    </Button>
                                  </>
                                )}
                                {signup.verificationStatus === "verified" && (
                                  <Badge variant="default" className="text-xs">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Complete
                                  </Badge>
                                )}
                                {signup.verificationStatus === "failed" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      handleOpenRetryDialog(signup)
                                    }
                                    disabled={retryMutation.isPending}
                                    title="Retry API verification"
                                    data-testid="button-retry-verification"
                                  >
                                    <RotateCcw className="h-4 w-4 mr-1" />
                                    Retry
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4">
                        <div className="text-sm text-gray-500">
                          Showing {startIndex + 1} to{" "}
                          {Math.min(endIndex, totalItems)} of {totalItems}{" "}
                          signups
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setCurrentPage(Math.max(1, currentPage - 1))
                            }
                            disabled={currentPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4" />
                            Previous
                          </Button>
                          <div className="text-sm text-gray-500">
                            Page {currentPage} of {totalPages}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setCurrentPage(
                                Math.min(totalPages, currentPage + 1)
                              )
                            }
                            disabled={currentPage === totalPages}
                          >
                            Next
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* System Information */}
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  System Information
                </h4>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <div>• Verification service runs every 30 seconds</div>
                  <div>
                    • New signups have 30-minute delay before API verification
                  </div>
                  <div>• Failed verifications can be retried manually</div>
                  <div>
                    • Entries are awarded only after successful verification
                  </div>
                  <div>
                    • Manual verification bypasses API and awards entries
                    immediately
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Competition-Specific Betfred Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Competition-Specific Betfred Settings</CardTitle>
          <CardDescription>
            Enable Betfred signup for specific competitions (Global setting must
            be enabled first)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {settings.enabled ? (
            <CompetitionBetfredManager />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <ExternalLink className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">
                Global Betfred Feature Disabled
              </p>
              <p className="text-sm">
                Enable the global Betfred feature above to manage
                competition-specific settings.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Retry Verification Dialog */}
      <Dialog open={retryDialogOpen} onOpenChange={setRetryDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5" style={{ color: "#EE600F" }} />
              Retry Betfred Verification
            </DialogTitle>
          </DialogHeader>

          {selectedSignup && (
            <div className="space-y-4">
              {/* Status Display */}
              <Card className="border-2 border-orange-200 bg-orange-50">
                <CardContent className="p-4">
                  <div className="text-center">
                    <AlertCircle className="h-12 w-12 text-orange-600 mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-orange-900 mb-2">
                      Verification Failed
                    </h3>
                    <p className="text-orange-700 mb-3">
                      {selectedSignup.verificationStatus === "invalid_email"
                        ? "Email wasn't found in Betfred's system. User may need to sign up first."
                        : "Technical issue occurred during verification."}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Current Email Display */}
              <div className="p-3 bg-gray-100 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">
                  Current Betfred Email:
                </div>
                <div className="text-sm font-medium text-gray-800">
                  {selectedSignup.betfredEmail}
                </div>
              </div>

              {/* Retry Options */}
              <div className="space-y-4 pt-4 border-t">
                <div className="text-center">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Retry Verification Options
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Choose how to retry the verification process
                  </p>
                </div>

                {/* Quick retry with same email */}
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-sm font-medium text-blue-900 mb-2">
                      Retry with Same Email:
                    </div>
                    <div className="text-xs text-blue-700 mb-3">
                      Retry verification using: {selectedSignup.betfredEmail}
                    </div>
                    <Button
                      onClick={handleQuickRetry}
                      disabled={retryMutation.isPending}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="button-quick-retry"
                    >
                      {retryMutation.isPending ? (
                        <>
                          <Clock className="h-4 w-4 mr-2 animate-spin" />
                          Retrying...
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-4 w-4 mr-2" />
                          Retry with Same Email
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="text-center text-gray-400">
                  <span className="text-sm">OR</span>
                </div>

                {/* Try with different email */}
                <div className="space-y-3">
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="text-sm font-medium text-orange-900 mb-2">
                      Retry with Different Email:
                    </div>
                    <form
                      onSubmit={handleRetryWithNewEmail}
                      className="space-y-3"
                    >
                      <Input
                        type="email"
                        placeholder="new.email@example.com"
                        value={retryEmail}
                        onChange={(e) => setRetryEmail(e.target.value)}
                        disabled={retryMutation.isPending}
                        className="w-full"
                        data-testid="input-retry-email"
                      />
                      <Button
                        type="submit"
                        disabled={retryMutation.isPending || !retryEmail.trim()}
                        className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                        data-testid="button-retry-different-email"
                      >
                        {retryMutation.isPending
                          ? "Retrying..."
                          : "Retry with Different Email"}
                      </Button>
                    </form>
                  </div>
                </div>
              </div>

              {/* Cancel Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setRetryDialogOpen(false)}
                  disabled={retryMutation.isPending}
                  data-testid="button-cancel-retry"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Competition-Specific Betfred Manager Component
function CompetitionBetfredManager() {
  const { toast } = useToast();

  // Fetch all competitions with forced refresh
  const { data: competitions = [], refetch: refetchCompetitions } = useQuery<
    any[]
  >({
    queryKey: ["/api/admin/competitions"],
    staleTime: 0, // Always consider data stale
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });

  // Toggle Betfred for specific competition
  const toggleCompetitionBetfredMutation = useMutation({
    mutationFn: async ({
      competitionId,
      enabled,
    }: {
      competitionId: number;
      enabled: boolean;
    }) => {
      const response = await apiRequest(
        "PUT",
        `/api/competitions/${competitionId}`,
        {
          body: {
            betfredEnabled: enabled,
          },
        }
      );
      return response.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/competitions"] });
      toast({
        title: `Betfred ${variables.enabled ? "Enabled" : "Disabled"}`,
        description: `Betfred signup has been ${
          variables.enabled ? "enabled" : "disabled"
        } for this competition.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description:
          error?.message || "Failed to update competition Betfred setting",
        variant: "destructive",
      });
    },
  });

  const handleToggleBetfred = (
    competitionId: number,
    currentlyEnabled: boolean
  ) => {
    toggleCompetitionBetfredMutation.mutate({
      competitionId,
      enabled: !currentlyEnabled,
    });
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 mb-4">
        Select which competitions should offer Betfred signup. Users will only
        see the Betfred option on competitions where it's enabled.
      </div>

      {competitions.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Competition</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Prize</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Betfred Enabled</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {competitions.map((competition: any) => (
              <TableRow key={competition.id}>
                <TableCell className="font-medium">
                  {competition.title}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{competition.type}</Badge>
                </TableCell>
                <TableCell>{competition.prize}</TableCell>
                <TableCell>
                  {(() => {
                    const status = getCompetitionStatus(competition);
                    return (
                      <Badge className={getStatusBadgeStyle(status)}>
                        {status}
                      </Badge>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      competition.betfredEnabled ? "default" : "secondary"
                    }
                    className={
                      competition.betfredEnabled
                        ? "bg-blue-100 text-blue-900"
                        : "bg-gray-100 text-gray-800"
                    }
                  >
                    {competition.betfredEnabled ? "Enabled" : "Disabled"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button
                    variant={
                      competition.betfredEnabled ? "destructive" : "default"
                    }
                    size="sm"
                    onClick={() =>
                      handleToggleBetfred(
                        competition.id,
                        competition.betfredEnabled
                      )
                    }
                    disabled={toggleCompetitionBetfredMutation.isPending}
                    className={
                      competition.betfredEnabled
                        ? "bg-red-600 hover:bg-red-700 text-white"
                        : "bg-blue-900 hover:bg-blue-950 text-white"
                    }
                  >
                    {toggleCompetitionBetfredMutation.isPending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : competition.betfredEnabled ? (
                      "Disable"
                    ) : (
                      "Enable"
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium mb-2">No Competitions Found</p>
          <p className="text-sm">
            Create competitions first to manage their Betfred settings.
          </p>
        </div>
      )}
    </div>
  );
}

// Facebook Login Redirect Configuration Component
function FacebookLoginRedirectConfig() {
  const { toast } = useToast();
  const [selectedCompetition, setSelectedCompetition] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch current configuration
  const { data: redirectConfig, refetch: refetchConfig } = useQuery<{
    competitionId: number | null;
    enabled: boolean;
    competition: { id: number; title: string; status: string; endDate: string } | null;
  }>({
    queryKey: ["/api/admin/facebook-login-redirect"],
  });

  // Fetch all competitions for dropdown - need admin endpoint
  const { data: competitionsData, isLoading: loadingCompetitions } = useQuery<{ competitions: any[] }>({
    queryKey: ["/api/admin/competitions/active"],
  });

  const competitions = competitionsData?.competitions || [];

  // Update local state when config is fetched
  useEffect(() => {
    if (redirectConfig !== undefined) {
      setSelectedCompetition(redirectConfig?.competitionId || null);
    }
  }, [redirectConfig]);

  // Use mutation for better error handling with apiRequest helper
  const saveMutation = useMutation({
    mutationFn: async (competitionId: number | null) => {
      const response = await apiRequest("POST", "/api/admin/facebook-login-redirect", {
        body: { competitionId },
      });
      return response.json();
    },
    onSuccess: (data) => {
      console.log('[FACEBOOK_LOGIN_CONFIG] Save successful, response:', data);
      // Update local state immediately
      setSelectedCompetition(data.competitionId || null);
      // Refetch server config to ensure consistency
      refetchConfig();
      toast({
        title: "Configuration Updated",
        description: "Facebook login redirect has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      console.error("Error saving facebook login config:", error);
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update facebook login redirect configuration.",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    saveMutation.mutate(selectedCompetition);
  };

  // Filter to show ONLY active and scheduled competitions (exclude ended, cancelled, draft)
  const availableCompetitions = competitions.filter(
    (comp: any) => {
      // Only show active or scheduled - explicitly exclude ended, cancelled, draft
      const isAvailable = comp.status === "active" || comp.status === "scheduled";
      console.log(`[FACEBOOK_LOGIN_CONFIG] Competition ${comp.id} "${comp.title}": status="${comp.status}", endDate=${comp.endDate}, isAvailable=${isAvailable}`);
      return isAvailable;
    }
  );

  console.log('[FACEBOOK_LOGIN_CONFIG] Total competitions fetched:', competitions.length);
  console.log('[FACEBOOK_LOGIN_CONFIG] Available (active/scheduled):', availableCompetitions.length);
  console.log('[FACEBOOK_LOGIN_CONFIG] Filtered out statuses:', competitions.filter((c: any) => c.status !== "active" && c.status !== "scheduled").map((c: any) => c.status));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Facebook className="h-5 w-5" />
          Facebook Login Redirect
        </CardTitle>
        <CardDescription>
          Configure which competition users are redirected to from the /facebook-login page
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Target Competition
          </label>
          {loadingCompetitions ? (
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                <span className="text-sm text-gray-600">Loading competitions...</span>
              </div>
            </div>
          ) : (
            <select
              value={selectedCompetition || ""}
              onChange={(e) =>
                setSelectedCompetition(e.target.value ? parseInt(e.target.value) : null)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">No redirect (Dashboard)</option>
              {availableCompetitions.map((comp: any) => (
                <option key={comp.id} value={comp.id}>
                  {comp.title} ({comp.status})
                </option>
              ))}
            </select>
          )}
          <p className="text-xs text-gray-500">
            Select a competition to redirect users to after Facebook login, or leave empty to redirect to dashboard
          </p>
          {!loadingCompetitions && availableCompetitions.length === 0 && (
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              No active or scheduled competitions found. Create one first.
            </p>
          )}
        </div>

        {redirectConfig?.competition && (
          <div className={`border rounded-lg p-4 ${
            redirectConfig.competition.status === "ended" || redirectConfig.competition.status === "cancelled"
              ? "bg-amber-50 border-amber-200"
              : "bg-blue-50 border-blue-200"
          }`}>
            <div className="flex items-start gap-2 mb-2">
              {(redirectConfig.competition.status === "ended" || redirectConfig.competition.status === "cancelled") && (
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              )}
              <h4 className={`text-sm font-medium ${
                redirectConfig.competition.status === "ended" || redirectConfig.competition.status === "cancelled"
                  ? "text-amber-900"
                  : "text-blue-900"
              }`}>
                Current Configuration
                {(redirectConfig.competition.status === "ended" || redirectConfig.competition.status === "cancelled") && 
                  " - Competition Inactive"}
              </h4>
            </div>
            <div className={`text-sm ${
              redirectConfig.competition.status === "ended" || redirectConfig.competition.status === "cancelled"
                ? "text-amber-700"
                : "text-blue-700"
            }`}>
              <p>
                <strong>Competition:</strong> {redirectConfig.competition.title}
              </p>
              <p>
                <strong>Status:</strong> {redirectConfig.competition.status}
              </p>
              {(redirectConfig.competition.status === "ended" || redirectConfig.competition.status === "cancelled") && (
                <p className="text-amber-800 font-medium mt-2">
                  ⚠️ This competition is {redirectConfig.competition.status}. Users will be redirected to dashboard instead.
                </p>
              )}
              <p className="mt-2">
                <strong>Link:</strong>{" "}
                <a
                  href="/facebook-login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={redirectConfig.competition.status === "ended" || redirectConfig.competition.status === "cancelled"
                    ? "text-amber-600 hover:underline"
                    : "text-blue-600 hover:underline"}
                >
                  /facebook-login
                </a>
              </p>
            </div>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          {saveMutation.isPending ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Configuration
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}

// System Settings tab component
function SystemSettingsTab() {
  const { toast } = useToast();
  const [settings, setSettings] = useState({
    enabled: true,
    title: "Spin to Win Entries!",
    description:
      "Spin the wheel daily for a chance to win competition entries!",
  });
  const [activeHours, setActiveHours] = useState({
    startTime: "00:00",
    endTime: "23:59",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [editingPrize, setEditingPrize] = useState<any>(null);
  const [isEditingPrizes, setIsEditingPrizes] = useState(false);

  // Fetch current spinner settings
  const { data: spinnerSettings, isLoading: loadingSettings } = useQuery<{
    enabled: boolean;
    title: string;
    description: string;
  }>({
    queryKey: ["/api/spinner/settings"],
  });

  // Fetch spinner prizes for admin management
  const {
    data: spinnerPrizes = [],
    isLoading: loadingPrizes,
    refetch: refetchPrizes,
  } = useQuery<any[]>({
    queryKey: ["/api/spinner/results"],
  });

  // Update local state when data is fetched
  useEffect(() => {
    if (spinnerSettings) {
      setSettings(spinnerSettings);
    }
  }, [spinnerSettings]);

  // Save settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: async (newSettings: typeof settings) => {
      const response = await apiRequest("POST", "/api/admin/spinner/settings", {
        body: newSettings,
      });
      return response.json();
    },
    onSuccess: () => {
      setIsSaving(false);
      // Invalidate spinner settings cache to refresh all components
      queryClient.invalidateQueries({ queryKey: ["/api/spinner/settings"] });
      toast({
        title: "Settings Updated",
        description: "Spinner settings have been saved successfully!",
      });
    },
    onError: (error: any) => {
      setIsSaving(false);
      toast({
        title: "Save Failed",
        description: error?.message || "Failed to save settings",
        variant: "destructive",
      });
    },
  });

  // Reset all spins mutation
  const resetSpinsMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        "POST",
        "/api/admin/spinner/reset-spins"
      );
      return response.json();
    },
    onSuccess: (data) => {
      setIsResetting(false);
      toast({
        title: "Spins Reset Successfully",
        description: `${data.deletedCount} user spins have been reset. All users can now spin again.`,
      });
    },
    onError: (error: any) => {
      setIsResetting(false);
      toast({
        title: "Reset Failed",
        description: error?.message || "Failed to reset user spins",
        variant: "destructive",
      });
    },
  });

  // Prize management mutations
  const updatePrizeMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await apiRequest(
        "PUT",
        `/api/admin/spinner/prizes/${id}`,
        { body: data }
      );
      return response.json();
    },
    onSuccess: () => {
      refetchPrizes();
      setEditingPrize(null);
      toast({ title: "Prize updated successfully!" });
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error?.message,
        variant: "destructive",
      });
    },
  });

  const deletePrizeMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest(
        "DELETE",
        `/api/admin/spinner/prizes/${id}`
      );
      return response.json();
    },
    onSuccess: () => {
      refetchPrizes();
      toast({ title: "Prize deleted successfully!" });
    },
    onError: (error: any) => {
      toast({
        title: "Delete failed",
        description: error?.message,
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    setIsSaving(true);
    saveSettingsMutation.mutate(settings);
  };

  const handleResetSpins = () => {
    setIsResetting(true);
    resetSpinsMutation.mutate();
  };

  // Clear cache mutation
  const clearCacheMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/admin/clear-cache");
      return response.json();
    },
    onSuccess: () => {
      // Invalidate all queries to force refetch
      queryClient.invalidateQueries();
      toast({
        title: "Cache Cleared",
        description: "All system cache has been cleared successfully!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Clear Failed",
        description: error?.message || "Failed to clear cache",
        variant: "destructive",
      });
    },
  });

  const handleClearCache = () => {
    clearCacheMutation.mutate();
  };

  const handleUpdatePrize = (prize: any) => {
    updatePrizeMutation.mutate({ id: prize.id, data: prize });
  };

  const handleDeletePrize = (id: number) => {
    if (confirm("Are you sure you want to delete this prize?")) {
      deletePrizeMutation.mutate(id);
    }
  };

  if (loadingSettings) {
    return (
      <div className="space-y-6">
        <div className="border-b border-gray-200 pb-4">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="h-6 w-6" />
            System Settings
          </h2>
          <p className="text-gray-600 mt-1">
            Configure system features and controls
          </p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="h-6 w-6" />
          System Settings
        </h2>
        <p className="text-gray-600 mt-1">
          Configure system features and controls
        </p>
      </div>

      {/* Win Entries Spinner Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Win Entries Spinner
          </CardTitle>
          <CardDescription>
            Control the availability and content of the daily entry spinner
            feature
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="spinner-enabled"
              checked={settings.enabled}
              onChange={(e) =>
                setSettings({ ...settings, enabled: e.target.checked })
              }
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <div>
              <label
                htmlFor="spinner-enabled"
                className="text-sm font-medium text-gray-700"
              >
                Enable Win Entries Spinner
              </label>
              <p className="text-xs text-gray-500">
                When disabled, users will see a "Coming Soon" message instead of
                the spinner
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label
                htmlFor="spinner-title"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Page Title
              </label>
              <input
                type="text"
                id="spinner-title"
                value={settings.title}
                onChange={(e) =>
                  setSettings({ ...settings, title: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Spin to Win Entries!"
              />
            </div>

            <div>
              <label
                htmlFor="spinner-description"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Page Description
              </label>
              <textarea
                id="spinner-description"
                value={settings.description}
                onChange={(e) =>
                  setSettings({ ...settings, description: e.target.value })
                }
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Spin the wheel daily for a chance to win competition entries!"
              />
            </div>
          </div>

          {/* Cache Management Section */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              Cache Management
            </h4>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <h5 className="text-sm font-medium text-yellow-800 mb-1">
                    Cache Disabled for Users
                  </h5>
                  <p className="text-sm text-yellow-700 mb-2">
                    User data caching has been disabled to ensure users always
                    get the latest information. Only admin operations use
                    minimal caching for performance.
                  </p>
                  <p className="text-xs text-yellow-600">
                    Use "Clear Cache" to force refresh of admin dashboard data
                    if needed.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Active Hours Section */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              Active Hours
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  value={activeHours.startTime}
                  onChange={(e) =>
                    setActiveHours({
                      ...activeHours,
                      startTime: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  value={activeHours.endTime}
                  onChange={(e) =>
                    setActiveHours({ ...activeHours, endTime: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Set the time window when the spinner is available (GMT+2 timezone)
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </button>

          <div className="flex gap-2">
            <button
              onClick={handleClearCache}
              disabled={clearCacheMutation.isPending}
              className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {clearCacheMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Clearing...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Clear Cache
                </>
              )}
            </button>

            <button
              onClick={handleResetSpins}
              disabled={isResetting}
              className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {isResetting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Resetting...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4" />
                  Reset All Spins
                </>
              )}
            </button>
          </div>
        </CardFooter>
      </Card>

      {/* Facebook Login Redirect Configuration */}
      <FacebookLoginRedirectConfig />

      {/* Prize Management Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Prize Management
          </CardTitle>
          <CardDescription>
            Configure the prizes and their probabilities for the spinner wheel
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingPrizes ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="space-y-3">
              {spinnerPrizes.map((prize: any) => (
                <div
                  key={prize.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                  style={{ borderLeft: `4px solid ${prize.color}` }}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: prize.color }}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{prize.label}</div>
                      <div className="text-sm text-gray-500">
                        {prize.entries} entries •{" "}
                        {((prize.probability / 10000) * 100).toFixed(1)}% chance
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingPrize(prize)}
                      className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeletePrize(prize.id)}
                      className="px-3 py-1 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Prize Dialog */}
      {editingPrize && (
        <Dialog
          open={!!editingPrize}
          onOpenChange={() => setEditingPrize(null)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Prize</DialogTitle>
              <DialogDescription>
                Modify the prize details and probability
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Label
                </label>
                <input
                  type="text"
                  value={editingPrize.label}
                  onChange={(e) =>
                    setEditingPrize({ ...editingPrize, label: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Entries
                </label>
                <input
                  type="number"
                  value={editingPrize.entries}
                  onChange={(e) =>
                    setEditingPrize({
                      ...editingPrize,
                      entries: Number(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Probability (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={(editingPrize.probability / 100).toFixed(1)}
                  onChange={(e) =>
                    setEditingPrize({
                      ...editingPrize,
                      probability: Number(e.target.value) * 100,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <input
                  type="color"
                  value={editingPrize.color}
                  onChange={(e) =>
                    setEditingPrize({ ...editingPrize, color: e.target.value })
                  }
                  className="w-full h-10 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setEditingPrize(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdatePrize(editingPrize)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Save Changes
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Winner Selection Component
function WinnerSelectionTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedCompetition, setSelectedCompetition] = useState<any>(null);
  const [winnerResult, setWinnerResult] = useState<any>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  // Fetch competitions
  const {
    data: competitions,
    isLoading: competitionsLoading,
    error: competitionsError,
  } = useQuery<Competition[]>({
    queryKey: ["/api/admin/competitions"],
    enabled: !!user,
  });
  const competitionsData = (competitions as any[]) || [];

  // Fetch all winners
  const {
    data: allWinners,
    refetch: refetchWinners,
    isLoading: winnersLoading,
    error: winnersError,
  } = useQuery<{ winners: any[]; totalWinners: number }>({
    queryKey: ["/api/admin/winners"],
    enabled: !!user,
  });
  const winnersData = (allWinners as any) || { winners: [], totalWinners: 0 };

  // Select winner mutation
  const selectWinnerMutation = useMutation({
    mutationFn: async (competitionId: number) => {
      const response = await apiRequest(
        "POST",
        `/api/admin/competitions/${competitionId}/select-winner`
      );
      return response.json();
    },
    onSuccess: (data) => {
      setWinnerResult(data);
      setIsSelecting(false);
      refetchWinners();
      toast({
        title: data.alreadySelected
          ? "Winner Already Selected"
          : "Winner Selected Successfully!",
        description: data.alreadySelected
          ? `A winner was already selected for this competition.`
          : `Winner selected using cryptographically secure randomization with ${data.competition.totalEntries} total entries.`,
      });
    },
    onError: (error: any) => {
      setIsSelecting(false);
      toast({
        title: "Error",
        description: error?.message || "Failed to select winner",
        variant: "destructive",
      });
    },
  });

  const handleSelectWinner = () => {
    if (!selectedCompetition) {
      toast({
        title: "No Competition Selected",
        description: "Please select a competition first",
        variant: "destructive",
      });
      return;
    }

    setIsSelecting(true);
    setWinnerResult(null);
    selectWinnerMutation.mutate(selectedCompetition.id);
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Zap className="h-6 w-6" />
          Winner Selection
        </h2>
        <p className="text-gray-600 mt-1">
          Select winners for competitions using cryptographically secure
          randomization
        </p>
      </div>

      {/* Competition Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Select Competition</CardTitle>
          <CardDescription>
            Choose a competition to randomly select a winner from all entries
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4">
            <div>
              <Label htmlFor="competition">Competition</Label>
              <Select
                value={selectedCompetition?.id?.toString() || ""}
                onValueChange={(value) => {
                  const comp = competitions?.find(
                    (c: any) => c.id.toString() === value
                  );
                  setSelectedCompetition(comp);
                  setWinnerResult(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a competition" />
                </SelectTrigger>
                <SelectContent>
                  {competitions?.map((competition: any) => (
                    <SelectItem
                      key={competition.id}
                      value={competition.id.toString()}
                    >
                      {competition.title} ({competition.entryCount} entries)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500 mt-1">
                Note: Winner selection is automated when competitions end, but
                you can manually test the process here.
              </p>
            </div>

            {selectedCompetition && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">
                  {selectedCompetition.title}
                </h4>
                <p className="text-sm text-gray-600 mb-2">
                  {selectedCompetition.description}
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Total Entries:</span>{" "}
                    {selectedCompetition.entryCount}
                  </div>
                  <div>
                    <span className="font-medium">Prize:</span>{" "}
                    {selectedCompetition.prize}
                  </div>
                  <div>
                    <span className="font-medium">End Date:</span>{" "}
                    {new Date(selectedCompetition.endDate).toLocaleDateString()}
                  </div>
                  <div>
                    <span className="font-medium">Status:</span>
                    <Badge
                      variant={
                        selectedCompetition.status === "active"
                          ? "default"
                          : "secondary"
                      }
                      className="ml-1"
                    >
                      {selectedCompetition.status}
                    </Badge>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={handleSelectWinner}
              disabled={!selectedCompetition || isSelecting}
              className="w-full"
            >
              {isSelecting ? (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Selecting Winner...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Test Winner Selection
                </>
              )}
            </Button>
            <div className="text-xs text-gray-500 text-center">
              This is for testing purposes. Actual winner selection happens
              automatically when competitions end.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Winner Result */}
      {winnerResult && (
        <Card>
          <CardHeader>
            <CardTitle className="text-green-600">
              {winnerResult.alreadySelected
                ? "Previously Selected Winner"
                : "🎉 Winner Selected!"}
            </CardTitle>
            <CardDescription>
              {winnerResult.alreadySelected
                ? "This competition already has a selected winner"
                : "Winner selected using cryptographically secure randomization"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-3">Winner Information</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">Name:</span>{" "}
                    {winnerResult.winner.firstName}{" "}
                    {winnerResult.winner.lastName}
                  </div>
                  <div>
                    <span className="font-medium">Username:</span>{" "}
                    {winnerResult.winner.username}
                  </div>
                  <div>
                    <span className="font-medium">Email:</span>{" "}
                    {winnerResult.winner.email}
                  </div>
                  <div>
                    <span className="font-medium">Entry Type:</span>{" "}
                    {winnerResult.winner.entryType}
                  </div>
                  {winnerResult.winner.platform && (
                    <div>
                      <span className="font-medium">Platform:</span>{" "}
                      {winnerResult.winner.platform}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-3">Selection Details</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">Total Entries:</span>{" "}
                    {winnerResult.competition.totalEntries}
                  </div>
                  <div>
                    <span className="font-medium">Selected Index:</span>{" "}
                    {winnerResult.selectionDetails?.selectedIndex}
                  </div>
                  <div>
                    <span className="font-medium">Method:</span>{" "}
                    {winnerResult.selectionDetails?.selectionMethod}
                  </div>
                  <div>
                    <span className="font-medium">Random Bytes:</span>
                    <code className="ml-1 text-xs bg-gray-100 px-1 py-0.5 rounded">
                      {winnerResult.selectionDetails?.randomBytes ||
                        winnerResult.previousSelection?.randomBytes}
                    </code>
                  </div>
                  <div>
                    <span className="font-medium">Timestamp:</span>{" "}
                    {new Date(
                      winnerResult.selectionDetails?.timestamp ||
                        winnerResult.winner.selectionDate
                    ).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Winners */}
      {allWinners && allWinners.winners?.length > 0 && (
        <WinnerManagement
          winners={allWinners.winners}
          totalWinners={allWinners.totalWinners}
          onWinnerDeleted={refetchWinners}
        />
      )}

      {/* Email Communications Section */}
      <WinnerEmailCommunications />
    </div>
  );
}

function CompetitionCouponsTab() {
  const { toast } = useToast();
  const [selectedCompetitionId, setSelectedCompetitionId] = useState<string>("");
  const [newCode, setNewCode] = useState("");
  const [entriesPerRedemption, setEntriesPerRedemption] = useState<number>(1);

  // Fetch competitions for dropdown
  const { data: competitions, isLoading: competitionsLoading } = useQuery<Competition[]>({
    queryKey: ["/api/admin/competitions"],
  });
  const competitionsData = (competitions as any[]) || [];

  // Fetch coupons for selected competition
  const {
    data: couponResponse,
    isLoading: couponsLoading,
    refetch: refetchCoupons,
  } = useQuery<any>({
    queryKey: ["/api/admin/competitions", selectedCompetitionId, "coupons"],
    enabled: !!selectedCompetitionId,
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        `/api/admin/competitions/${selectedCompetitionId}/coupons`
      );
      return response.json();
    },
  });

  const coupons = couponResponse?.coupons || [];

  const [isUniversalCoupon, setIsUniversalCoupon] = useState(false);

  const createCouponMutation = useMutation({
    mutationFn: async () => {
      if (!selectedCompetitionId) {
        throw new Error("Select a competition first");
      }
      const payload: any = {
        entriesPerRedemption: entriesPerRedemption || 1,
        isUniversal: isUniversalCoupon,
      };
      if (newCode.trim()) {
        payload.code = newCode.trim();
      }
      const response = await apiRequest(
        "POST",
        `/api/admin/competitions/${selectedCompetitionId}/coupons`,
        { body: payload }
      );
      return response.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: "Coupon created",
        description: `Code ${data.coupon?.code || ""} created successfully.`,
      });
      setNewCode("");
      setEntriesPerRedemption(1);
      setIsUniversalCoupon(false);
      refetchCoupons();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create coupon",
        description: error?.message || "An error occurred while creating coupon",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Gift className="h-6 w-6" />
          Competition Coupons
        </h2>
        <p className="text-gray-600 mt-1">
          Create and manage coupon codes that grant bonus entries for specific competitions.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Select Competition</CardTitle>
          <CardDescription>
            Choose the competition you want to manage coupons for.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Competition</Label>
              <Select
                value={selectedCompetitionId}
                onValueChange={(value) => {
                  setSelectedCompetitionId(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a competition" />
                </SelectTrigger>
                <SelectContent>
                  {competitionsLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading competitions...
                    </SelectItem>
                  ) : (
                    competitionsData.map((competition: any) => (
                      <SelectItem
                        key={competition.id}
                        value={competition.id.toString()}
                      >
                        {competition.title}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedCompetitionId && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Create Coupon</CardTitle>
              <CardDescription>
                Generate a new coupon code for this competition.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <Label htmlFor="coupon-code">Coupon Code (optional)</Label>
                  <Input
                    id="coupon-code"
                    placeholder="e.g. WELCOME10"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave blank to auto-generate a random code.
                  </p>
                </div>
                <div>
                  <Label htmlFor="entries-per-redemption">
                    Entries per redemption
                  </Label>
                  <Input
                    id="entries-per-redemption"
                    type="number"
                    min={1}
                    max={100}
                    value={entriesPerRedemption}
                    onChange={(e) =>
                      setEntriesPerRedemption(
                        Math.max(1, Math.min(100, Number(e.target.value) || 1))
                      )
                    }
                  />
                </div>
                <div className="flex items-center gap-3 mt-6">
                  <Switch
                    checked={isUniversalCoupon}
                    onCheckedChange={setIsUniversalCoupon}
                    id="is-universal-coupon"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="is-universal-coupon">Universal coupon</Label>
                    <p className="text-xs text-gray-500">
                      Works on any competition, but each user can redeem it only once.
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <Button
                  onClick={() => createCouponMutation.mutate()}
                  disabled={createCouponMutation.isPending}
                >
                  {createCouponMutation.isPending ? "Creating..." : "Create Coupon"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Existing Coupons</CardTitle>
              <CardDescription>
                View all coupons for this competition.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {couponsLoading ? (
                <div className="text-center py-6 text-gray-500">
                  Loading coupons...
                </div>
              ) : coupons.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  No coupons created yet for this competition.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Entries</TableHead>
                        <TableHead>Total Redemptions</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Valid From</TableHead>
                        <TableHead>Valid Until</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {coupons.map((coupon: any) => (
                        <TableRow key={coupon.id}>
                          <TableCell className="font-mono text-sm">
                            {coupon.code}
                          </TableCell>
                          <TableCell>{coupon.entriesPerRedemption}</TableCell>
                          <TableCell>{coupon.totalRedemptions}</TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                coupon.status === "active"
                                  ? "default"
                                  : "secondary"
                              }
                              className="text-xs"
                            >
                              {coupon.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {coupon.validFrom
                              ? new Date(coupon.validFrom).toLocaleDateString()
                              : "—"}
                          </TableCell>
                          <TableCell>
                            {coupon.validUntil
                              ? new Date(
                                  coupon.validUntil
                                ).toLocaleDateString()
                              : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// Winner Management Component
function WinnerManagement({
  winners,
  totalWinners,
  onWinnerDeleted,
}: {
  winners: any[];
  totalWinners: number;
  onWinnerDeleted: () => void;
}) {
  const { toast } = useToast();
  const [selectedWinner, setSelectedWinner] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);
  const [editingNotes, setEditingNotes] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  // Update prize claim mutation
  const updatePrizeClaimMutation = useMutation({
    mutationFn: async ({
      winnerId,
      prizeClaimed,
    }: {
      winnerId: number;
      prizeClaimed: boolean;
    }) => {
      const response = await apiRequest(
        "PUT",
        `/api/admin/winners/${winnerId}/claim`,
        { body: { prizeClaimed } }
      );
      return response.json();
    },
    onSuccess: () => {
      onWinnerDeleted(); // Refresh data
      toast({
        title: "Success",
        description: "Prize claim status updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to update prize claim status",
        variant: "destructive",
      });
    },
  });

  // Update notes mutation
  const updateNotesMutation = useMutation({
    mutationFn: async ({
      winnerId,
      notes,
    }: {
      winnerId: number;
      notes: string;
    }) => {
      const response = await apiRequest(
        "PUT",
        `/api/admin/winners/${winnerId}/notes`,
        { body: { notes } }
      );
      return response.json();
    },
    onSuccess: () => {
      setIsNotesDialogOpen(false);
      onWinnerDeleted(); // Refresh data
      toast({
        title: "Success",
        description: "Winner notes updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to update winner notes",
        variant: "destructive",
      });
    },
  });

  const handlePrizeClaimToggle = (winnerId: number, currentStatus: boolean) => {
    updatePrizeClaimMutation.mutate({ winnerId, prizeClaimed: !currentStatus });
  };

  const handleNotesEdit = (winner: any) => {
    setSelectedWinner(winner);
    setEditingNotes(winner.adminNotes || "");
    setIsNotesDialogOpen(true);
  };

  const handleNotesSubmit = () => {
    if (selectedWinner) {
      updateNotesMutation.mutate({
        winnerId: selectedWinner.id,
        notes: editingNotes.trim(),
      });
    }
  };

  // Delete winner mutation
  const deleteWinnerMutation = useMutation({
    mutationFn: async (winnerId: number) => {
      const response = await apiRequest(
        "DELETE",
        `/api/admin/winners/${winnerId}`
      );
      return response.json();
    },
    onSuccess: () => {
      setIsDetailsOpen(false);
      onWinnerDeleted(); // Refresh data
      toast({
        title: "Success",
        description: "Winner deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to delete winner",
        variant: "destructive",
      });
    },
  });

  const handleDeleteWinner = (winnerId: number) => {
    deleteWinnerMutation.mutate(winnerId);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await fetch("/api/admin/winners/export", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Export failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `winners-export-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Success",
        description: "Winners data exported successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export winners data",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getBetfredStatusBadge = (status: string) => {
    switch (status) {
      case "verified":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-200">
            ✓ Verified
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200">
            ⏳ Pending
          </Badge>
        );
      case "invalid":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-200">
            ✗ Invalid
          </Badge>
        );
      case "failed":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-200">
            ⚠ Failed
          </Badge>
        );
      default:
        return <Badge variant="outline">Not Registered</Badge>;
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Previous Winners ({totalWinners})</CardTitle>
            <CardDescription>
              Complete history of all selected winners with prize claim tracking
            </CardDescription>
          </div>
          <Button
            onClick={handleExport}
            disabled={isExporting}
            variant="outline"
            size="sm"
            data-testid="export-winners-button"
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2"></div>
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </>
            )}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Competition</TableHead>
                  <TableHead>Winner</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Betfred Status</TableHead>
                  <TableHead>Prize Claimed</TableHead>
                  <TableHead>Total Entries</TableHead>
                  <TableHead>Date Selected</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {winners.map((winner: any) => (
                  <TableRow key={winner.id}>
                    <TableCell>
                      <div className="font-medium">
                        {winner.competitionTitle || `#${winner.competitionId}`}
                      </div>
                      <div className="text-sm text-gray-500">
                        {winner.prize}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {winner.firstName} {winner.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          @{winner.username}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{winner.email}</TableCell>
                    <TableCell>
                      {getBetfredStatusBadge(winner.betfredStatus)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handlePrizeClaimToggle(
                              winner.id,
                              winner.prizeClaimed
                            )
                          }
                          disabled={updatePrizeClaimMutation.isPending}
                          className={
                            winner.prizeClaimed
                              ? "text-green-600"
                              : "text-gray-600"
                          }
                          data-testid={`prize-claim-toggle-${winner.id}`}
                        >
                          {winner.prizeClaimed ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </Button>
                        <span className="text-sm">
                          {winner.prizeClaimed ? "Claimed" : "Unclaimed"}
                        </span>
                        {winner.prizeClaimed && winner.claimedDate && (
                          <div className="text-xs text-gray-500">
                            {new Date(winner.claimedDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{winner.totalEntries}</TableCell>
                    <TableCell>
                      {new Date(winner.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedWinner(winner);
                            setIsDetailsOpen(true);
                          }}
                          data-testid={`view-winner-${winner.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleNotesEdit(winner)}
                          data-testid={`edit-notes-${winner.id}`}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                              data-testid={`delete-winner-${winner.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Winner</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this winner?
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteWinner(winner.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Winner Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Winner Details</DialogTitle>
            <DialogDescription>
              Complete information about the selected winner
            </DialogDescription>
          </DialogHeader>

          {selectedWinner && (
            <div className="space-y-6">
              {/* Personal Information */}
              <div>
                <h4 className="text-lg font-semibold mb-3">
                  Personal Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Full Name</Label>
                    <p className="text-sm">
                      {selectedWinner.user?.first_name ||
                        selectedWinner.firstName ||
                        ""}{" "}
                      {selectedWinner.user?.last_name ||
                        selectedWinner.lastName ||
                        ""}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Username</Label>
                    <p className="text-sm">
                      @
                      {selectedWinner.user?.username ||
                        selectedWinner.username ||
                        "N/A"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Email</Label>
                    <p className="text-sm">
                      {selectedWinner.user?.email ||
                        selectedWinner.email ||
                        "N/A"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Phone</Label>
                    <p className="text-sm">
                      {selectedWinner.user?.phone ||
                        selectedWinner.phone ||
                        "Not provided"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Date of Birth</Label>
                    <p className="text-sm">
                      {selectedWinner.dateOfBirth
                        ? formatSADate(selectedWinner.dateOfBirth)
                        : "Not provided"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Gender</Label>
                    <p className="text-sm">
                      {selectedWinner.gender || "Not provided"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Competition Details */}
              <div>
                <h4 className="text-lg font-semibold mb-3">
                  Competition Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">
                      Competition ID
                    </Label>
                    <p className="text-sm">#{selectedWinner.competitionId}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Total Entries</Label>
                    <p className="text-sm">{selectedWinner.totalEntries}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">
                      Selected Index
                    </Label>
                    <p className="text-sm">{selectedWinner.selectedIndex}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Entry Type</Label>
                    <p className="text-sm">
                      {selectedWinner.entryType || "Standard"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Method</Label>
                    <p className="text-sm">
                      {selectedWinner.selectionMethod || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Selection Information */}
              <div>
                <h4 className="text-lg font-semibold mb-3">
                  Selection Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">
                      Selection Date
                    </Label>
                    <p className="text-sm">
                      {new Date(selectedWinner.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Random Bytes</Label>
                    <code className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                      {selectedWinner.randomBytes || "N/A"}
                    </code>
                  </div>
                  {selectedWinner.platform && (
                    <div>
                      <Label className="text-sm font-medium">Platform</Label>
                      <p className="text-sm">{selectedWinner.platform}</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-sm font-medium">
                      Verification Status
                    </Label>
                    <Badge variant="outline" className="text-green-600">
                      Verified Winner
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Timestamp</Label>
                    <p className="text-sm">
                      {new Date(selectedWinner.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Prize Claim Status */}
              <div>
                <h4 className="text-lg font-semibold mb-3">
                  Prize Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Prize</Label>
                    <p className="text-sm">
                      {selectedWinner.competition?.prize || "R300 in Prizes"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Claim Status</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge
                        variant={
                          selectedWinner.prizeClaimed ? "default" : "secondary"
                        }
                      >
                        {selectedWinner.prizeClaimed ? "Claimed" : "Unclaimed"}
                      </Badge>
                      {selectedWinner.claimedDate && (
                        <span className="text-xs text-gray-500">
                          on{" "}
                          {new Date(
                            selectedWinner.claimedDate
                          ).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">
                      Betfred Status
                    </Label>
                    <div className="mt-1">
                      {getBetfredStatusBadge(selectedWinner.betfredStatus)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Delete Winner Section */}
              <div className="border-t pt-4">
                <h4 className="text-lg font-semibold mb-3 text-red-600">
                  Danger Zone
                </h4>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Winner
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Winner</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this winner? This action
                        cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDeleteWinner(selectedWinner.id)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Delete Winner
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {/* Admin Notes */}
              {selectedWinner.adminNotes && (
                <div>
                  <h4 className="text-lg font-semibold mb-3">Admin Notes</h4>
                  <div className="p-3 bg-gray-50 rounded-md">
                    <p className="text-sm whitespace-pre-wrap">
                      {selectedWinner.adminNotes}
                    </p>
                  </div>
                </div>
              )}

              {/* Additional Information */}
              {(selectedWinner.location || selectedWinner.phone) && (
                <div>
                  <h4 className="text-lg font-semibold mb-3">
                    Contact Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedWinner.phone && (
                      <div>
                        <Label className="text-sm font-medium">Phone</Label>
                        <p className="text-sm">{selectedWinner.phone}</p>
                      </div>
                    )}
                    {selectedWinner.location && (
                      <div>
                        <Label className="text-sm font-medium">Location</Label>
                        <p className="text-sm">{selectedWinner.location}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={isNotesDialogOpen} onOpenChange={setIsNotesDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Winner Notes</DialogTitle>
            <DialogDescription>
              Add or update notes for {selectedWinner?.firstName}{" "}
              {selectedWinner?.lastName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="notes">Administrative Notes</Label>
              <Textarea
                id="notes"
                value={editingNotes}
                onChange={(e) => setEditingNotes(e.target.value)}
                placeholder="Add any notes about this winner (e.g. contact status, prize delivery, etc.)"
                rows={5}
                data-testid="winner-notes-textarea"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsNotesDialogOpen(false)}
                data-testid="cancel-notes-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleNotesSubmit}
                disabled={updateNotesMutation.isPending}
                data-testid="save-notes-button"
              >
                {updateNotesMutation.isPending ? "Saving..." : "Save Notes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Challenge Management Tab Component
function ChallengeManagementTab() {
  const { toast } = useToast();
  const [selectedChallenge, setSelectedChallenge] = useState<any>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAnalyticsDialogOpen, setIsAnalyticsDialogOpen] = useState(false);
  const [isEnrolledUsersDialogOpen, setIsEnrolledUsersDialogOpen] =
    useState(false);

  // Fetch challenges data
  const {
    data: challenges = [],
    isLoading: challengesLoading,
    refetch: refetchChallenges,
  } = useQuery<any[]>({
    queryKey: ["/api/admin/challenges"],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  // Create challenge mutation
  const createChallengeMutation = useMutation({
    mutationFn: async (challengeData: any) => {
      const response = await apiRequest("POST", "/api/admin/challenges", {
        body: challengeData,
      });
      return response.json();
    },
    onSuccess: () => {
      refetchChallenges();
      setIsCreateDialogOpen(false);
      toast({
        title: "Challenge Created",
        description: "New challenge has been created successfully!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to create challenge",
        variant: "destructive",
      });
    },
  });

  // Update challenge mutation
  const updateChallengeMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await apiRequest("PUT", `/api/admin/challenges/${id}`, {
        body: data,
      });
      return response.json();
    },
    onSuccess: () => {
      refetchChallenges();
      setIsEditDialogOpen(false);
      toast({
        title: "Challenge Updated",
        description: "Challenge has been updated successfully!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to update challenge",
        variant: "destructive",
      });
    },
  });

  // Delete challenge mutation
  const deleteChallengeMutation = useMutation({
    mutationFn: async (challengeId: number) => {
      const response = await apiRequest(
        "DELETE",
        `/api/admin/challenges/${challengeId}`
      );
      // Handle both JSON response and empty 204 response
      if (response.status === 204) {
        return { success: true, message: "Challenge deleted successfully" };
      }
      return response.json();
    },
    onSuccess: () => {
      refetchChallenges();
      toast({
        title: "Challenge Deleted",
        description: "Challenge has been deleted successfully!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to delete challenge",
        variant: "destructive",
      });
    },
  });

  // Duplicate challenge mutation
  const duplicateChallengeMutation = useMutation({
    mutationFn: async (challengeId: number) => {
      const response = await apiRequest(
        "POST",
        `/api/admin/challenges/${challengeId}/duplicate`
      );
      return response.json();
    },
    onSuccess: () => {
      refetchChallenges();
      toast({
        title: "Challenge Duplicated",
        description: "Challenge has been duplicated successfully!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to duplicate challenge",
        variant: "destructive",
      });
    },
  });

  // Toggle challenge visibility mutation
  const toggleVisibilityMutation = useMutation({
    mutationFn: async ({
      challengeId,
      isVisible,
    }: {
      challengeId: number;
      isVisible: boolean;
    }) => {
      const response = await apiRequest(
        "PATCH",
        `/api/admin/challenges/${challengeId}/visibility`,
        {
          body: { isVisible },
        }
      );
      return response.json();
    },
    onSuccess: () => {
      refetchChallenges();
      toast({
        title: "Visibility Updated",
        description: "Challenge visibility has been updated successfully!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to update challenge visibility",
        variant: "destructive",
      });
    },
  });

  // Get challenge statistics
  const { data: challengeStats } = useQuery<{
    participantCount?: number;
    totalReferrals?: number;
    topReferrers?: any[];
  }>({
    queryKey: ["/api/admin/challenges", selectedChallenge?.id, "stats"],
    enabled: !!selectedChallenge?.id,
  });

  // Get enrolled users
  const { data: enrolledUsersData, isLoading: enrolledUsersLoading } =
    useQuery<{ enrolledUsers: any[]; total: number }>({
      queryKey: [
        "/api/admin/challenges",
        selectedChallenge?.id,
        "enrolled-users",
      ],
      enabled: !!selectedChallenge?.id && isEnrolledUsersDialogOpen,
    });

  const handleCreateChallenge = (formData: FormData) => {
    const startDate = formData.get("startDate") as string;
    const startTime = formData.get("startTime") as string;
    const startDatetime =
      startDate && startTime ? `${startDate}T${startTime}:00` : startDate;

    const challengeData = {
      title: formData.get("title"),
      description: formData.get("description"),
      type: formData.get("type") || "acquisition", // Default to acquisition if not provided
      prize1: formData.get("prize1"),
      prize2: formData.get("prize2"),
      prize3: formData.get("prize3"),
      targetReferrals: parseInt(formData.get("targetReferrals") as string),
      startDate: startDatetime,
      status: formData.get("status") || "draft",
      isActive: true,
    };
    console.log("Challenge data being sent:", challengeData);
    createChallengeMutation.mutate(challengeData);
  };

  const handleUpdateChallenge = (formData: FormData) => {
    if (!selectedChallenge) return;

    const startDate = formData.get("startDate") as string;
    const startTime = formData.get("startTime") as string;
    const startDatetime =
      startDate && startTime ? `${startDate}T${startTime}:00` : startDate;

    const challengeData = {
      title: formData.get("title"),
      description: formData.get("description"),
      prize1: formData.get("prize1"),
      prize2: formData.get("prize2"),
      prize3: formData.get("prize3"),
      leftImageUrl: formData.get("leftImageUrl"),
      rightImageUrl: formData.get("rightImageUrl"),
      reviewMilestones: formData.get("reviewMilestones"),
      customRules: formData.get("customRules"),
      targetReferrals: parseInt(formData.get("targetReferrals") as string),
      startDate: startDatetime,
      status: formData.get("status"),
      isActive: formData.get("isActive") === "true",
    };
    updateChallengeMutation.mutate({
      id: selectedChallenge.id,
      data: challengeData,
    });
  };

  const handleDeleteChallenge = (challengeId: number) => {
    if (
      confirm(
        "Are you sure you want to delete this challenge? This action cannot be undone."
      )
    ) {
      deleteChallengeMutation.mutate(challengeId);
    }
  };

  const handleDuplicateChallenge = (challengeId: number) => {
    duplicateChallengeMutation.mutate(challengeId);
  };

  const handleToggleVisibility = (challenge: any) => {
    toggleVisibilityMutation.mutate({
      challengeId: challenge.id,
      isVisible: !challenge.isVisible,
    });
  };

  const handleViewAnalytics = (challenge: any) => {
    setSelectedChallenge(challenge);
    setIsAnalyticsDialogOpen(true);
  };

  const handleViewEnrolledUsers = (challenge: any) => {
    setSelectedChallenge(challenge);
    setIsEnrolledUsersDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Target className="h-6 w-6" />
          Challenges
        </h2>
        <p className="text-gray-600 mt-1">
          Manage referral-based challenges and track performance
        </p>
      </div>

      {/* Challenge Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {(() => {
          // Treat only explicit "live" status as active for high-level stats.
          const activeChallenges = challenges.filter(
            (c: any) => c.status === "live"
          );
          const completedChallenges = challenges.filter(
            (c: any) => c.status === "ended"
          );
        return (
          <>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Live Challenges
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {activeChallenges.length}
            </div>
            <p className="text-xs text-gray-500">Live challenges</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Total Participants
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {challenges.reduce(
                (sum: number, c: any) => sum + (c.participantCount || 0),
                0
              )}
            </div>
            <p className="text-xs text-gray-500">Users participating</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Total Referrals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {challenges.reduce(
                (sum: number, c: any) => sum + (c.totalReferrals || 0),
                0
              )}
            </div>
            <p className="text-xs text-gray-500">Referrals made</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Completion Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {challenges.length > 0
                ? Math.round(
                    (completedChallenges.length / challenges.length) * 100
                  )
                : 0}
              %
            </div>
            <p className="text-xs text-gray-500">Challenges completed</p>
          </CardContent>
        </Card>
          </>
        );
        })()}
      </div>

      {/* Challenges Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Live Challenges</CardTitle>
              <CardDescription>
                Manage your referral-based challenges
              </CardDescription>
            </div>
            <Dialog
              open={isCreateDialogOpen}
              onOpenChange={setIsCreateDialogOpen}
            >
              <DialogTrigger asChild>
                <Button
                  className="primary-gradient"
                  data-testid="create-challenge-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Challenge
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Challenge</DialogTitle>
                  <DialogDescription>
                    Set up a new referral-based challenge
                  </DialogDescription>
                </DialogHeader>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleCreateChallenge(new FormData(e.currentTarget));
                  }}
                  className="space-y-4"
                  data-testid="create-challenge-form"
                >
                  <div>
                    <Label htmlFor="title">Challenge Title</Label>
                    <Input
                      name="title"
                      required
                      placeholder="e.g., Win an iPhone 16"
                      data-testid="input-challenge-title"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      name="description"
                      required
                      placeholder="Describe the challenge and how to participate"
                      data-testid="input-challenge-description"
                    />
                  </div>
                  <div>
                    <Label htmlFor="type">Challenge Type</Label>
                    <select
                      name="type"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      defaultValue="acquisition"
                      data-testid="select-challenge-type"
                    >
                      <option value="acquisition">Acquisition</option>
                      <option value="participation">Participation</option>
                      <option value="growth">Growth</option>
                      <option value="marketing">Marketing</option>
                    </select>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="prize1">First Prize</Label>
                      <Input
                        name="prize1"
                        required
                        placeholder="e.g., iPhone 16 Pro Max"
                        data-testid="input-prize1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="prize2">Second Prize (Optional)</Label>
                      <Input
                        name="prize2"
                        placeholder="e.g., AirPods Pro"
                        data-testid="input-prize2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="prize3">Third Prize (Optional)</Label>
                      <Input
                        name="prize3"
                        placeholder="e.g., Apple Watch"
                        data-testid="input-prize3"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="targetReferrals">
                      Target Referrals (Challenge ends when reached)
                    </Label>
                    <Input
                      name="targetReferrals"
                      type="number"
                      required
                      placeholder="1000"
                      defaultValue="1000"
                      data-testid="input-target-referrals"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="startDate">Start Date</Label>
                      <Input
                        name="startDate"
                        type="date"
                        required
                        defaultValue={new Date().toISOString().split("T")[0]}
                        data-testid="input-start-date"
                      />
                    </div>
                    <div>
                      <Label htmlFor="startTime">Start Time</Label>
                      <Input
                        name="startTime"
                        type="time"
                        placeholder="HH:MM"
                        data-testid="input-start-time"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <select
                      name="status"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      defaultValue="draft"
                      data-testid="select-challenge-status"
                    >
                      <option value="draft">Draft</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="live">Live</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Draft: Not visible to users. Scheduled: Will go live at
                      start time. Live: Active now.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsCreateDialogOpen(false)}
                      data-testid="button-cancel-challenge"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={createChallengeMutation.isPending}
                      data-testid="button-submit-challenge"
                    >
                      {createChallengeMutation.isPending
                        ? "Creating..."
                        : "Create Challenge"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {challengesLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">Loading challenges...</div>
            </div>
          ) : challenges.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Target className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">No Challenges Yet</p>
              <p className="text-sm">
                Create your first referral challenge to get started.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Challenge</TableHead>
                  <TableHead>Prize</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Participants</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.isArray(challenges)
                  ? challenges.map((challenge: any) => (
                      <TableRow key={challenge.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{challenge.name}</div>
                            <div className="text-sm text-gray-500">
                              {challenge.description}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{challenge.prize1}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {challenge.targetValue} referrals
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {challenge.participantCount || 0} users
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    challenge.targetValue > 0
                                      ? ((challenge.topPerformerReferrals ||
                                          0) /
                                          challenge.targetValue) *
                                          100
                                      : 0
                                  )}%`,
                                }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-600">
                              {Math.min(
                                100,
                                Math.round(
                                  challenge.targetValue > 0
                                    ? ((challenge.topPerformerReferrals || 0) /
                                        challenge.targetValue) *
                                        100
                                    : 0
                                )
                              )}
                              %
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const now = new Date();
                            const hasEnded =
                              (challenge.endDatetime &&
                                new Date(challenge.endDatetime).getTime() <
                                  now.getTime()) ||
                              challenge.status === "ended";
                            const isLive =
                              !hasEnded &&
                              (challenge.status === "live" || challenge.isActive);
                            const label = hasEnded
                              ? "Ended"
                              : isLive
                              ? "Active"
                              : "Inactive";

                            const className =
                              label === "Ended"
                                ? "bg-orange-100 text-orange-800"
                                : label === "Active"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800";

                            return <Badge className={className}>{label}</Badge>;
                          })()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                window.open(
                                  `/challenges/${challenge.id}`,
                                  "_blank"
                                )
                              }
                              title="View Challenge Page"
                              className="text-purple-600 hover:text-purple-700"
                              data-testid={`button-view-page-${challenge.id}`}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleVisibility(challenge)}
                              disabled={toggleVisibilityMutation.isPending}
                              title={
                                challenge.isVisible
                                  ? "Hide Challenge"
                                  : "Show Challenge"
                              }
                              className={
                                challenge.isVisible
                                  ? "text-green-600 hover:text-green-700"
                                  : "text-gray-600 hover:text-gray-700"
                              }
                              data-testid={`button-toggle-visibility-${challenge.id}`}
                            >
                              {challenge.isVisible ? (
                                <Eye className="h-4 w-4" />
                              ) : (
                                <EyeOff className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewAnalytics(challenge)}
                              title="View Analytics"
                            >
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewEnrolledUsers(challenge)}
                              title="View Enrolled Users"
                              className="text-indigo-600 hover:text-indigo-700"
                              data-testid={`button-view-enrolled-${challenge.id}`}
                            >
                              <Users className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedChallenge(challenge);
                                setIsEditDialogOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleDuplicateChallenge(challenge.id)
                              }
                              disabled={duplicateChallengeMutation.isPending}
                              title="Duplicate Challenge"
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleDeleteChallenge(challenge.id)
                              }
                              disabled={deleteChallengeMutation.isPending}
                              title="Delete Challenge"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  : []}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Challenge Management Tools */}
      <Card>
        <CardHeader>
          <CardTitle>Challenge Management Tools</CardTitle>
          <CardDescription>
            Administrative tools for managing challenges
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2"
              onClick={() => setIsCreateDialogOpen(true)}
            >
              <Plus className="h-6 w-6" />
              <span className="text-sm">Create New Challenge</span>
            </Button>
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="h-20 flex flex-col items-center justify-center gap-2"
                >
                  <Settings className="h-6 w-6" />
                  <span className="text-sm">Challenge Settings</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Challenge System Settings</DialogTitle>
                  <DialogDescription>
                    Configure global challenge system settings
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Default Challenge Duration</Label>
                      <Select defaultValue="30">
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7">7 days</SelectItem>
                          <SelectItem value="14">14 days</SelectItem>
                          <SelectItem value="30">30 days</SelectItem>
                          <SelectItem value="60">60 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Auto-activate Challenges</Label>
                      <Select defaultValue="true">
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Enabled</SelectItem>
                          <SelectItem value="false">Disabled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Challenge Notification Settings</Label>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          defaultChecked
                          className="rounded"
                        />
                        <Label className="text-sm">
                          Email notifications for new challenges
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          defaultChecked
                          className="rounded"
                        />
                        <Label className="text-sm">
                          Push notifications for challenge updates
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input type="checkbox" className="rounded" />
                        <Label className="text-sm">
                          SMS notifications for winners
                        </Label>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Referral Settings</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Bonus per referral</Label>
                        <Input
                          type="number"
                          defaultValue="35"
                          min="1"
                          max="100"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">
                          Max referrals per user
                        </Label>
                        <Input type="number" defaultValue="1000" min="1" />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline">Cancel</Button>
                    <Button
                      onClick={() => {
                        toast({
                          title: "Settings Updated",
                          description:
                            "Challenge system settings have been saved successfully!",
                        });
                      }}
                    >
                      Save Settings
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2"
              onClick={async () => {
                try {
                  // Export challenges data as CSV
                  const csvData = [
                    [
                      "ID",
                      "Name",
                      "Type",
                      "Description",
                      "Target",
                      "Participants",
                      "Referrals",
                      "Status",
                      "Start Date",
                      "End Date",
                    ].join(","),
                    ...(Array.isArray(challenges)
                      ? challenges.map((challenge: any) =>
                          [
                            challenge.id,
                            `"${
                              challenge.name || challenge.title || "Untitled"
                            }"`,
                            challenge.type,
                            `"${(challenge.description || "").replace(
                              /"/g,
                              '""'
                            )}"`,
                            challenge.targetValue || challenge.targetReferrals,
                            challenge.participantCount || 0,
                            challenge.totalReferrals || 0,
                            challenge.isActive ? "Active" : "Inactive",
                            challenge.startDatetime ||
                              challenge.startDate ||
                              "",
                            challenge.endDatetime || challenge.endDate || "",
                          ].join(",")
                        )
                      : []),
                  ].join("\n");

                  const blob = new Blob([csvData], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `challenges-export-${
                    new Date().toISOString().split("T")[0]
                  }.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);

                  toast({
                    title: "Export Complete",
                    description:
                      "Challenge data has been exported successfully!",
                  });
                } catch (error) {
                  toast({
                    title: "Export Failed",
                    description: "Failed to export challenge data",
                    variant: "destructive",
                  });
                }
              }}
            >
              <Download className="h-6 w-6" />
              <span className="text-sm">Export Data</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Edit Challenge Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Challenge</DialogTitle>
            <DialogDescription>
              Update challenge details and settings
            </DialogDescription>
          </DialogHeader>
          {selectedChallenge && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleUpdateChallenge(new FormData(e.currentTarget));
              }}
              className="space-y-4"
            >
              <div>
                <Label htmlFor="title">Challenge Title</Label>
                <Input
                  name="title"
                  required
                  defaultValue={selectedChallenge.name}
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  name="description"
                  required
                  defaultValue={selectedChallenge.description}
                />
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="prize1">First Prize</Label>
                  <Input
                    name="prize1"
                    required
                    defaultValue={
                      selectedChallenge.prize1 || selectedChallenge.prize || ""
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="prize2">Second Prize (Optional)</Label>
                  <Input
                    name="prize2"
                    defaultValue={selectedChallenge.prize2 || ""}
                  />
                </div>
                <div>
                  <Label htmlFor="prize3">Third Prize (Optional)</Label>
                  <Input
                    name="prize3"
                    defaultValue={selectedChallenge.prize3 || ""}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="leftImageUrl">
                  Left Hero Image URL (Optional)
                </Label>
                <Input
                  name="leftImageUrl"
                  type="url"
                  placeholder="https://example.com/image-left.png"
                  defaultValue={selectedChallenge.leftImageUrl || ""}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Image displayed on the left side of the challenge page
                  (desktop only)
                </p>
              </div>
              <div>
                <Label htmlFor="rightImageUrl">
                  Right Hero Image URL (Optional)
                </Label>
                <Input
                  name="rightImageUrl"
                  type="url"
                  placeholder="https://example.com/image-right.png"
                  defaultValue={selectedChallenge.rightImageUrl || ""}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Image displayed on the right side of the challenge page
                  (desktop only)
                </p>
              </div>
              <div>
                <Label htmlFor="reviewMilestones">
                  Review Milestones (Optional)
                </Label>
                <Input
                  name="reviewMilestones"
                  placeholder="50,100,250,500,750"
                  defaultValue={selectedChallenge.reviewMilestones || ""}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Comma-separated numbers for manual review checkpoints
                </p>
              </div>
              <div>
                <Label htmlFor="customRules">Custom Rules (Optional)</Label>
                <Textarea
                  name="customRules"
                  rows={8}
                  placeholder="Enter custom challenge rules (use line breaks for formatting)..."
                  defaultValue={selectedChallenge.customRules || ""}
                />
                <p className="text-xs text-gray-500 mt-1">
                  If provided, these rules will replace the default template.
                  Leave empty to use auto-generated rules.
                </p>
              </div>
              <div>
                <Label htmlFor="targetReferrals">
                  Target Referrals (Challenge ends when reached)
                </Label>
                <Input
                  name="targetReferrals"
                  type="number"
                  required
                  defaultValue={selectedChallenge.targetValue}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    name="startDate"
                    type="date"
                    required
                    defaultValue={
                      selectedChallenge.startDatetime
                        ? new Date(selectedChallenge.startDatetime)
                            .toISOString()
                            .split("T")[0]
                        : ""
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="startTime">Start Time</Label>
                  <Input
                    name="startTime"
                    type="time"
                    placeholder="HH:MM"
                    defaultValue={
                      selectedChallenge.startDatetime
                        ? new Date(selectedChallenge.startDatetime)
                            .toTimeString()
                            .slice(0, 5)
                        : ""
                    }
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select name="status" defaultValue={selectedChallenge.status}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="live">Live</SelectItem>
                    <SelectItem value="ended">Ended</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Draft: Not visible to users. Scheduled: Will go live at start
                  time. Live: Active now.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="isActive"
                  value="true"
                  className="rounded"
                  defaultChecked={selectedChallenge.isActive}
                />
                <Label htmlFor="isActive">Challenge Active</Label>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateChallengeMutation.isPending}
                >
                  {updateChallengeMutation.isPending
                    ? "Updating..."
                    : "Update Challenge"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog
        open={isAnalyticsDialogOpen}
        onOpenChange={setIsAnalyticsDialogOpen}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Challenge Analytics: {selectedChallenge?.name}
            </DialogTitle>
            <DialogDescription>
              Detailed performance metrics and participant data
            </DialogDescription>
          </DialogHeader>
          {selectedChallenge && challengeStats && (
            <div className="space-y-6">
              {/* Stats Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {challengeStats.participantCount || 0}
                  </div>
                  <div className="text-sm text-blue-800">Participants</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {challengeStats.totalReferrals || 0}
                  </div>
                  <div className="text-sm text-green-800">Total Referrals</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {challengeStats.topReferrers &&
                    challengeStats.topReferrers.length > 0 &&
                    selectedChallenge.targetValue
                      ? Math.min(
                          100,
                          Math.round(
                            (challengeStats.topReferrers[0].referralCount /
                              selectedChallenge.targetValue) *
                              100
                          )
                        )
                      : 0}
                    %
                  </div>
                  <div className="text-sm text-purple-800">Progress</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {challengeStats.participantCount > 0
                      ? Math.round(
                          (challengeStats.totalReferrals || 0) /
                            challengeStats.participantCount
                        )
                      : 0}
                  </div>
                  <div className="text-sm text-orange-800">Avg per User</div>
                </div>
              </div>

              {/* Top Referrers */}
              {challengeStats.topReferrers &&
                challengeStats.topReferrers.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Top Referrers</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>User</TableHead>
                            <TableHead>Referrals</TableHead>
                            <TableHead>Progress</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {challengeStats.topReferrers.map(
                            (user: any, index: number) => (
                              <TableRow key={user.userId}>
                                <TableCell>
                                  <div>
                                    <div className="font-medium">
                                      {user.username}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {user.email}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="secondary">
                                    {user.referralCount}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                                      <div
                                        className="bg-green-600 h-2 rounded-full"
                                        style={{
                                          width: `${Math.min(
                                            100,
                                            (user.referralCount /
                                              selectedChallenge.targetValue) *
                                              100
                                          )}%`,
                                        }}
                                      ></div>
                                    </div>
                                    <span className="text-sm text-gray-600">
                                      {Math.min(
                                        100,
                                        Math.round(
                                          (user.referralCount /
                                            selectedChallenge.targetValue) *
                                            100
                                        )
                                      )}
                                      %
                                    </span>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Enrolled Users Dialog */}
      <Dialog
        open={isEnrolledUsersDialogOpen}
        onOpenChange={setIsEnrolledUsersDialogOpen}
      >
        <DialogContent className="max-w-[90vw] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enrolled Users: {selectedChallenge?.name}</DialogTitle>
            <DialogDescription>
              {enrolledUsersData?.total || 0} users enrolled in this challenge
            </DialogDescription>
          </DialogHeader>
          {enrolledUsersLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">Loading enrolled users...</div>
            </div>
          ) : enrolledUsersData?.enrolledUsers &&
            enrolledUsersData.enrolledUsers.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Referred By</TableHead>
                    <TableHead>Joined</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enrolledUsersData.enrolledUsers.map((user: any) => (
                    <TableRow key={user.userId}>
                      <TableCell>
                        {user.firstName || user.lastName
                          ? `${user.firstName || ""} ${
                              user.lastName || ""
                            }`.trim()
                          : "-"}
                      </TableCell>
                      <TableCell>{user.email || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {user.progressCount || 0} referrals
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Priority 1: Challenge-specific referrer
                          if (
                            user.challengeReferrerFirstName ||
                            user.challengeReferrerLastName
                          ) {
                            return `${user.challengeReferrerFirstName || ""} ${
                              user.challengeReferrerLastName || ""
                            }`.trim();
                          }
                          // Priority 2: General referrer
                          if (
                            user.generalReferrerFirstName ||
                            user.generalReferrerLastName
                          ) {
                            return `${user.generalReferrerFirstName || ""} ${
                              user.generalReferrerLastName || ""
                            }`.trim();
                          }
                          // No referrer found
                          return "N/A";
                        })()}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {user.joinedAt
                          ? new Date(user.joinedAt).toLocaleDateString()
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">No Users Enrolled</p>
              <p className="text-sm">
                This challenge doesn't have any enrolled users yet.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Integration Status */}
      <Card>
        <CardHeader>
          <CardTitle>Challenge System Status</CardTitle>
          <CardDescription>
            Current status of the challenge system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <div className="font-medium text-green-900">
                  Challenge System Active
                </div>
                <div className="text-sm text-green-700">
                  Challenge functionality is integrated and ready for use
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="h-5 w-5 text-blue-600" />
              <div>
                <div className="font-medium text-blue-900">Database Ready</div>
                <div className="text-sm text-blue-700">
                  Challenge tables and relationships are properly configured
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <Target className="h-5 w-5 text-purple-600" />
              <div>
                <div className="font-medium text-purple-900">
                  User Interface Ready
                </div>
                <div className="text-sm text-purple-700">
                  Challenge page and components are available at{" "}
                  <code>/challenges</code>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Winner Email Communications Component
function WinnerEmailCommunications() {
  const { toast } = useToast();
  const [selectedCommunication, setSelectedCommunication] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSubject, setEditedSubject] = useState("");
  const [editedBody, setEditedBody] = useState("");

  // Get communications data with real-time updates
  const { data: communications = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/communications"],
    refetchInterval: 30000, // Refetch every 30 seconds
    refetchOnWindowFocus: true,
  });

  // Mark as sent mutation
  const markSentMutation = useMutation({
    mutationFn: (id: number) =>
      apiRequest("POST", `/api/admin/communications/${id}/mark-sent`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/communications"],
      });
      toast({ title: "Success", description: "Email marked as sent" });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to mark email as sent: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Delete communication mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiRequest("DELETE", `/api/admin/communications/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/communications"],
      });
      toast({ title: "Success", description: "Communication deleted" });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to delete communication: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Update communication mutation
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      subject,
      body,
    }: {
      id: number;
      subject: string;
      body: string;
    }) =>
      apiRequest("PUT", `/api/admin/communications/${id}`, {
        body: { subject, body },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/communications"],
      });
      toast({ title: "Success", description: "Email updated successfully" });
      setIsEditing(false);
      setIsDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to update email: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Send email mutation
  const sendEmailMutation = useMutation({
    mutationFn: (id: number) =>
      apiRequest("POST", `/api/admin/communications/${id}/send`),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/communications"],
      });
      toast({
        title: "Success",
        description: `Email sent successfully${
          response.messageId ? ` (ID: ${response.messageId})` : ""
        }`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to send email: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const handleEdit = (communication: any) => {
    setSelectedCommunication(communication);
    setEditedSubject(communication.subject);
    setEditedBody(communication.body);
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const handleView = (communication: any) => {
    setSelectedCommunication(communication);
    setIsEditing(false);
    setIsDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (selectedCommunication && editedSubject.trim() && editedBody.trim()) {
      updateMutation.mutate({
        id: selectedCommunication.id,
        subject: editedSubject.trim(),
        body: editedBody.trim(),
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "draft":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "sent":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "failed":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          📧 Winner Email Communications
        </CardTitle>
        <CardDescription>
          Winner notification emails are automatically generated when winners
          are selected
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-4">Loading communications...</div>
        ) : communications.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No email communications yet.</p>
            <p className="text-sm mt-2">
              Emails will appear here when winners are selected.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {communications.slice(0, 5).map((comm: any) => (
              <div
                key={comm.id}
                className="border rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium">{comm.subject}</span>
                      <Badge className={getStatusColor(comm.status)}>
                        {comm.status}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>
                        To: {comm.recipientName} ({comm.recipientEmail})
                      </div>
                      {comm.competition && (
                        <div>Competition: {comm.competition.title}</div>
                      )}
                      <div>
                        Created: {new Date(comm.createdAt).toLocaleDateString()}
                      </div>
                      {comm.sentAt && (
                        <div>
                          Sent: {new Date(comm.sentAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(comm)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {comm.status === "draft" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(comm)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    {(comm.status === "draft" || comm.status === "failed") && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => sendEmailMutation.mutate(comm.id)}
                        disabled={sendEmailMutation.isPending}
                        className={
                          comm.status === "failed"
                            ? "text-orange-600 hover:text-orange-700"
                            : "text-blue-600 hover:text-blue-700"
                        }
                        title={
                          comm.status === "failed"
                            ? "Retry sending email"
                            : "Send Email via cPanel"
                        }
                      >
                        <Send className="h-4 w-4 mr-2" />
                        {comm.status === "failed" ? "Retry" : "Send"}
                      </Button>
                    )}
                    {comm.status === "draft" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => markSentMutation.mutate(comm.id)}
                        disabled={markSentMutation.isPending}
                        className="text-green-600 hover:text-green-700"
                        title="Mark as Sent"
                      >
                        Mark Sent
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteMutation.mutate(comm.id)}
                      disabled={deleteMutation.isPending}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {communications.length > 5 && (
              <div className="text-center text-sm text-gray-500 py-2">
                Showing latest 5 communications. Total: {communications.length}
              </div>
            )}
          </div>
        )}

        {/* Communication Details Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {isEditing ? "Edit Email" : "Email Details"}
                {selectedCommunication && (
                  <Badge
                    className={getStatusColor(selectedCommunication.status)}
                  >
                    {selectedCommunication.status}
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription>
                {isEditing
                  ? "Edit the email content before sending"
                  : "Review and manage winner notification email"}
              </DialogDescription>
            </DialogHeader>

            {selectedCommunication && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Recipient</Label>
                    <p className="text-sm">
                      {selectedCommunication.recipientName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {selectedCommunication.recipientEmail}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Type</Label>
                    <p className="text-sm">
                      {selectedCommunication.type.replace("_", " ")}
                    </p>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">Subject</Label>
                  {isEditing ? (
                    <Input
                      value={editedSubject}
                      onChange={(e) => setEditedSubject(e.target.value)}
                      className="mt-1"
                      placeholder="Email subject"
                    />
                  ) : (
                    <p className="text-sm mt-1 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      {selectedCommunication.subject}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-medium">Email Body</Label>
                  {isEditing ? (
                    <Textarea
                      value={editedBody}
                      onChange={(e) => setEditedBody(e.target.value)}
                      className="mt-1 min-h-[200px]"
                      placeholder="Email body content"
                    />
                  ) : (
                    <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-800 rounded border max-h-60 overflow-y-auto">
                      {selectedCommunication.body.includes("<html") ||
                      selectedCommunication.body.includes("<!DOCTYPE") ? (
                        <div
                          className="text-sm"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeEmailContent(
                              selectedCommunication.body
                            ),
                          }}
                        />
                      ) : (
                        <pre className="text-xs whitespace-pre-wrap">
                          {selectedCommunication.body}
                        </pre>
                      )}
                    </div>
                  )}
                </div>

                {selectedCommunication.competition && (
                  <div>
                    <Label className="text-sm font-medium">
                      Related Competition
                    </Label>
                    <p className="text-sm mt-1">
                      {selectedCommunication.competition.title}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
                  <div>
                    <Label className="text-sm font-medium">Created</Label>
                    <p>
                      {new Date(
                        selectedCommunication.createdAt
                      ).toLocaleString()}
                    </p>
                  </div>
                  {selectedCommunication.sentAt && (
                    <div>
                      <Label className="text-sm font-medium">Sent</Label>
                      <p>
                        {new Date(
                          selectedCommunication.sentAt
                        ).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>

                {(selectedCommunication.status === "draft" ||
                  selectedCommunication.status === "failed") && (
                  <div className="flex justify-end space-x-2 pt-4 border-t">
                    {isEditing ? (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setIsEditing(false);
                            setEditedSubject(selectedCommunication.subject);
                            setEditedBody(selectedCommunication.body);
                          }}
                          disabled={updateMutation.isPending}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSaveEdit}
                          disabled={
                            updateMutation.isPending ||
                            !editedSubject.trim() ||
                            !editedBody.trim()
                          }
                        >
                          {updateMutation.isPending
                            ? "Saving..."
                            : "Save Changes"}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => handleEdit(selectedCommunication)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Email
                        </Button>
                        <Button
                          onClick={() => {
                            sendEmailMutation.mutate(selectedCommunication.id);
                            setIsDialogOpen(false);
                          }}
                          disabled={sendEmailMutation.isPending}
                          className={
                            selectedCommunication.status === "failed"
                              ? "bg-orange-600 hover:bg-orange-700"
                              : "bg-blue-600 hover:bg-blue-700"
                          }
                        >
                          <Send className="h-4 w-4 mr-2" />
                          {selectedCommunication.status === "failed"
                            ? "Retry Email"
                            : "Send Email"}
                        </Button>
                        {selectedCommunication.status === "draft" && (
                          <Button
                            variant="outline"
                            onClick={() => {
                              markSentMutation.mutate(selectedCommunication.id);
                              setIsDialogOpen(false);
                            }}
                            disabled={markSentMutation.isPending}
                          >
                            Mark as Sent
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

const normalizeStringList = (values: unknown[]): string[] =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .filter((value): value is string => typeof value === "string")
        .map((value) => value.trim())
        .filter((value) => value.length > 0)
    )
  );

const parseKeywordList = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return normalizeStringList(value);
  }
  if (typeof value === "string") {
    return normalizeStringList(value.split(","));
  }
  return [];
};

// Question Management Component with 4 tabs
function QuestionManagementTab({
  preSelectedVendor,
  onVendorUsed,
}: {
  preSelectedVendor?: any;
  onVendorUsed?: () => void;
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [questionTab, setQuestionTab] = useState("competition");
  const [allQuestionsSearch, setAllQuestionsSearch] = useState("");
  const [allQuestionsSourceFilter, setAllQuestionsSourceFilter] =
    useState("all");
  const [isExporting, setIsExporting] = useState(false);

  // Handle pre-selected vendor from vendor management
  useEffect(() => {
    if (preSelectedVendor) {
      setQuestionTab("competition");
      setQuestionSource("ai");
      setEditingQuestion(null);
      setIsQuestionDialogOpen(true);
      onVendorUsed?.();
    }
  }, [preSelectedVendor, onVendorUsed]);
  const [selectedCompetition, setSelectedCompetition] = useState<any>(null);
  const [isQuestionDialogOpen, setIsQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<any>(null);
  const [vendorFilter, setVendorFilter] = useState("");
  const [isVendorDialogOpen, setIsVendorDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<any>(null);
  const [questionSource, setQuestionSource] = useState("ai");
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [customCategory, setCustomCategory] = useState<string>("");
  const [customSubCategory, setCustomSubCategory] = useState<string>("");
  const [customKeywords, setCustomKeywords] = useState<string[]>([]);
  const [customKeywordInput, setCustomKeywordInput] = useState<string>("");
  const [aiQuestionCount, setAiQuestionCount] = useState<number>(3);
  const [aiTheme, setAiTheme] = useState<string>("");
  const addKeywordToSelection = (keyword: string) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    setCustomKeywords((prev) =>
      prev.includes(trimmed) ? prev : [...prev, trimmed]
    );
  };
  const removeKeywordFromSelection = (keyword: string) => {
    setCustomKeywords((prev) => prev.filter((item) => item !== keyword));
  };
  const editingQuestionCategory =
    (editingQuestion?.category as string) ||
    (editingQuestion as any)?.category_name ||
    "";
  const editingQuestionSubCategory =
    (editingQuestion?.subCategory as string) ||
    (editingQuestion?.sub_category as string) ||
    "";
  const editingQuestionKeywords = parseKeywordList(
    (editingQuestion as any)?.keywords ??
      (editingQuestion as any)?.tags ??
      (editingQuestion as any)?.keywordList ??
      (editingQuestion as any)?.tagList
  );

  const categoryDefinitionsList = useMemo(() => {
    if (
      editingQuestionCategory &&
      !CATEGORY_DEFINITIONS.some(
        (definition) => definition.name === editingQuestionCategory
      )
    ) {
      return [
        ...CATEGORY_DEFINITIONS,
        {
          name: editingQuestionCategory,
          subCategories: editingQuestionSubCategory
            ? [editingQuestionSubCategory]
            : [],
          keywords: editingQuestionKeywords,
        },
      ];
    }
    return CATEGORY_DEFINITIONS;
  }, [
    editingQuestionCategory,
    editingQuestionSubCategory,
    editingQuestionKeywords,
  ]);

  const selectedCategoryDefinition = useMemo(
    () =>
      categoryDefinitionsList.find(
        (definition) => definition.name === customCategory
      ) || null,
    [categoryDefinitionsList, customCategory]
  );

  const availableSubCategories = useMemo(() => {
    const subCategories = selectedCategoryDefinition?.subCategories ?? [];
    return customSubCategory && !subCategories.includes(customSubCategory)
      ? [customSubCategory, ...subCategories]
      : subCategories;
  }, [selectedCategoryDefinition, customSubCategory]);

  useEffect(() => {
    const defaultTheme =
      selectedCompetition?.description || selectedCompetition?.title || "";

    if (!isQuestionDialogOpen) {
      setQuestionSource("ai");
      setCustomCategory("");
      setCustomSubCategory("");
      setCustomKeywords([]);
      setCustomKeywordInput("");
      setAiQuestionCount(3);
      setAiTheme(defaultTheme);
      return;
    }

    if (editingQuestion) {
      const nextCategory =
        (editingQuestion?.category as string) ||
        (editingQuestion as any)?.category_name ||
        "";
      const nextSubCategory =
        (editingQuestion?.subCategory as string) ||
        (editingQuestion?.sub_category as string) ||
        "";
      const existingKeywords = parseKeywordList(
        (editingQuestion as any)?.keywords ??
          (editingQuestion as any)?.tags ??
          (editingQuestion as any)?.keywordList ??
          (editingQuestion as any)?.tagList
      );

      setQuestionSource("custom");
      setCustomCategory(nextCategory);
      setCustomSubCategory(nextSubCategory);
      setCustomKeywords(existingKeywords);
      setCustomKeywordInput("");
      return;
    }

    setQuestionSource("ai");
    setCustomCategory(selectedCompetition?.category || "");
    setCustomSubCategory("");
    setCustomKeywords([]);
    setCustomKeywordInput("");
    setAiQuestionCount(3);
    setAiTheme(defaultTheme);
  }, [editingQuestion, isQuestionDialogOpen, selectedCompetition]);

  // Fetch data for different question types
  const { data: globalQuestions } = useQuery<GlobalQuestion[]>({
    queryKey: ["/api/admin/global-questions"],
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
  const globalQuestionsData = (globalQuestions as any[]) || [];

  const { data: brandQuestions } = useQuery<BrandQuestion[]>({
    queryKey: ["/api/admin/brand-questions"],
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
  const brandQuestionsData = (brandQuestions as any[]) || [];

  const { data: behavioralQuestions } = useQuery<BehavioralQuestion[]>({
    queryKey: ["/api/admin/behavioral-questions"],
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
  const behavioralQuestionsData = (behavioralQuestions as any[]) || [];

  const shouldFetchUsedQuestionIds = useMemo(
    () => ["global", "brand", "behavioral"].includes(questionSource),
    [questionSource]
  );

  // Used question IDs for the selected competition type and current source
  const { data: usedQuestionIdsData } = useQuery({
    queryKey: [
      "/api/admin/questions/used",
      selectedCompetition?.type,
      questionSource,
    ],
    enabled:
      !!user && !!selectedCompetition?.type && shouldFetchUsedQuestionIds,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/admin/questions/used?type=${selectedCompetition?.type}&source=${questionSource}`
      );
      return res.json();
    },
  });
  const usedQuestionIds: number[] = Array.isArray(usedQuestionIdsData?.usedIds)
    ? usedQuestionIdsData.usedIds
    : [];

  const { data: competitions } = useQuery<Competition[]>({
    queryKey: ["/api/admin/competitions"],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: competitionQuestions } = useQuery({
    queryKey: ["/api/admin/questions", selectedCompetition?.id],
    enabled: !!selectedCompetition?.id && questionTab === "competition",
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: vendors } = useQuery({
    queryKey: ["/api/admin/vendors"],
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Fetch all questions from all sources
  const { data: allQuestionsData, isLoading: allQuestionsLoading } = useQuery({
    queryKey: ["/api/admin/all-questions"],
    enabled: questionTab === "all",
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // CSV Export handler
  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      
      // Get auth token from Supabase
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch("/api/admin/all-questions/export", {
        credentials: "include",
        headers,
      });

      if (!response.ok) throw new Error("Export failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `questions-export-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Success",
        description: "Questions exported successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export questions",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Vendor mutations
  const addVendorMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/admin/vendors", {
        body: data,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/vendors"] });
      toast({ title: "Vendor added successfully!" });
      setIsVendorDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error adding vendor",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const editVendorMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("PUT", `/api/admin/vendors/${data.id}`, {
        body: data,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/vendors"] });
      toast({ title: "Vendor updated successfully!" });
      setIsVendorDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error updating vendor",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteVendorMutation = useMutation({
    mutationFn: async (vendorId: number) => {
      const res = await apiRequest("DELETE", `/api/admin/vendors/${vendorId}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/vendors"] });
      toast({ title: "Vendor deleted successfully!" });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting vendor",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Mutations for different question types
  const addGlobalQuestionMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/admin/global-questions", {
        body: data,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/global-questions"],
      });
      toast({ title: "Global question added successfully!" });
      setIsQuestionDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error adding question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const addBrandQuestionMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/admin/brand-questions", {
        body: data,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/brand-questions"],
      });
      toast({ title: "Brand question added successfully!" });
      setIsQuestionDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error adding question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const addBehavioralQuestionMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/admin/behavioral-questions", {
        body: data,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/behavioral-questions"],
      });
      toast({ title: "Behavioral question added successfully!" });
      setIsQuestionDialogOpen(false);
      setEditingQuestion(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error adding question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update mutations for different question types
  const updateGlobalQuestionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await apiRequest("PUT", `/api/admin/global-questions/${id}`, {
        body: data,
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.message || "Failed to update global question"
        );
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/global-questions"],
      });
      toast({ title: "Global question updated successfully!" });
      setIsQuestionDialogOpen(false);
      setEditingQuestion(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error updating question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateBrandQuestionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await apiRequest("PUT", `/api/admin/brand-questions/${id}`, {
        body: data,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/brand-questions"],
      });
      toast({ title: "Brand question updated successfully!" });
      setIsQuestionDialogOpen(false);
      setEditingQuestion(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error updating question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateBehavioralQuestionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await apiRequest(
        "PUT",
        `/api/admin/behavioral-questions/${id}`,
        { body: data }
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/behavioral-questions"],
      });
      toast({ title: "Behavioral question updated successfully!" });
      setIsQuestionDialogOpen(false);
      setEditingQuestion(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error updating question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateCompetitionQuestionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await apiRequest("PUT", `/api/admin/questions/${id}`, {
        body: data,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/questions", selectedCompetition?.id],
      });
      toast({ title: "Competition question updated successfully!" });
      setIsQuestionDialogOpen(false);
      setEditingQuestion(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error updating question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const addCompetitionQuestionMutation = useMutation({
    mutationFn: async ({
      competitionId,
      data,
    }: {
      competitionId: number;
      data: any;
    }) => {
      const res = await apiRequest(
        "POST",
        `/api/competitions/${competitionId}/questions`,
        { body: data }
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/questions", selectedCompetition?.id],
      });
      toast({ title: "Competition question added successfully!" });
      setIsQuestionDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error adding question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const generateAiCompetitionQuestionsMutation = useMutation({
    mutationFn: async ({
      competitionId,
      data,
    }: {
      competitionId: number;
      data: {
        count: number;
        category: string;
        subCategory?: string;
        keywords: string[];
        theme?: string;
      };
    }) => {
      const res = await apiRequest(
        "POST",
        `/api/admin/competitions/${competitionId}/questions/generate-ai`,
        {
          body: data,
        }
      );
      const payload = await res
        .json()
        .catch(() => ({ message: "Failed to parse response" }));
      if (!res.ok) {
        throw new Error(payload?.message || "Failed to generate AI questions");
      }
      return payload;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/questions", selectedCompetition?.id],
      });
      toast({
        title: "AI questions generated",
        description: `Created ${
          data.createdCount ?? data?.createdQuestions?.length ?? 0
        } question${
          (data.createdCount ?? data?.createdQuestions?.length ?? 0) === 1
            ? ""
            : "s"
        } for this competition.`,
      });
      setIsQuestionDialogOpen(false);
      setEditingQuestion(null);
    },
    onError: (error: any) => {
      toast({
        title: "AI generation failed",
        description: error.message || "Failed to generate questions",
        variant: "destructive",
      });
    },
  });

  // Delete mutations for different question types
  const deleteGlobalQuestionMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest(
        "DELETE",
        `/api/admin/global-questions/${id}`
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/global-questions"],
      });
      toast({ title: "Global question deleted successfully!" });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteBrandQuestionMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest(
        "DELETE",
        `/api/admin/brand-questions/${id}`
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/brand-questions"],
      });
      toast({ title: "Brand question deleted successfully!" });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteBehavioralQuestionMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest(
        "DELETE",
        `/api/admin/behavioral-questions/${id}`
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/behavioral-questions"],
      });
      toast({ title: "Behavioral question deleted successfully!" });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteCompetitionQuestionMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/admin/questions/${id}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/questions", selectedCompetition?.id],
      });
      toast({ title: "Competition question deleted successfully!" });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleAddQuestion = (formData: FormData) => {
    if (questionSource === "ai") {
      if (!selectedCompetition) {
        toast({
          title: "Select a competition first",
          description:
            "Choose a competition to assign the AI-generated questions to.",
          variant: "destructive",
        });
        return;
      }

      if (!customCategory) {
        toast({
          title: "Category required",
          description: "Pick a category so the AI knows what to focus on.",
          variant: "destructive",
        });
        return;
      }

      if (!aiQuestionCount || aiQuestionCount < 1) {
        toast({
          title: "Invalid quantity",
          description: "Please request at least one question.",
          variant: "destructive",
        });
        return;
      }

      const trimmedTheme =
        aiTheme.trim() ||
        selectedCompetition.description ||
        selectedCompetition.title ||
        "";

      generateAiCompetitionQuestionsMutation.mutate({
        competitionId: selectedCompetition.id,
        data: {
          count: Math.min(aiQuestionCount, 20),
          category: customCategory,
          subCategory: customSubCategory || undefined,
          keywords: customKeywords,
          theme: trimmedTheme || undefined,
        },
      });
      return;
    }

    const existingQuestionId = formData.get("existingQuestionId") as string;

    if (
      questionTab === "competition" &&
      questionSource !== "custom" &&
      existingQuestionId &&
      !editingQuestion
    ) {
      // Adding existing question to competition
      handleAddQuestionToCompetition(
        questionSource,
        parseInt(existingQuestionId)
      );
      setIsQuestionDialogOpen(false);
      return;
    }

    // Creating/updating question
    const questionText = formData.get("questionText") as string;
    const questionType = formData.get("questionType") as string;
    const options = formData.get("options") as string;
    // HIDDEN: Correct answer removed per user request
    // const correctAnswer = formData.get('correctAnswer') as string;
    const difficulty = formData.get("difficulty") as string;
    const isRequired = formData.get("isRequired") === "true";
    const vendorId = formData.get("vendorId") as string;
    const isCustomQuestion =
      questionTab !== "competition" || questionSource === "custom";
    const categoryValue = isCustomQuestion
      ? customCategory
      : (formData.get("category") as string) || "";

    if (isCustomQuestion && !categoryValue) {
      toast({
        title: "Validation Error",
        description: "Please select a category for this question",
        variant: "destructive",
      });
      return;
    }

    const subCategoryValue = isCustomQuestion
      ? customSubCategory
      : (formData.get("subCategory") as string) || "";
    const keywordsFromForm = formData.get("keywords");
    const keywordsValue = isCustomQuestion
      ? customKeywords
      : parseKeywordList(keywordsFromForm);

    const questionData = {
      questionText,
      questionType: questionTab === "global" ? "mcq" : questionType, // Force MCQ for global questions
      options: options ? options.split("\n").filter((opt) => opt.trim()) : [],
      // HIDDEN: Correct answer removed per user request
      // correctAnswer,
      category: categoryValue,
      subCategory: subCategoryValue || undefined,
      keywords: keywordsValue,
      difficulty: difficulty || "easy",
      isRequired: questionTab === "global" ? false : isRequired, // Force optional for global questions
      isActive: true,
      vendorId: vendorId && vendorId !== "none" ? parseInt(vendorId) : null,
    };

    if (editingQuestion) {
      // Updating existing question
      switch (questionTab) {
        case "global":
          updateGlobalQuestionMutation.mutate({
            id: editingQuestion.id,
            data: questionData,
          });
          break;
        case "brand":
          updateBrandQuestionMutation.mutate({
            id: editingQuestion.id,
            data: questionData,
          });
          break;
        case "behavioral":
          updateBehavioralQuestionMutation.mutate({
            id: editingQuestion.id,
            data: questionData,
          });
          break;
        case "competition":
          updateCompetitionQuestionMutation.mutate({
            id: editingQuestion.id,
            data: questionData,
          });
          break;
      }
    } else {
      // Creating new question
      switch (questionTab) {
        case "global":
          addGlobalQuestionMutation.mutate(questionData);
          break;
        case "brand":
          addBrandQuestionMutation.mutate(questionData);
          break;
        case "behavioral":
          addBehavioralQuestionMutation.mutate(questionData);
          break;
        case "competition":
          if (selectedCompetition) {
            addCompetitionQuestionMutation.mutate({
              competitionId: selectedCompetition.id,
              data: questionData,
            });
          }
          break;
      }
    }
  };

  const handleDeleteQuestion = (question: any) => {
    switch (questionTab) {
      case "global":
        deleteGlobalQuestionMutation.mutate(question.id);
        break;
      case "brand":
        deleteBrandQuestionMutation.mutate(question.id);
        break;
      case "behavioral":
        deleteBehavioralQuestionMutation.mutate(question.id);
        break;
      case "competition":
        deleteCompetitionQuestionMutation.mutate(question.id);
        break;
    }
  };

  // Mutations for adding questions from pools to competitions
  const addGlobalToCompetitionMutation = useMutation({
    mutationFn: async ({
      competitionId,
      globalQuestionId,
    }: {
      competitionId: number;
      globalQuestionId: number;
    }) => {
      const res = await apiRequest(
        "POST",
        `/api/admin/competitions/${competitionId}/add-global-question`,
        { body: { globalQuestionId } }
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/questions", selectedCompetition?.id],
      });
      toast({ title: "Global question added to competition!" });
    },
    onError: (error: any) => {
      toast({
        title: "Error adding question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const addBrandToCompetitionMutation = useMutation({
    mutationFn: async ({
      competitionId,
      brandQuestionId,
    }: {
      competitionId: number;
      brandQuestionId: number;
    }) => {
      const res = await apiRequest(
        "POST",
        `/api/admin/competitions/${competitionId}/add-brand-question`,
        { body: { brandQuestionId } }
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/questions", selectedCompetition?.id],
      });
      toast({ title: "Brand question added to competition!" });
    },
    onError: (error: any) => {
      toast({
        title: "Error adding question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const addBehavioralToCompetitionMutation = useMutation({
    mutationFn: async ({
      competitionId,
      behavioralQuestionId,
    }: {
      competitionId: number;
      behavioralQuestionId: number;
    }) => {
      const res = await apiRequest(
        "POST",
        `/api/admin/competitions/${competitionId}/add-behavioral-question`,
        { body: { behavioralQuestionId } }
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/questions", selectedCompetition?.id],
      });
      toast({ title: "Behavioral question added to competition!" });
    },
    onError: (error: any) => {
      toast({
        title: "Error adding question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const removeFromCompetitionMutation = useMutation({
    mutationFn: async ({
      competitionId,
      questionId,
    }: {
      competitionId: number;
      questionId: number;
    }) => {
      const res = await apiRequest(
        "DELETE",
        `/api/admin/competitions/${competitionId}/questions/${questionId}`
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/questions", selectedCompetition?.id],
      });
      toast({ title: "Question removed from competition!" });
    },
    onError: (error: any) => {
      toast({
        title: "Error removing question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Import questions mutation
  const importQuestionsMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      // Get auth headers from Supabase session
      const authHeaders: Record<string, string> = {};
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token) {
        authHeaders.Authorization = `Bearer ${session.access_token}`;
      }

      const response = await fetch("/api/admin/questions/import", {
        method: "POST",
        headers: authHeaders,
        body: formData,
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
      }

      return response.json();
    },
    onSuccess: (result) => {
      setImportResult(result);
      // Invalidate all question queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/global-questions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/brand-questions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/behavioral-questions"],
      });
      toast({
        title: "Import completed!",
        description: `Successfully imported ${result.successful} questions`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Import failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleImport = () => {
    if (!importFile) {
      toast({ title: "Please select a file first", variant: "destructive" });
      return;
    }
    importQuestionsMutation.mutate(importFile);
  };

  const handleAddQuestionToCompetition = (type: string, questionId: number) => {
    if (!selectedCompetition) return;

    switch (type) {
      case "global":
        (addGlobalToCompetitionMutation as any).mutate({
          competitionId: selectedCompetition.id,
          globalQuestionId: questionId,
        });
        break;
      case "brand":
        (addBrandToCompetitionMutation as any).mutate({
          competitionId: selectedCompetition.id,
          brandQuestionId: questionId,
        });
        break;
      case "behavioral":
        (addBehavioralToCompetitionMutation as any).mutate({
          competitionId: selectedCompetition.id,
          behavioralQuestionId: questionId,
        });
        break;
    }
  };

  const handleRemoveQuestionFromCompetition = (questionId: number) => {
    if (!selectedCompetition) return;
    removeFromCompetitionMutation.mutate({
      competitionId: selectedCompetition.id,
      questionId,
    });
  };

  const getCurrentQuestions = () => {
    switch (questionTab) {
      case "global":
        return globalQuestions;
      case "brand":
        return brandQuestions;
      case "behavioral":
        return behavioralQuestions;
      case "competition":
        return competitionQuestions;
      case "all":
        return allQuestionsData?.questions || [];
      default:
        return [];
    }
  };

  const getQuestionTypeConfig = () => {
    switch (questionTab) {
      case "global":
        return {
          title: "Global Questions",
          description: "Manage questions that can be added to any competition",
          categories: ["general", "trivia", "knowledge", "opinion", "personal"],
        };
      case "brand":
        return {
          title: "Brand Questions",
          description:
            "Manage brand-specific questions for marketing and awareness",
          categories: [
            "brand-awareness",
            "product-knowledge",
            "marketing",
            "advertising",
            "brand-identity",
          ],
        };
      case "behavioral":
        return {
          title: "Behavioral Questions",
          description: "Manage questions about user behavior and preferences",
          categories: [
            "personality",
            "preferences",
            "lifestyle",
            "habits",
            "decision-making",
          ],
        };
      case "competition":
        return {
          title: "Competition Questions",
          description: selectedCompetition
            ? `Manage questions for "${selectedCompetition.title}"`
            : "Select a competition to manage its questions",
          categories: ["general", "trivia", "knowledge", "opinion"],
        };
      case "all":
        return {
          title: "All Questions",
          description: "View all questions from all sources with categories",
          categories: [],
        };
      default:
        return { title: "", description: "", categories: [] };
    }
  };

  const config = getQuestionTypeConfig();
  const questions = getCurrentQuestions();

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Question Management</CardTitle>
            <CardDescription>
              Create competition questions manually or generate fresh sets with
              AI
            </CardDescription>
          </div>
          <div className="flex gap-2 items-center">
            {/* Tab Selection */}
            <Tabs value={questionTab} onValueChange={setQuestionTab}>
              <TabsList>
                <TabsTrigger value="competition">Competition</TabsTrigger>
                <TabsTrigger value="global">Global</TabsTrigger>
                <TabsTrigger value="brand">Brand</TabsTrigger>
                <TabsTrigger value="behavioral">Behavioral</TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>
            </Tabs>

            {questionTab === "competition" && (
              <Select
                value={selectedCompetition?.id?.toString() || ""}
                onValueChange={(value) => {
                  const comp = Array.isArray(competitions)
                    ? competitions.find((c: any) => c.id.toString() === value)
                    : null;
                  setSelectedCompetition(comp);
                }}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select Competition" />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(competitions) &&
                    competitions.map((competition: any) => (
                      <SelectItem
                        key={competition.id}
                        value={competition.id.toString()}
                      >
                        {competition.title}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
            {(questionTab !== "competition" || selectedCompetition) && (
              <>
                <Dialog
                  open={isImportDialogOpen}
                  onOpenChange={setIsImportDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button variant="outline">
                      <Upload className="h-4 w-4 mr-2" />
                      Import Questions
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl">
                    <DialogHeader>
                      <DialogTitle>Import Questions from Excel</DialogTitle>
                      <DialogDescription>
                        Upload an Excel file (.xlsx, .xls) with questions to
                        import them into your database.
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6">
                      {/* File Upload Section */}
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="file-upload">Select Excel File</Label>
                          <Input
                            id="file-upload"
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              setImportFile(file);
                              setImportResult(null); // Clear previous results
                            }}
                            className="mt-2"
                          />
                          {importFile && (
                            <p className="text-sm text-muted-foreground mt-2">
                              Selected: {importFile.name} (
                              {Math.round(importFile.size / 1024)} KB)
                            </p>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            onClick={handleImport}
                            disabled={
                              !importFile || importQuestionsMutation.isPending
                            }
                            className="primary-gradient"
                          >
                            {importQuestionsMutation.isPending ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Importing...
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                Import Questions
                              </>
                            )}
                          </Button>

                          <Button
                            variant="outline"
                            onClick={async () => {
                              try {
                                const res = await fetch(
                                  "/api/admin/questions/import/template"
                                );
                                const data = await res.json();

                                // Create a downloadable template file
                                const csvContent = [
                                  data.template.headers.join(","),
                                  ...data.template.sampleData.map(
                                    (row: any[]) =>
                                      row.map((cell) => `"${cell}"`).join(",")
                                  ),
                                ].join("\n");

                                const blob = new Blob([csvContent], {
                                  type: "text/csv",
                                });
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = "questions-import-template.csv";
                                a.click();
                                window.URL.revokeObjectURL(url);

                                toast({
                                  title: "Template Downloaded",
                                  description:
                                    "Check your downloads folder for the template file",
                                });
                              } catch (error) {
                                toast({
                                  title: "Download Failed",
                                  description:
                                    "Failed to download template file",
                                  variant: "destructive",
                                });
                              }
                            }}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download Template
                          </Button>
                        </div>
                      </div>

                      {/* Import Result */}
                      {importResult && (
                        <div className="space-y-4">
                          <div
                            className={`p-4 rounded-lg border ${
                              importResult.success
                                ? "bg-green-50 border-green-200"
                                : "bg-red-50 border-red-200"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              {importResult.success ? (
                                <CheckCircle className="h-5 w-5 text-green-600" />
                              ) : (
                                <XCircle className="h-5 w-5 text-red-600" />
                              )}
                              <h3 className="font-medium">
                                {importResult.success
                                  ? "Import Successful"
                                  : "Import Failed"}
                              </h3>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">
                              {importResult.message}
                            </p>

                            {importResult.details && (
                              <div className="grid grid-cols-3 gap-4 mb-3">
                                <div className="text-center">
                                  <div className="text-lg font-medium">
                                    {importResult.details.totalProcessed}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    Total
                                  </div>
                                </div>
                                <div className="text-center">
                                  <div className="text-lg font-medium text-green-600">
                                    {importResult.details.successful}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    Success
                                  </div>
                                </div>
                                <div className="text-center">
                                  <div className="text-lg font-medium text-red-600">
                                    {importResult.details.failed}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    Failed
                                  </div>
                                </div>
                              </div>
                            )}

                            {importResult.details?.errors?.length > 0 && (
                              <div className="space-y-2">
                                <h4 className="font-medium text-sm">Errors:</h4>
                                <div className="max-h-32 overflow-y-auto">
                                  {importResult.details.errors.map(
                                    (error: string, index: number) => (
                                      <div
                                        key={index}
                                        className="text-xs text-red-600 p-1 bg-red-100 rounded"
                                      >
                                        {error}
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Instructions */}
                      <div className="space-y-3 text-sm">
                        <h3 className="font-medium">
                          File Format Instructions:
                        </h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>
                            <strong>Required Columns:</strong>
                          </p>
                          <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>Question Text - The actual question</li>
                            <li>Question Type - mcq, open, or true_false</li>
                            <li>
                              Options - Either separate columns (Option 1,
                              Option 2...) or combined with | separator
                            </li>
                            <li>Correct Answer - The correct answer text</li>
                            <li>Category - Question category</li>
                            <li>
                              Question Source - global, brand, or behavioral
                            </li>
                          </ul>
                          <p>
                            <strong>Optional Columns:</strong>
                          </p>
                          <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>
                              Difficulty - easy, medium, hard (defaults to easy)
                            </li>
                            <li>Tags - Comma-separated keywords</li>
                            <li>Vendor ID - For brand questions only</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </>
            )}
            {(questionTab !== "competition" || selectedCompetition) && (
              <Dialog
                open={isQuestionDialogOpen}
                onOpenChange={(open) => {
                  setIsQuestionDialogOpen(open);
                  if (!open) {
                    setEditingQuestion(null);
                    setVendorFilter("");
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button className="primary-gradient">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Question
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>
                      {editingQuestion ? "Edit" : "Add New"}{" "}
                      {config.title.slice(0, -1)}
                    </DialogTitle>
                    <DialogDescription>{config.description}</DialogDescription>
                  </DialogHeader>
                  <form
                    key={`${editingQuestion?.id || 'new'}-${questionSource}-${isQuestionDialogOpen}`}
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleAddQuestion(new FormData(e.currentTarget));
                    }}
                    className="space-y-4"
                  >
                    {questionTab === "competition" && (
                      <div>
                        <Label htmlFor="questionSource">Question Source</Label>
                        <Select
                          name="questionSource"
                          value={questionSource}
                          onValueChange={setQuestionSource}
                          required
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select question source" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ai">Generate with AI</SelectItem>
                            <SelectItem value="custom">
                              Manual Question
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {questionSource === "custom" ? (
                      <>
                        <div>
                          <Label htmlFor="questionText">Question Text</Label>
                          <Textarea
                            name="questionText"
                            required
                            autoComplete="off"
                            defaultValue={
                              editingQuestion?.questionText ||
                              editingQuestion?.question_text ||
                              ""
                            }
                          />
                        </div>
                        <input
                          type="hidden"
                          name="category"
                          value={customCategory}
                        />
                        <input
                          type="hidden"
                          name="subCategory"
                          value={customSubCategory}
                        />
                        <input
                          type="hidden"
                          name="keywords"
                          value={customKeywords.join(",")}
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="questionType">Question Type</Label>
                            <Select
                              name="questionType"
                              required
                              defaultValue={
                                editingQuestion?.questionType ||
                                editingQuestion?.question_type ||
                                (questionTab === "global" ? "mcq" : "mcq")
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="open">Text Input</SelectItem>
                                <SelectItem value="mcq">
                                  Multiple Choice
                                </SelectItem>
                                <SelectItem value="true_false">
                                  True/False
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Category</Label>
                            <Select
                              value={customCategory || undefined}
                              onValueChange={(value) => {
                                setCustomCategory(value);
                                setCustomSubCategory("");
                                setCustomKeywords([]);
                                setCustomKeywordInput("");
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent>
                                {categoryDefinitionsList.map((category) => (
                                  <SelectItem
                                    key={category.name}
                                    value={category.name}
                                  >
                                    {category.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Sub-Category</Label>
                            <Select
                              value={customSubCategory || undefined}
                              onValueChange={setCustomSubCategory}
                              disabled={!customCategory}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select sub-category" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableSubCategories.map((subCategory) => (
                                  <SelectItem
                                    key={subCategory}
                                    value={subCategory}
                                  >
                                    {subCategory}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {selectedCategoryDefinition &&
                              availableSubCategories.length === 0 && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  No sub-categories available for this category.
                                </p>
                              )}
                          </div>
                          <div>
                            <Label>Keywords</Label>
                            {customKeywords.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-2">
                                {customKeywords.map((keyword) => (
                                  <Badge
                                    key={keyword}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                  >
                                    {keyword}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeKeywordFromSelection(keyword)
                                      }
                                      className="rounded-full p-0.5 hover:text-destructive focus:outline-none focus:ring-1 focus:ring-ring"
                                      aria-label={`Remove keyword ${keyword}`}
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {selectedCategoryDefinition && (
                              <div className="space-y-2">
                                <p className="text-xs text-muted-foreground">
                                  Suggested keywords for this category
                                </p>
                                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                                  {selectedCategoryDefinition.keywords.map(
                                    (keyword) => {
                                      const isSelected =
                                        customKeywords.includes(keyword);
                                      return (
                                        <Badge
                                          key={keyword}
                                          variant={
                                            isSelected ? "default" : "outline"
                                          }
                                          className="cursor-pointer hover:bg-muted"
                                          onClick={() =>
                                            isSelected
                                              ? removeKeywordFromSelection(
                                                  keyword
                                                )
                                              : addKeywordToSelection(keyword)
                                          }
                                        >
                                          {keyword}
                                          {isSelected ? " ✓" : " +"}
                                        </Badge>
                                      );
                                    }
                                  )}
                                </div>
                                {selectedCategoryDefinition.keywords.length ===
                                  0 && (
                                  <p className="text-xs text-muted-foreground">
                                    No predefined keywords for this category.
                                  </p>
                                )}
                              </div>
                            )}
                            {!selectedCategoryDefinition && (
                              <p className="text-xs text-muted-foreground mt-2">
                                Select a category to view suggested keywords.
                              </p>
                            )}
                            <div className="flex gap-2 mt-2">
                              <Input
                                value={customKeywordInput}
                                onChange={(event) =>
                                  setCustomKeywordInput(event.target.value)
                                }
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    addKeywordToSelection(customKeywordInput);
                                    setCustomKeywordInput("");
                                  }
                                }}
                                placeholder="Add custom keyword"
                                disabled={!customCategory}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  addKeywordToSelection(customKeywordInput);
                                  setCustomKeywordInput("");
                                }}
                                disabled={
                                  !customCategory || !customKeywordInput.trim()
                                }
                              >
                                Add
                              </Button>
                            </div>
                          </div>
                        </div>
                        {questionTab === "brand" && (
                          <div className="space-y-2">
                            <Label htmlFor="vendorId">Vendor</Label>
                            <Select
                              name="vendorId"
                              defaultValue={
                                editingQuestion?.vendorId?.toString() ||
                                editingQuestion?.vendor_id?.toString() ||
                                "none"
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select vendor" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No vendor</SelectItem>
                                {Array.isArray(vendors) &&
                                  vendors.map((vendor: any) => (
                                    <SelectItem
                                      key={vendor.id}
                                      value={vendor.id.toString()}
                                    >
                                      {vendor.name}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div>
                          <Label htmlFor="options">
                            Options (one per line, for multiple choice)
                          </Label>
                          <Textarea
                            name="options"
                            placeholder="Option 1&#10;Option 2&#10;Option 3"
                            autoComplete="off"
                            defaultValue={
                              editingQuestion?.options &&
                              Array.isArray(editingQuestion.options)
                                ? editingQuestion.options.join("\n")
                                : ""
                            }
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          {/* HIDDEN: Correct answer field removed per user request
                          <div>
                            <Label htmlFor="correctAnswer">Correct Answer</Label>
                            <Input
                              name="correctAnswer"
                              placeholder="For multiple choice, enter the correct option"
                              defaultValue={editingQuestion?.correctAnswer || editingQuestion?.correct_answer || ''}
                            />
                          </div>
                          */}
                          <div></div>
                          <div>
                            <Label htmlFor="difficulty">Difficulty</Label>
                            <Select
                              name="difficulty"
                              defaultValue={
                                editingQuestion?.difficulty || "easy"
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select difficulty" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="easy">Easy</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="hard">Hard</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            name="isRequired"
                            value="true"
                            className="rounded"
                            defaultChecked={
                              questionTab === "global"
                                ? false
                                : editingQuestion?.isRequired ?? false
                            }
                          />
                          <Label htmlFor="isRequired">
                            Required Question (Leave unchecked for optional)
                          </Label>
                        </div>
                      </>
                    ) : questionSource === "ai" ? (
                      <>
                        <div>
                          <Label htmlFor="aiTheme">
                            Competition theme or insight focus
                          </Label>
                          <Textarea
                            id="aiTheme"
                            name="aiTheme"
                            value={aiTheme}
                            onChange={(event) => setAiTheme(event.target.value)}
                            placeholder="Describe the tone, campaign angle, or insight you want these questions to explore."
                            rows={3}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            We share this context with the AI so questions feel
                            on-brand and useful.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Category</Label>
                            <Select
                              value={customCategory || undefined}
                              onValueChange={(value) => {
                                setCustomCategory(value);
                                setCustomSubCategory("");
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent>
                                {categoryDefinitionsList.map((category) => (
                                  <SelectItem
                                    key={category.name}
                                    value={category.name}
                                  >
                                    {category.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="aiQuestionCount">
                              Number of questions
                            </Label>
                            <Input
                              id="aiQuestionCount"
                              name="aiQuestionCount"
                              type="number"
                              min={1}
                              max={20}
                              value={aiQuestionCount}
                              onChange={(event) => {
                                const value = Number(event.target.value);
                                setAiQuestionCount(
                                  Number.isFinite(value) ? value : 0
                                );
                              }}
                              required
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              You can generate up to 20 questions at a time.
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Sub-Category</Label>
                            <Select
                              value={customSubCategory || undefined}
                              onValueChange={setCustomSubCategory}
                              disabled={!customCategory}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select sub-category" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableSubCategories.map((subCategory) => (
                                  <SelectItem
                                    key={subCategory}
                                    value={subCategory}
                                  >
                                    {subCategory}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Keywords to emphasise</Label>
                            <div className="flex gap-2">
                              <Input
                                value={customKeywordInput}
                                onChange={(event) =>
                                  setCustomKeywordInput(event.target.value)
                                }
                                placeholder="loyalty, gaming, travel"
                              />
                              <Button
                                type="button"
                                onClick={() => {
                                  addKeywordToSelection(customKeywordInput);
                                  setCustomKeywordInput("");
                                }}
                                disabled={!customKeywordInput.trim()}
                              >
                                Add
                              </Button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Optional: We lightly weave these into the
                              questions.
                            </p>
                          </div>
                        </div>
                        {customKeywords.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {customKeywords.map((keyword) => (
                              <Badge
                                key={keyword}
                                variant="secondary"
                                className="flex items-center gap-1"
                              >
                                {keyword}
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeKeywordFromSelection(keyword)
                                  }
                                  className="rounded-full p-0.5 hover:text-destructive focus:outline-none focus:ring-1 focus:ring-ring"
                                  aria-label={`Remove keyword ${keyword}`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="space-y-4">
                        {questionSource === "brand" && (
                          <div>
                            <Label htmlFor="vendorFilter">
                              Filter by Vendor (optional)
                            </Label>
                            <Select
                              name="vendorFilter"
                              value={vendorFilter}
                              onValueChange={setVendorFilter}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="All vendors" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All vendors</SelectItem>
                                {Array.isArray(vendors) &&
                                  vendors.map((vendor: any) => (
                                    <SelectItem
                                      key={vendor.id}
                                      value={vendor.id.toString()}
                                    >
                                      {vendor.name}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div>
                          <Label htmlFor="existingQuestionId">
                            Select{" "}
                            {questionSource === "custom"
                              ? "Custom"
                              : questionSource.charAt(0).toUpperCase() +
                                questionSource.slice(1)}{" "}
                            Question
                          </Label>
                          <Select name="existingQuestionId" required>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={`Select ${questionSource} question`}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {questionSource === "global" &&
                                Array.isArray(globalQuestions) &&
                                globalQuestions.map((q: any) => (
                                  <SelectItem
                                    key={q.id}
                                    value={q.id.toString()}
                                    disabled={usedQuestionIds.includes(q.id)}
                                  >
                                    {(
                                      q.questionText || q.question_text
                                    )?.substring(0, 50) || "No question text"}
                                    {usedQuestionIds.includes(q.id)
                                      ? " (already used in this type)"
                                      : ""}
                                    ...
                                  </SelectItem>
                                ))}
                              {questionSource === "brand" &&
                                Array.isArray(brandQuestions) &&
                                brandQuestions
                                  .filter(
                                    (q: any) =>
                                      !vendorFilter ||
                                      vendorFilter === "all" ||
                                      (vendorFilter === "none" &&
                                        !q.vendor_id) ||
                                      q.vendor_id?.toString() === vendorFilter
                                  )
                                  .map((q: any) => (
                                    <SelectItem
                                      key={q.id}
                                      value={q.id.toString()}
                                      disabled={usedQuestionIds.includes(q.id)}
                                    >
                                      {(
                                        q.questionText || q.question_text
                                      )?.substring(0, 40) || "No question text"}
                                      ...{" "}
                                      {q.vendor_name
                                        ? `(${q.vendor_name})`
                                        : "(No vendor)"}
                                      {usedQuestionIds.includes(q.id)
                                        ? " (already used in this type)"
                                        : ""}
                                    </SelectItem>
                                  ))}
                              {questionSource === "behavioral" &&
                                Array.isArray(behavioralQuestions) &&
                                behavioralQuestions.map((q: any) => (
                                  <SelectItem
                                    key={q.id}
                                    value={q.id.toString()}
                                    disabled={usedQuestionIds.includes(q.id)}
                                  >
                                    {(
                                      q.questionText || q.question_text
                                    )?.substring(0, 50) || "No question text"}
                                    {usedQuestionIds.includes(q.id)
                                      ? " (already used in this type)"
                                      : ""}
                                    ...
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsQuestionDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={
                          addGlobalQuestionMutation.isPending ||
                          addBrandQuestionMutation.isPending ||
                          addBehavioralQuestionMutation.isPending ||
                          addCompetitionQuestionMutation.isPending ||
                          updateGlobalQuestionMutation.isPending ||
                          updateBrandQuestionMutation.isPending ||
                          updateBehavioralQuestionMutation.isPending ||
                          updateCompetitionQuestionMutation.isPending ||
                          generateAiCompetitionQuestionsMutation.isPending
                        }
                      >
                        {editingQuestion
                          ? "Update Question"
                          : questionSource === "ai"
                          ? generateAiCompetitionQuestionsMutation.isPending
                            ? "Generating..."
                            : `Generate ${
                                aiQuestionCount > 1
                                  ? `${Math.min(aiQuestionCount, 20)} Questions`
                                  : "Question"
                              }`
                          : "Add Question"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            {config.description}
          </div>

          {/* All Questions View */}
          {questionTab === "all" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center gap-4">
                <div className="flex gap-2 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search questions..."
                      value={allQuestionsSearch}
                      onChange={(e) => setAllQuestionsSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select
                    value={allQuestionsSourceFilter}
                    onValueChange={setAllQuestionsSourceFilter}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="competition">Competition</SelectItem>
                      <SelectItem value="global">Global</SelectItem>
                      <SelectItem value="brand">Brand</SelectItem>
                      <SelectItem value="behavioral">Behavioral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleExportCSV} disabled={isExporting}>
                  <Download className="h-4 w-4 mr-2" />
                  {isExporting ? "Exporting..." : "Export CSV"}
                </Button>
              </div>

              {allQuestionsLoading && (
                <div className="text-center py-8 text-gray-500">
                  Loading questions...
                </div>
              )}

              {!allQuestionsLoading && allQuestionsData && (
                <>
                  <div className="text-sm text-gray-600">
                    Total: {allQuestionsData.summary?.total || 0} questions
                    {allQuestionsData.summary && (
                      <span className="ml-2">
                        (Competition: {allQuestionsData.summary.competition},
                        Global: {allQuestionsData.summary.global}, Brand:{" "}
                        {allQuestionsData.summary.brand}, Behavioral:{" "}
                        {allQuestionsData.summary.behavioral})
                      </span>
                    )}
                  </div>

                  <div className="border rounded-lg overflow-x-auto max-h-[600px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[35%]">Question</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead className="w-[25%]">Options</TableHead>
                          <TableHead>Competition</TableHead>
                          <TableHead>Created</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {questions
                          .filter((q: any) => {
                            const matchesSearch =
                              !allQuestionsSearch ||
                              (q.questionText?.toLowerCase() || "").includes(
                                allQuestionsSearch.toLowerCase()
                              ) ||
                              (q.category?.toLowerCase() || "").includes(
                                allQuestionsSearch.toLowerCase()
                              );
                            const matchesSource =
                              allQuestionsSourceFilter === "all" ||
                              q.source === allQuestionsSourceFilter;
                            return matchesSearch && matchesSource;
                          })
                          .map((question: any) => (
                            <TableRow key={`${question.source}-${question.id}`}>
                              <TableCell className="font-medium">
                                {(question.questionText || "").length > 100
                                  ? `${question.questionText.substring(
                                      0,
                                      100
                                    )}...`
                                  : question.questionText}
                              </TableCell>
                              <TableCell>{question.category || "-"}</TableCell>
                              <TableCell>
                                {question.options &&
                                Array.isArray(question.options) &&
                                question.options.length > 0
                                  ? question.options.join(", ")
                                  : question.questionType === "open"
                                  ? "Open Response"
                                  : "-"}
                              </TableCell>
                              <TableCell>
                                {question.competitionTitle || "-"}
                              </TableCell>
                              <TableCell>
                                {question.createdAt
                                  ? new Date(
                                      question.createdAt
                                    ).toLocaleDateString()
                                  : "-"}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>

                  {questions.filter((q: any) => {
                    const matchesSearch =
                      !allQuestionsSearch ||
                      (q.questionText?.toLowerCase() || "").includes(
                        allQuestionsSearch.toLowerCase()
                      ) ||
                      (q.category?.toLowerCase() || "").includes(
                        allQuestionsSearch.toLowerCase()
                      );
                    const matchesSource =
                      allQuestionsSourceFilter === "all" ||
                      q.source === allQuestionsSourceFilter;
                    return matchesSearch && matchesSource;
                  }).length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <HelpCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No questions found matching your filters.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {questionTab === "competition" && !selectedCompetition && (
            <div className="text-center py-8 text-gray-500">
              <Trophy className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Select a competition to manage its questions.</p>
            </div>
          )}

          {questionTab !== "all" &&
            (questionTab !== "competition" || selectedCompetition) && (
              <div className="space-y-4">
                {Array.isArray(questions) &&
                  questions.map((question: any) => (
                    <Card key={question.id}>
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">
                              {question.questionText ||
                                question.question_text ||
                                "No question text"}
                            </CardTitle>
                            <CardDescription>
                              Type:{" "}
                              {question.questionType || question.question_type}{" "}
                              | Category: {question.category} |
                              {question.difficulty &&
                                ` Difficulty: ${question.difficulty} |`}
                              {question.isRequired ? " Required" : " Optional"}
                              {questionTab === "brand" &&
                                question.vendor_name &&
                                ` | Vendor: ${question.vendor_name}`}
                            </CardDescription>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingQuestion(question);
                                setIsQuestionDialogOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="outline" size="sm">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Delete Question
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete this
                                    question? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() =>
                                      handleDeleteQuestion(question)
                                    }
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </CardHeader>
                      {question.options && question.options.length > 0 && (
                        <CardContent>
                          <div>
                            <p className="text-sm font-medium mb-2">Options:</p>
                            <ul className="text-sm space-y-1">
                              {question.options.map(
                                (option: string, index: number) => (
                                  <li
                                    key={index}
                                    className="flex items-center gap-2"
                                  >
                                    <span className="w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center text-xs">
                                      {index + 1}
                                    </span>
                                    {option}
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                {(!Array.isArray(questions) || questions.length === 0) &&
                  questionTab !== "all" &&
                  (questionTab !== "competition" || selectedCompetition) && (
                    <div className="text-center py-8 text-gray-500">
                      <HelpCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>
                        No questions added yet for this {questionTab} category.
                      </p>
                    </div>
                  )}
              </div>
            )}
        </div>
      </CardContent>
    </Card>
  );
}

// ===== BETFRED UNIQUE VERIFIED TAB =====
function BetfredUniqueVerifiedTab() {
  const [sortField, setSortField] = useState<
    "email" | "date" | "signups" | "users"
  >("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [searchTerm, setSearchTerm] = useState("");

  const { data: uniqueVerifications = [], isLoading } = useQuery<
    Array<{
      betfredEmail: string;
      registrationDate: string;
      verifiedCount: number;
      userCount: number;
      firstName: string;
      lastName: string;
    }>
  >({
    queryKey: ["/api/admin/betfred-unique-verifications"],
  });

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const filteredAndSortedData = [...uniqueVerifications]
    .filter((verification) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        verification.betfredEmail.toLowerCase().includes(searchLower) ||
        verification.firstName?.toLowerCase().includes(searchLower) ||
        verification.lastName?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      const direction = sortDirection === "asc" ? 1 : -1;

      switch (sortField) {
        case "email":
          return direction * a.betfredEmail.localeCompare(b.betfredEmail);
        case "date":
          return (
            direction *
            (new Date(a.registrationDate).getTime() -
              new Date(b.registrationDate).getTime())
          );
        case "signups":
          return direction * (a.verifiedCount - b.verifiedCount);
        case "users":
          return direction * (a.userCount - b.userCount);
        default:
          return 0;
      }
    });

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 ml-1" />;
    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4 ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 ml-1" />
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center">Loading unique verifications...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Betfred Unique Verified Signups</CardTitle>
        <CardDescription>
          All unique Betfred email addresses with verified signups
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="text-sm text-muted-foreground">
              Total Unique Verified Emails:{" "}
              <span className="font-semibold text-foreground">
                {filteredAndSortedData.length}
              </span>
              {searchTerm && ` (filtered from ${uniqueVerifications.length})`}
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search email or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
                data-testid="input-search-betfred"
              />
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("email")}
                    data-testid="header-email"
                  >
                    <div className="flex items-center">
                      Email Address
                      <SortIcon field="email" />
                    </div>
                  </TableHead>
                  <TableHead>User Name</TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("date")}
                    data-testid="header-date"
                  >
                    <div className="flex items-center">
                      First Registration
                      <SortIcon field="date" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="text-center cursor-pointer select-none"
                    onClick={() => handleSort("signups")}
                    data-testid="header-signups"
                  >
                    <div className="flex items-center justify-center">
                      Total Signups
                      <SortIcon field="signups" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="text-center cursor-pointer select-none"
                    onClick={() => handleSort("users")}
                    data-testid="header-users"
                  >
                    <div className="flex items-center justify-center">
                      Unique Users
                      <SortIcon field="users" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedData.length > 0 ? (
                  filteredAndSortedData.map((verification, index) => (
                    <TableRow
                      key={index}
                      data-testid={`row-verification-${index}`}
                    >
                      <TableCell
                        className="font-medium"
                        data-testid={`text-email-${index}`}
                      >
                        {verification.betfredEmail}
                      </TableCell>
                      <TableCell data-testid={`text-name-${index}`}>
                        {verification.firstName} {verification.lastName}
                      </TableCell>
                      <TableCell data-testid={`text-date-${index}`}>
                        {new Date(
                          verification.registrationDate
                        ).toLocaleDateString("en-ZA", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </TableCell>
                      <TableCell
                        className="text-center"
                        data-testid={`text-signups-${index}`}
                      >
                        <Badge variant="secondary">
                          {verification.verifiedCount}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className="text-center"
                        data-testid={`text-users-${index}`}
                      >
                        <Badge variant="outline">
                          {verification.userCount}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-muted-foreground py-8"
                    >
                      {searchTerm
                        ? "No matching verifications found"
                        : "No unique verified Betfred signups found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ===== TERMS & CONDITIONS ADMIN INTERFACE =====
function TermsAndConditionsTab() {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedTerms, setSelectedTerms] = useState<TermsAndConditions | null>(
    null
  );
  const [showViewDialog, setShowViewDialog] = useState(false);

  // Fetch all terms and conditions versions
  const {
    data: termsVersions = [],
    isLoading: termsLoading,
    error: termsError,
  } = useQuery<TermsAndConditions[]>({
    queryKey: ["/api/terms-and-conditions"],
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get currently active terms
  const activeTerms = termsVersions.find((terms) => terms.isActive);

  // Create new terms version mutation
  const createTermsMutation = useMutation({
    mutationFn: async (data: InsertTermsAndConditions) => {
      const response = await apiRequest("POST", "/api/terms-and-conditions", {
        body: data,
      });
      return response.json() as Promise<TermsAndConditions>;
    },
    onSuccess: (newTerms) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/terms-and-conditions"],
      });
      setIsCreateDialogOpen(false);
      toast({
        title: "Terms & Conditions Created!",
        description: `Version ${newTerms.version} has been created successfully.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error creating terms",
        description: error.message || "Failed to create new terms version",
        variant: "destructive",
      });
    },
  });

  // Activate terms version mutation
  const activateTermsMutation = useMutation({
    mutationFn: async (termsId: number) => {
      const response = await apiRequest(
        "PUT",
        `/api/terms-and-conditions/${termsId}/activate`
      );
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/terms-and-conditions"],
      });
      toast({
        title: "Terms & Conditions Activated!",
        description: `Version ${data.version} is now active.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error activating terms",
        description: error.message || "Failed to activate terms version",
        variant: "destructive",
      });
    },
  });

  // Create terms form setup
  const createForm = useForm<InsertTermsAndConditions>({
    resolver: zodResolver(insertTermsAndConditionsSchema),
    defaultValues: {
      title: "",
      content: "",
      isActive: false,
    },
  });

  const handleCreateTerms = (data: InsertTermsAndConditions) => {
    createTermsMutation.mutate(data);
  };

  const handleActivateTerms = (termsId: number) => {
    activateTermsMutation.mutate(termsId);
  };

  const handleViewTerms = (terms: TermsAndConditions) => {
    setSelectedTerms(terms);
    setShowViewDialog(true);
  };

  // Loading state
  if (termsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-64 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
          </div>
          <div className="h-10 bg-gray-200 rounded w-40 animate-pulse"></div>
        </div>

        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="border rounded-lg p-6">
              <div className="h-6 bg-gray-200 rounded w-48 animate-pulse mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-64 animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (termsError) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error Loading Terms & Conditions</AlertTitle>
          <AlertDescription>
            Failed to load terms and conditions data. Please try refreshing the
            page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Terms & Conditions Management</h2>
          <p className="text-gray-600">
            Manage platform terms and conditions versions
          </p>
        </div>
        <Button
          className="primary-gradient"
          data-testid="button-create-terms"
          onClick={() => setIsCreateDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create New Version
        </Button>
      </div>

      {/* Current Active Terms */}
      {activeTerms ? (
        <Card>
          <CardHeader>
            <CardTitle>Current Active Terms</CardTitle>
            <CardDescription>
              The currently active terms and conditions displayed to users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div>
                  <h3 className="font-medium text-green-800 dark:text-green-200">
                    Version {activeTerms.version}
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Active since{" "}
                    {format(new Date(activeTerms.createdAt), "PPP")}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    Title: {activeTerms.title}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Badge variant="default" className="bg-green-600">
                    Active
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="button-view-active-terms"
                    onClick={() => handleViewTerms(activeTerms)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>No Active Terms</AlertTitle>
          <AlertDescription>
            There are currently no active terms and conditions. Create a new
            version to get started.
          </AlertDescription>
        </Alert>
      )}

      {/* Terms & Conditions History */}
      <Card>
        <CardHeader>
          <CardTitle>Version History</CardTitle>
          <CardDescription>
            All versions of terms and conditions including drafts and archived
            versions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {termsVersions.length > 0 ? (
            <Table data-testid="table-terms-history">
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {termsVersions.map((terms) => (
                  <TableRow key={terms.id}>
                    <TableCell className="font-medium">
                      {terms.version}
                    </TableCell>
                    <TableCell>{terms.title}</TableCell>
                    <TableCell>
                      {terms.isActive ? (
                        <Badge variant="default" className="bg-green-600">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Archived</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {format(new Date(terms.createdAt), "PPP")}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`button-view-terms-${terms.version}`}
                          onClick={() => handleViewTerms(terms)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        {!terms.isActive && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`button-activate-terms-${terms.version}`}
                            onClick={() => handleActivateTerms(terms.id)}
                            disabled={activateTermsMutation.isPending}
                            className="text-green-600 border-green-600 hover:bg-green-50"
                          >
                            {activateTermsMutation.isPending ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent mr-1" />
                            ) : (
                              <CheckCircle className="h-4 w-4 mr-1" />
                            )}
                            Activate
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No terms and conditions versions found.</p>
              <p className="text-sm">
                Create your first version to get started.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Terms Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Total Versions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{termsVersions.length}</div>
            <p className="text-xs text-gray-500 mt-1">Including archived</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Active Version
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {activeTerms?.version || "None"}
            </div>
            <p className="text-xs text-gray-500 mt-1">Currently displayed</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Last Updated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {activeTerms
                ? format(new Date(activeTerms.createdAt), "MMM d")
                : "N/A"}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {activeTerms
                ? format(new Date(activeTerms.createdAt), "yyyy")
                : "No active version"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Create New Terms Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Terms & Conditions Version</DialogTitle>
            <DialogDescription>
              Create a new version of the terms and conditions. The version
              number will be automatically generated.
            </DialogDescription>
          </DialogHeader>

          <Form {...createForm}>
            <form
              onSubmit={createForm.handleSubmit(handleCreateTerms)}
              className="space-y-6"
            >
              <FormField
                control={createForm.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter terms and conditions title"
                        data-testid="input-terms-title"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={createForm.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter the full terms and conditions content..."
                        className="min-h-[400px] resize-y"
                        data-testid="textarea-terms-content"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                  data-testid="button-cancel-create-terms"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createTermsMutation.isPending}
                  data-testid="button-save-terms"
                  className="primary-gradient"
                >
                  {createTermsMutation.isPending ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Create Version
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* View Terms Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Terms & Conditions - Version {selectedTerms?.version}
              {selectedTerms?.isActive && (
                <Badge className="ml-2 bg-green-600">Active</Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Created on{" "}
              {selectedTerms
                ? format(new Date(selectedTerms.createdAt), "PPP")
                : ""}
            </DialogDescription>
          </DialogHeader>

          {selectedTerms && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">
                  {selectedTerms.title}
                </h3>
                <div className="prose max-w-none bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border">
                  <div className="whitespace-pre-wrap text-sm">
                    {selectedTerms.content}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-4">
                <Button
                  variant="outline"
                  onClick={() => setShowViewDialog(false)}
                  data-testid="button-close-view-terms"
                >
                  Close
                </Button>
                {!selectedTerms.isActive && (
                  <Button
                    onClick={() => {
                      handleActivateTerms(selectedTerms.id);
                      setShowViewDialog(false);
                    }}
                    disabled={activateTermsMutation.isPending}
                    className="text-green-600 border-green-600 hover:bg-green-50"
                    variant="outline"
                    data-testid="button-activate-from-view"
                  >
                    {activateTermsMutation.isPending ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent mr-2" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Activate This Version
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, signOut } = useAuth();

  // Initialize admin cache management
  const { clearAdminCache } = useAdminCacheManager();

  // Inline Artifact Viewer Component (Claude-style)
  function InlineArtifactViewer({
    artifact,
    onOpenFullscreen,
    onEdit,
    showActions = true,
  }: {
    artifact: any;
    onOpenFullscreen?: () => void;
    onEdit?: () => void;
    showActions?: boolean;
  }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const getLanguageLabel = (type: string, language?: string) => {
      if (language) return language.toUpperCase();
      switch (type) {
        case "html":
          return "HTML";
        case "css":
          return "CSS";
        case "javascript":
          return "JavaScript";
        case "typescript":
          return "TypeScript";
        case "python":
          return "Python";
        case "sql":
          return "SQL";
        case "json":
          return "JSON";
        case "markdown":
          return "Markdown";
        default:
          return "Code";
      }
    };

    const getTypeIcon = (type: string) => {
      switch (type) {
        case "html":
          return <FileCode className="h-4 w-4 text-orange-500" />;
        case "css":
          return <FileCode className="h-4 w-4 text-blue-500" />;
        case "javascript":
        case "typescript":
          return <FileCode className="h-4 w-4 text-yellow-500" />;
        case "python":
          return <FileCode className="h-4 w-4 text-green-500" />;
        case "sql":
          return <FileCode className="h-4 w-4 text-purple-500" />;
        case "json":
          return <FileCode className="h-4 w-4 text-gray-500" />;
        case "markdown":
          return <FileText className="h-4 w-4 text-blue-600" />;
        default:
          return <FileCode className="h-4 w-4 text-gray-400" />;
      }
    };

    const canPreview = (type: string) => {
      return ["html", "css", "javascript"].includes(type.toLowerCase());
    };

    const renderPreview = () => {
      if (!canPreview(artifact.type) || !showPreview) return null;

      let content = artifact.content;
      if (artifact.type === "html") {
        // For HTML, create a simple preview
        return (
          <div className="border rounded p-4 bg-white">
            <iframe
              srcDoc={content}
              className="w-full h-48 border-0"
              sandbox="allow-scripts"
              title="HTML Preview"
            />
          </div>
        );
      }

      return null;
    };

    const copyToClipboard = () => {
      navigator.clipboard.writeText(artifact.content);
      toast({
        title: "Copied!",
        description: "Artifact content copied to clipboard",
      });
    };

    return (
      <div className="border rounded-lg bg-card my-3 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b bg-muted/30">
          <div className="flex items-center gap-2">
            {getTypeIcon(artifact.type)}
            <span className="font-medium text-sm">{artifact.title}</span>
            <Badge variant="secondary" className="text-xs">
              {getLanguageLabel(artifact.type, artifact.language)}
            </Badge>
          </div>
          {showActions && (
            <div className="flex items-center gap-1">
              {canPreview(artifact.type) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowPreview(!showPreview)}
                  data-testid="button-toggle-preview"
                  className="h-7 px-2 text-xs"
                >
                  {showPreview ? (
                    <EyeOff className="h-3 w-3" />
                  ) : (
                    <Eye className="h-3 w-3" />
                  )}
                  {showPreview ? "Hide" : "Preview"}
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={copyToClipboard}
                data-testid="button-copy-inline"
                className="h-7 px-2 text-xs"
              >
                <Copy className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsExpanded(!isExpanded)}
                data-testid="button-expand-code"
                className="h-7 px-2 text-xs"
              >
                {isExpanded ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </Button>
              {onOpenFullscreen && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onOpenFullscreen}
                  data-testid="button-fullscreen-inline"
                  className="h-7 px-2 text-xs"
                >
                  <Maximize className="h-3 w-3" />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Preview Section */}
        {showPreview && renderPreview()}

        {/* Code Content */}
        <div
          className={`transition-all duration-200 ${
            isExpanded ? "max-h-none" : "max-h-32"
          } overflow-hidden`}
        >
          <pre className="bg-muted/20 p-3 text-xs font-mono overflow-x-auto">
            <code className="text-foreground/90">{artifact.content}</code>
          </pre>
        </div>

        {/* Footer with metadata */}
        {artifact.description && (
          <div className="px-3 py-2 text-xs text-muted-foreground border-t">
            {artifact.description}
          </div>
        )}

        {/* Expand overlay for truncated content */}
        {!isExpanded && artifact.content.length > 200 && (
          <div className="relative">
            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-muted/20 to-transparent" />
            <div className="absolute bottom-1 right-3">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsExpanded(true)}
                className="h-6 px-2 text-xs bg-background/80 backdrop-blur-sm"
              >
                Show more
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Artifact Viewer/Editor Component
  function ArtifactViewer({
    artifactId,
    isOpen,
    onClose,
    conversationId,
  }: {
    artifactId: number | null;
    isOpen: boolean;
    onClose: () => void;
    conversationId: number;
  }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editedTitle, setEditedTitle] = useState("");
    const [editedContent, setEditedContent] = useState("");
    const [editedDescription, setEditedDescription] = useState("");

    // Fetch artifact details
    const { data: artifact, isLoading } = useQuery({
      queryKey: ["/api/ai/artifacts", artifactId],
      queryFn: async () => {
        const response = await apiRequest(
          "GET",
          `/api/ai/artifacts/single/${artifactId}`
        );
        return response.json();
      },
      enabled: artifactId !== null && isOpen,
    });

    // Update artifact mutation
    const updateArtifactMutation = useMutation({
      mutationFn: async ({
        title,
        description,
        content,
      }: {
        title: string;
        description?: string;
        content: string;
      }) => {
        const response = await apiRequest(
          "PUT",
          `/api/ai/artifacts/${artifactId}`,
          {
            body: {
              title,
              description,
              content,
            },
          }
        );
        return response.json();
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["/api/ai/artifacts", conversationId],
        });
        queryClient.invalidateQueries({
          queryKey: ["/api/ai/artifacts", artifactId],
        });
        setIsEditing(false);
        toast({
          title: "Artifact Updated",
          description: "Your changes have been saved successfully!",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Update Failed",
          description: error?.message || "Failed to update artifact",
          variant: "destructive",
        });
      },
    });

    // Delete artifact mutation
    const deleteArtifactMutation = useMutation({
      mutationFn: async () => {
        const response = await apiRequest(
          "DELETE",
          `/api/ai/artifacts/${artifactId}`
        );
        return response.json();
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["/api/ai/artifacts", conversationId],
        });
        onClose();
        toast({
          title: "Artifact Deleted",
          description: "Artifact has been deleted successfully!",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Delete Failed",
          description: error?.message || "Failed to delete artifact",
          variant: "destructive",
        });
      },
    });

    // Reset editing state when artifact changes
    useEffect(() => {
      if (artifact) {
        setEditedTitle(artifact.title);
        setEditedContent(artifact.content);
        setEditedDescription(artifact.description || "");
        setIsEditing(false);
      }
    }, [artifact]);

    const handleSave = () => {
      if (!editedTitle.trim() || !editedContent.trim()) {
        toast({
          title: "Validation Error",
          description: "Title and content are required",
          variant: "destructive",
        });
        return;
      }

      updateArtifactMutation.mutate({
        title: editedTitle.trim(),
        description: editedDescription.trim() || undefined,
        content: editedContent.trim(),
      });
    };

    const handleDelete = () => {
      if (
        window.confirm(
          "Are you sure you want to delete this artifact? This action cannot be undone."
        )
      ) {
        deleteArtifactMutation.mutate();
      }
    };

    const getLanguageLabel = (type: string, language?: string) => {
      if (language) return language.toUpperCase();
      switch (type) {
        case "html":
          return "HTML";
        case "css":
          return "CSS";
        case "javascript":
          return "JavaScript";
        case "typescript":
          return "TypeScript";
        case "python":
          return "Python";
        case "sql":
          return "SQL";
        case "json":
          return "JSON";
        case "markdown":
          return "Markdown";
        default:
          return "Code";
      }
    };

    if (!isOpen || !artifactId) return null;

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileCode className="h-5 w-5" />
                {isEditing ? (
                  <Input
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="text-lg font-semibold"
                    data-testid="input-artifact-title"
                  />
                ) : (
                  <DialogTitle className="text-lg">
                    {artifact?.title}
                  </DialogTitle>
                )}
                {artifact && (
                  <Badge variant="secondary">
                    {getLanguageLabel(artifact.type, artifact.language)}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={updateArtifactMutation.isPending}
                      data-testid="button-save-artifact"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditedTitle(artifact?.title || "");
                        setEditedContent(artifact?.content || "");
                        setEditedDescription(artifact?.description || "");
                        setIsEditing(false);
                      }}
                      data-testid="button-cancel-edit"
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIsEditing(true)}
                      data-testid="button-edit-artifact"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleDelete}
                      disabled={deleteArtifactMutation.isPending}
                      data-testid="button-delete-artifact"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
            {isEditing && (
              <div className="mt-2">
                <Input
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  placeholder="Description (optional)"
                  data-testid="input-artifact-description"
                />
              </div>
            )}
            {!isEditing && artifact?.description && (
              <DialogDescription>{artifact.description}</DialogDescription>
            )}
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">Loading artifact...</div>
              </div>
            ) : artifact ? (
              <div className="h-full">
                {isEditing ? (
                  <Textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="font-mono text-sm h-96 resize-none"
                    placeholder="Artifact content..."
                    data-testid="textarea-artifact-content"
                  />
                ) : (
                  <ScrollArea className="h-96 w-full">
                    <pre className="bg-muted p-4 rounded-lg font-mono text-sm overflow-x-auto">
                      <code>{artifact.content}</code>
                    </pre>
                  </ScrollArea>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">Artifact not found</div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {artifact && (
                <>
                  Created: {new Date(artifact.createdAt).toLocaleString()}
                  {artifact.updatedAt !== artifact.createdAt && (
                    <>
                      {" "}
                      • Updated: {new Date(artifact.updatedAt).toLocaleString()}
                    </>
                  )}
                </>
              )}
            </div>
            {artifact?.content && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(artifact.content);
                  toast({
                    title: "Copied!",
                    description: "Artifact content copied to clipboard",
                  });
                }}
                data-testid="button-copy-artifact"
              >
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // WAIN Chat Interface Component (embedded directly in admin dashboard)
  function WainChatInterface() {
    const [selectedConversationId, setSelectedConversationId] = useState<
      number | null
    >(null);
    const [messageInput, setMessageInput] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [isExecutingQuery, setIsExecutingQuery] = useState(false);
    const [selectedArtifactId, setSelectedArtifactId] = useState<number | null>(
      null
    );
    const [showArtifactViewer, setShowArtifactViewer] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Fetch artifacts for the current conversation
    const { data: artifacts = [] } = useQuery({
      queryKey: ["/api/ai/artifacts", selectedConversationId],
      enabled: selectedConversationId !== null,
    });

    // Fetch conversations for the current user
    const { data: conversations = [], isLoading: conversationsLoading } =
      useQuery<AiConversation[]>({
        queryKey: ["/api/ai/conversations"],
      });

    // Fetch messages for selected conversation
    const { data: messages = [], isLoading: messagesLoading } = useQuery<
      AiMessage[]
    >({
      queryKey: ["/api/ai/messages", selectedConversationId],
      enabled: selectedConversationId !== null,
    });

    // Create new conversation mutation
    const createConversationMutation = useMutation({
      mutationFn: async (title: string) => {
        const response = await apiRequest("POST", "/api/ai/conversations", {
          body: { title },
        });
        return response.json() as Promise<AiConversation>;
      },
      onSuccess: (newConversation: AiConversation) => {
        queryClient.invalidateQueries({ queryKey: ["/api/ai/conversations"] });
        setSelectedConversationId(newConversation.id);
      },
    });

    // Send message mutation
    const sendMessageMutation = useMutation({
      mutationFn: async ({
        conversationId,
        content,
        role,
      }: {
        conversationId: number;
        content: string;
        role: string;
      }) => {
        const response = await apiRequest("POST", "/api/ai/messages", {
          body: { conversationId, content, role },
        });
        return response.json() as Promise<AiMessage>;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["/api/ai/messages", selectedConversationId],
        });
        queryClient.invalidateQueries({ queryKey: ["/api/ai/conversations"] });
      },
    });

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Artifact detection and creation functions
    const detectPotentialArtifacts = (
      content: string
    ): Array<{
      type: string;
      title: string;
      content: string;
      language?: string;
    }> => {
      const artifacts: Array<{
        type: string;
        title: string;
        content: string;
        language?: string;
      }> = [];

      // Detect code blocks
      const codeBlockRegex = /```(\w+)?\s*\n([\s\S]*?)\n```/g;
      let match;
      let codeBlockIndex = 1;

      while ((match = codeBlockRegex.exec(content)) !== null) {
        const language = match[1] || "text";
        const code = match[2].trim();

        if (code.length > 50) {
          // Only suggest artifacts for substantial code blocks
          let type = "code";
          let title = `Code Block ${codeBlockIndex}`;

          // Determine artifact type and title based on language and content
          if (language === "html" || language === "htm") {
            type = "html";
            title = code.includes("<!DOCTYPE")
              ? "HTML Document"
              : "HTML Snippet";
          } else if (language === "css") {
            type = "css";
            title = "CSS Styles";
          } else if (language === "javascript" || language === "js") {
            type = "javascript";
            title = "JavaScript Code";
          } else if (language === "typescript" || language === "ts") {
            type = "typescript";
            title = "TypeScript Code";
          } else if (language === "python" || language === "py") {
            type = "python";
            title = "Python Script";
          } else if (language === "sql") {
            type = "sql";
            title = "SQL Query";
          } else if (language === "json") {
            type = "json";
            title = "JSON Data";
          } else if (language === "markdown" || language === "md") {
            type = "markdown";
            title = "Markdown Document";
          }

          artifacts.push({
            type,
            title,
            content: code,
            language,
          });

          codeBlockIndex++;
        }
      }

      // Detect structured content that could be reports/documents
      if (
        content.includes("# ") ||
        content.includes("## ") ||
        content.includes("### ")
      ) {
        const wordCount = content.split(/\s+/).length;
        if (wordCount > 100 && !artifacts.some((a) => a.type === "markdown")) {
          artifacts.push({
            type: "markdown",
            title: "Generated Report",
            content: content,
          });
        }
      }

      return artifacts;
    };

    const createArtifactMutation = useMutation({
      mutationFn: async ({
        conversationId,
        messageId,
        title,
        description,
        type,
        language,
        content,
        isPublic,
      }: {
        conversationId: number;
        messageId?: number;
        title: string;
        description?: string;
        type: string;
        language?: string;
        content: string;
        isPublic?: boolean;
      }) => {
        const response = await apiRequest("POST", "/api/ai/artifacts", {
          body: {
            conversationId,
            messageId,
            title,
            description,
            type,
            language,
            content,
            isPublic: isPublic || false,
          },
        });
        return response.json();
      },
      onSuccess: () => {
        toast({
          title: "Artifact Created",
          description: "Your artifact has been saved successfully!",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to Create Artifact",
          description: error?.message || "Something went wrong",
          variant: "destructive",
        });
      },
    });

    // Query detection and execution functions
    const detectWainQuery = (
      content: string
    ): { hasQuery: boolean; queryData?: any; textContent: string } => {
      const queryPattern = /\[WAIN_QUERY\]([\s\S]*?)\[\/WAIN_QUERY\]/;
      const match = content.match(queryPattern);

      if (match) {
        try {
          const queryData = JSON.parse(match[1].trim());
          const textContent = content.replace(queryPattern, "").trim();
          return { hasQuery: true, queryData, textContent };
        } catch (error) {
          console.error("Failed to parse WAIN query:", error);
          return { hasQuery: false, textContent: content };
        }
      }

      return { hasQuery: false, textContent: content };
    };

    const executeWainQuery = async (queryData: any): Promise<any> => {
      try {
        const response = await apiRequest("POST", "/api/admin/wain/query", {
          body: {
            queryId: queryData.queryId,
            parameters: queryData.parameters || {},
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Query execution failed");
        }

        return await response.json();
      } catch (error) {
        console.error("Query execution error:", error);
        throw error;
      }
    };

    const formatQueryResults = (queryResult: any, queryData: any): string => {
      if (!queryResult.success || !queryResult.data) {
        return `❌ Query execution failed: ${
          queryResult.error || "Unknown error"
        }`;
      }

      const { data, metadata } = queryResult;
      const rowCount = data.length;
      const executionTime = metadata?.executionTimeMs || 0;

      let formatted = `\n\n📊 **Query Results** (${queryData.queryId})\n`;
      formatted += `• Found ${rowCount} records in ${executionTime}ms\n`;

      if (queryData.reasoning) {
        formatted += `• ${queryData.reasoning}\n`;
      }

      formatted += "\n";

      if (rowCount === 0) {
        formatted += "No data found for the specified criteria.";
        return formatted;
      }

      // Format based on query type
      if (queryData.queryId === "analytics.kpis") {
        const kpis = data[0];
        formatted += `**Key Performance Indicators:**\n`;
        formatted += `• Total Users: ${
          kpis.total_users?.toLocaleString() || "N/A"
        }\n`;
        formatted += `• Total Competitions: ${
          kpis.total_competitions?.toLocaleString() || "N/A"
        }\n`;
        formatted += `• Total Entries: ${
          kpis.total_entries?.toLocaleString() || "N/A"
        }\n`;
        formatted += `• Total Shares: ${
          kpis.total_shares?.toLocaleString() || "N/A"
        }\n`;
        formatted += `• Unique Participants: ${
          kpis.unique_participants?.toLocaleString() || "N/A"
        }\n`;
        formatted += `• Avg Entries per User: ${
          kpis.avg_entries_per_user
            ? Number(kpis.avg_entries_per_user).toFixed(2)
            : "N/A"
        }\n`;
      } else if (queryData.queryId === "competitions.top_by_entries") {
        formatted += `**Top Competitions by Entry Count:**\n`;
        data.slice(0, 10).forEach((comp: any, index: number) => {
          formatted += `${index + 1}. **${comp.title}** (${comp.category})\n`;
          formatted += `   • Entries: ${
            comp.entry_count?.toLocaleString() || 0
          } from ${comp.unique_users?.toLocaleString() || 0} users\n`;
          formatted += `   • Period: ${
            comp.start_date
              ? new Date(comp.start_date).toLocaleDateString()
              : "N/A"
          } - ${
            comp.end_date ? new Date(comp.end_date).toLocaleDateString() : "N/A"
          }\n`;
        });
      } else if (queryData.queryId === "users.active_counts_by_day") {
        formatted += `**Daily Active User Counts:**\n`;
        data.slice(0, 10).forEach((day: any) => {
          formatted += `• ${day.date}: ${
            day.active_users?.toLocaleString() || 0
          } users\n`;
        });
      } else if (queryData.queryId === "shares.by_platform") {
        formatted += `**Social Media Shares by Platform:**\n`;
        data.forEach((platform: any) => {
          formatted += `• **${platform.platform}**: ${
            platform.total_shares?.toLocaleString() || 0
          } shares\n`;
        });
      } else if (queryData.queryId === "winners.recent") {
        formatted += `**Recent Winners:**\n`;
        data.slice(0, 10).forEach((winner: any, index: number) => {
          formatted += `${index + 1}. **${winner.competition_title}**\n`;
          formatted += `   • Winner: User #${winner.user_id}\n`;
          formatted += `   • Date: ${
            winner.created_at
              ? new Date(winner.created_at).toLocaleDateString()
              : "N/A"
          }\n`;
        });
      } else {
        // Generic table format for other queries
        if (data.length > 0) {
          const keys = Object.keys(data[0]);
          formatted += `**Data Table:**\n`;
          data.slice(0, 10).forEach((row: any, index: number) => {
            formatted += `${index + 1}. `;
            keys.slice(0, 3).forEach((key) => {
              formatted += `${key}: ${row[key]} | `;
            });
            formatted += "\n";
          });
        }
      }

      if (rowCount > 10) {
        formatted += `\n... and ${rowCount - 10} more records`;
      }

      return formatted;
    };

    const handleNewChat = () => {
      const title = `Chat ${new Date().toLocaleString()}`;
      createConversationMutation.mutate(title);
    };

    const handleSendMessage = async () => {
      if (!messageInput.trim() || !selectedConversationId || isGenerating)
        return;

      const userMessage = messageInput.trim();
      setMessageInput("");
      setIsGenerating(true);

      try {
        // Send user message
        await sendMessageMutation.mutateAsync({
          conversationId: selectedConversationId,
          content: userMessage,
          role: "user",
        });

        // Send to AI and get response
        const response = await apiRequest("POST", "/api/ai/chat", {
          body: {
            conversationId: selectedConversationId,
            message: userMessage,
          },
        });
        const aiResponse = (await response.json()) as { content: string };

        // Check for WAIN queries in the response
        const { hasQuery, queryData, textContent } = detectWainQuery(
          aiResponse.content
        );
        let finalContent = textContent;

        if (hasQuery && queryData) {
          setIsExecutingQuery(true);
          try {
            console.log("Executing WAIN query:", queryData);
            const queryResult = await executeWainQuery(queryData);
            const formattedResults = formatQueryResults(queryResult, queryData);
            finalContent = textContent + formattedResults;
            console.log("Query executed successfully:", queryResult);
          } catch (queryError) {
            console.error("Query execution failed:", queryError);
            finalContent =
              textContent +
              `\n\n❌ **Query Error**: ${
                queryError instanceof Error
                  ? queryError.message
                  : "Failed to execute database query"
              }`;
          } finally {
            setIsExecutingQuery(false);
          }
        }

        // Save AI response (with query results if applicable)
        await sendMessageMutation.mutateAsync({
          conversationId: selectedConversationId,
          content: finalContent,
          role: "assistant",
        });
      } catch (error) {
        console.error("Failed to send message:", error);
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsGenerating(false);
        setIsExecutingQuery(false);
      }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    };

    const suggestedPrompts = [
      "Show me our key performance indicators",
      "What are our top competitions by entries?",
      "How many active users do we have this month?",
      "Show me recent winners and their competitions",
      "Which social media platform drives the most shares?",
      "What's our referral conversion funnel like?",
      "Help me write a professional email",
      "Explain complex topics in simple terms",
    ];

    const handlePromptClick = (prompt: string) => {
      setMessageInput(prompt);
    };

    const handleStartNewChat = async (initialMessage?: string) => {
      try {
        // Create new conversation
        const title = `Chat ${new Date().toLocaleString()}`;
        const newConversation = await createConversationMutation.mutateAsync(
          title
        );
        setSelectedConversationId(newConversation.id);

        // If there's an initial message, send it immediately (Claude-style behavior)
        if (initialMessage?.trim()) {
          setMessageInput(""); // Clear input immediately for responsive feel
          setIsGenerating(true);

          try {
            // Send user message
            await sendMessageMutation.mutateAsync({
              conversationId: newConversation.id,
              content: initialMessage.trim(),
              role: "user",
            });

            // Send to AI and get response
            const response = await apiRequest("POST", "/api/ai/chat", {
              body: {
                conversationId: newConversation.id,
                message: initialMessage.trim(),
              },
            });
            const aiResponse = (await response.json()) as { content: string };

            // Check for WAIN queries in the response
            const { hasQuery, queryData, textContent } = detectWainQuery(
              aiResponse.content
            );
            let finalContent = textContent;

            if (hasQuery && queryData) {
              setIsExecutingQuery(true);
              try {
                console.log("Executing WAIN query in new chat:", queryData);
                const queryResult = await executeWainQuery(queryData);
                const formattedResults = formatQueryResults(
                  queryResult,
                  queryData
                );
                finalContent = textContent + formattedResults;
                console.log(
                  "Query executed successfully in new chat:",
                  queryResult
                );
              } catch (queryError) {
                console.error(
                  "Query execution failed in new chat:",
                  queryError
                );
                finalContent =
                  textContent +
                  `\n\n❌ **Query Error**: ${
                    queryError instanceof Error
                      ? queryError.message
                      : "Failed to execute database query"
                  }`;
              } finally {
                setIsExecutingQuery(false);
              }
            }

            // Save AI response (with query results if applicable)
            await sendMessageMutation.mutateAsync({
              conversationId: newConversation.id,
              content: finalContent,
              role: "assistant",
            });
          } catch (error) {
            console.error("Failed to send initial message:", error);
            // Even if message sending fails, stay on conversation screen
          } finally {
            setIsGenerating(false);
          }
        }
      } catch (error) {
        console.error("Failed to create conversation:", error);
        setIsGenerating(false);
      }
    };

    // Claude-style home screen when no conversation is selected
    if (!selectedConversationId) {
      return (
        <div className="min-h-[calc(100vh-200px)] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Brain className="h-8 w-8 text-primary" />
              <h2 className="text-3xl font-bold">WAIN AI Assistant</h2>
              <Badge variant="secondary">Powered by Claude</Badge>
            </div>
            {conversations.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setSelectedConversationId(conversations[0]?.id || null)
                }
                data-testid="button-view-conversations"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                View Conversations ({conversations.length})
              </Button>
            )}
          </div>

          {/* Main content area - Claude style */}
          <div className="flex-1 flex flex-col items-center justify-center max-w-4xl mx-auto w-full px-4">
            {/* Welcome message */}
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
                How can I help you today?
              </h1>
              <p className="text-muted-foreground text-lg max-w-2xl">
                I'm WAIN, your AI assistant powered by Claude. I can help with
                writing, analysis, coding, problem-solving, and much more.
              </p>
            </div>

            {/* Large input box - Claude style */}
            <div className="w-full max-w-3xl mb-8">
              <div className="relative">
                <Input
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      !e.shiftKey &&
                      messageInput.trim()
                    ) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleStartNewChat(messageInput.trim());
                    }
                  }}
                  placeholder="Message WAIN..."
                  disabled={
                    isGenerating || createConversationMutation.isPending
                  }
                  data-testid="input-message"
                  className="h-14 text-lg pr-14 rounded-3xl border-2 border-muted focus:border-primary transition-colors"
                />
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (messageInput.trim()) {
                      handleStartNewChat(messageInput.trim());
                    }
                  }}
                  disabled={
                    !messageInput.trim() ||
                    isGenerating ||
                    createConversationMutation.isPending
                  }
                  data-testid="button-send"
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 rounded-full h-10 w-10 p-0"
                >
                  {createConversationMutation.isPending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Suggested prompts - Claude/ChatGPT style */}
            <div className="w-full max-w-4xl">
              <h3 className="text-lg font-semibold mb-4 text-center">
                Try asking me about...
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {suggestedPrompts.map((prompt, index) => (
                  <Card
                    key={index}
                    className="cursor-pointer hover:bg-muted/50 transition-colors border-dashed"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleStartNewChat(prompt);
                    }}
                    data-testid={`prompt-suggestion-${index}`}
                  >
                    <CardContent className="p-4">
                      <p className="text-sm text-center">{prompt}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Footer info */}
            <div className="mt-12 text-center text-sm text-muted-foreground">
              <p>
                WAIN can make mistakes. Consider checking important information.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Chat interface when conversation is selected
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedConversationId(null)}
              data-testid="button-back-home"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Brain className="h-6 w-6 text-primary" />
            <h2 className="text-xl font-bold">WAIN</h2>
            <Badge variant="secondary">Claude</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleNewChat}
              disabled={createConversationMutation.isPending}
              data-testid="button-new-chat"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Chat
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-300px)]">
          {/* Conversations Sidebar */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Conversations</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-400px)]">
                {conversationsLoading ? (
                  <div className="p-4 text-center text-muted-foreground">
                    Loading...
                  </div>
                ) : (
                  conversations.map((conversation: AiConversation) => (
                    <div
                      key={conversation.id}
                      className={`p-3 cursor-pointer hover:bg-muted/50 border-b ${
                        selectedConversationId === conversation.id
                          ? "bg-muted"
                          : ""
                      }`}
                      onClick={() => setSelectedConversationId(conversation.id)}
                      data-testid={`conversation-${conversation.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <MessageCircle className="h-4 w-4 mt-1 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">
                            {conversation.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(
                              conversation.createdAt
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Chat Area */}
          <Card className="lg:col-span-3">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {conversations.find((c) => c.id === selectedConversationId)
                    ?.title || "Chat"}
                </CardTitle>
                {(artifacts as any[])?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {(artifacts as any[])?.length} artifact
                      {(artifacts as any[])?.length !== 1 ? "s" : ""}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        // Show first artifact or a list if multiple
                        if ((artifacts as any[])?.length === 1) {
                          setSelectedArtifactId((artifacts as any[])[0].id);
                          setShowArtifactViewer(true);
                        } else {
                          // For now, show the first one - could enhance with a dropdown
                          setSelectedArtifactId((artifacts as any[])[0].id);
                          setShowArtifactViewer(true);
                        }
                      }}
                      data-testid="button-view-artifacts"
                    >
                      <FileCode className="h-4 w-4 mr-1" />
                      View Artifacts
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0 flex flex-col h-[calc(100vh-420px)]">
              <ScrollArea className="flex-1 p-4">
                {messagesLoading ? (
                  <div className="text-center text-muted-foreground">
                    Loading messages...
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-muted-foreground">
                    Start the conversation!
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message: AiMessage) => {
                      const potentialArtifacts =
                        message.role === "assistant"
                          ? detectPotentialArtifacts(message.content)
                          : [];

                      return (
                        <div
                          key={message.id}
                          className={`flex ${
                            message.role === "user"
                              ? "justify-end"
                              : "justify-start"
                          }`}
                          data-testid={`message-${message.id}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg p-3 ${
                              message.role === "user"
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted"
                            }`}
                          >
                            <div className="whitespace-pre-wrap text-sm">
                              {message.content}
                            </div>

                            {/* Artifact creation UI for assistant messages */}
                            {message.role === "assistant" &&
                              potentialArtifacts.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-muted-foreground/20">
                                  <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                                    <FileCode className="h-3 w-3" />
                                    {potentialArtifacts.length === 1
                                      ? "Create artifact:"
                                      : "Create artifacts:"}
                                  </div>
                                  <div className="space-y-2">
                                    {potentialArtifacts.map(
                                      (artifact, index) => (
                                        <Button
                                          key={index}
                                          variant="outline"
                                          size="sm"
                                          onClick={async () => {
                                            try {
                                              const newArtifact =
                                                await createArtifactMutation.mutateAsync(
                                                  {
                                                    conversationId:
                                                      selectedConversationId!,
                                                    messageId: message.id,
                                                    title: artifact.title,
                                                    type: artifact.type,
                                                    language: artifact.language,
                                                    content: artifact.content,
                                                    description: `Generated from AI response`,
                                                  }
                                                );
                                              // Open the artifact viewer after creation
                                              setSelectedArtifactId(
                                                newArtifact.id
                                              );
                                              setShowArtifactViewer(true);
                                            } catch (error) {
                                              // Error handling is in the mutation
                                            }
                                          }}
                                          disabled={
                                            createArtifactMutation.isPending
                                          }
                                          data-testid={`button-create-artifact-${index}`}
                                          className="text-xs h-7 bg-background hover:bg-muted"
                                        >
                                          <Plus className="h-3 w-3 mr-1" />
                                          {artifact.title}
                                          {artifact.language && (
                                            <Badge
                                              variant="secondary"
                                              className="ml-2 text-xs px-1 py-0"
                                            >
                                              {artifact.language}
                                            </Badge>
                                          )}
                                        </Button>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}

                            {/* Inline artifact display for existing artifacts - Claude-style */}
                            {message.role === "assistant" &&
                              artifacts &&
                              (artifacts as any[])?.filter(
                                (artifact: any) =>
                                  artifact.messageId === message.id
                              ).length > 0 && (
                                <div className="mt-3 space-y-3">
                                  {(artifacts as any[])
                                    ?.filter(
                                      (artifact: any) =>
                                        artifact.messageId === message.id
                                    )
                                    .map((artifact: any) => (
                                      <div
                                        key={artifact.id}
                                        className="border border-gray-200 rounded-lg overflow-hidden bg-white"
                                        data-testid={`inline-artifact-${artifact.id}`}
                                      >
                                        {/* Artifact header */}
                                        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            <div className="flex items-center gap-1">
                                              <FileCode className="h-4 w-4 text-gray-600" />
                                              <span className="text-sm font-medium text-gray-900">
                                                {artifact.title}
                                              </span>
                                            </div>
                                            {artifact.language && (
                                              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                                {artifact.language}
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-6 w-6 p-0"
                                              onClick={() => {
                                                navigator.clipboard.writeText(
                                                  artifact.content
                                                );
                                                toast({
                                                  description:
                                                    "Copied to clipboard!",
                                                });
                                              }}
                                              title="Copy to clipboard"
                                              data-testid={`button-copy-${artifact.id}`}
                                            >
                                              <Copy className="h-3 w-3" />
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-6 w-6 p-0"
                                              onClick={() => {
                                                setSelectedArtifactId(
                                                  artifact.id
                                                );
                                                setShowArtifactViewer(true);
                                              }}
                                              title="Open fullscreen"
                                              data-testid={`button-fullscreen-${artifact.id}`}
                                            >
                                              <Maximize className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        </div>

                                        {/* Artifact content preview */}
                                        <div className="p-3">
                                          {artifact.type === "html" &&
                                            artifact.content.includes("<") && (
                                              <div className="space-y-2">
                                                <div className="text-xs text-gray-600 font-medium">
                                                  Live Preview:
                                                </div>
                                                <iframe
                                                  srcDoc={artifact.content}
                                                  className="w-full h-32 border border-gray-200 rounded"
                                                  sandbox="allow-scripts"
                                                  referrerPolicy="no-referrer"
                                                  title={`Preview of ${artifact.title}`}
                                                />
                                              </div>
                                            )}
                                          <div className="mt-2">
                                            <pre className="text-xs text-gray-800 bg-gray-50 p-2 rounded border overflow-x-auto max-h-40 overflow-y-auto font-mono whitespace-pre-wrap break-words">
                                              {artifact.content.length > 500
                                                ? artifact.content.substring(
                                                    0,
                                                    500
                                                  ) + "..."
                                                : artifact.content}
                                            </pre>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              )}

                            <div className="mt-1 text-xs opacity-70">
                              {new Date(message.createdAt).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {isGenerating && (
                      <div className="flex justify-start">
                        <div className="bg-muted rounded-lg p-3 max-w-[80%]">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Brain className="h-4 w-4 animate-pulse" />
                            WAIN is thinking...
                          </div>
                        </div>
                      </div>
                    )}
                    {isExecutingQuery && (
                      <div className="flex justify-start">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 max-w-[80%]">
                          <div className="flex items-center gap-2 text-sm text-blue-700">
                            <BarChart3 className="h-4 w-4 animate-spin" />
                            Querying database for insights...
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>
              <Separator />
              <div className="p-4">
                <div className="flex gap-2">
                  <Input
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Message WAIN..."
                    disabled={isGenerating}
                    data-testid="input-message"
                    className="flex-1"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim() || isGenerating}
                    data-testid="button-send"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Artifact Viewer Modal */}
        {selectedConversationId && (
          <ArtifactViewer
            artifactId={selectedArtifactId}
            isOpen={showArtifactViewer}
            onClose={() => {
              setShowArtifactViewer(false);
              setSelectedArtifactId(null);
            }}
            conversationId={selectedConversationId}
          />
        )}
      </div>
    );
  }

  // Direct admin authentication check - bypasses Supabase session issues
  const {
    data: adminAuth,
    isLoading: adminAuthLoading,
    error: adminAuthError,
  } = useQuery<User>({
    queryKey: ["/api/auth/user"],
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Check authentication from either Supabase auth or direct admin auth
  const isAuthenticated = !!user || !!adminAuth;
  const currentUser = user || adminAuth;
  const isLoading = loading && adminAuthLoading;

  // Show loading state while auth is being determined
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Check authentication and admin role
  if (
    !isAuthenticated ||
    (currentUser && (currentUser as any).role !== "admin")
  ) {
    console.log("Admin access check:", {
      isAuthenticated,
      userRole: (currentUser as any)?.role,
      userId: (currentUser as any)?.id,
    });
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p>You need admin access to view this page.</p>
          <p className="text-sm text-gray-500 mt-2">
            Status: {isAuthenticated ? "Authenticated" : "Not authenticated"} |
            Role: {(currentUser as any)?.role || "None"} | User ID:{" "}
            {(currentUser as any)?.id || "None"}
          </p>
        </div>
      </div>
    );
  }
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompetition, setSelectedCompetition] = useState<any>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isBulkUploadDialogOpen, setIsBulkUploadDialogOpen] = useState(false);
  const [isQuestionDialogOpen, setIsQuestionDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Date range state for Google Analytics filtering
  const [gaDateRange, setGaDateRange] = useState<DateRange>({
    from: subDays(new Date(), 6), // Last 7 days (including today)
    to: new Date(),
  });

  // Utility function to convert DateRange to Google Analytics format
  const convertDateRangeToGA = (dateRange: DateRange | undefined) => {
    if (!dateRange?.from || !dateRange?.to) {
      return { startDate: "7daysAgo", endDate: "today" };
    }

    // Convert to YYYY-MM-DD format for Google Analytics API
    const startDate = format(dateRange.from, "yyyy-MM-dd");
    const endDate = format(dateRange.to, "yyyy-MM-dd");

    return { startDate, endDate };
  };

  // Get the formatted date range for queries
  const { startDate, endDate } = convertDateRangeToGA(gaDateRange);

  // Month selection for Daily New Users chart (overview tab)
  const [selectedUserGrowthMonth, setSelectedUserGrowthMonth] = useState(
    () => format(new Date(), "yyyy-MM")
  );
  const [inactiveUsersDays, setInactiveUsersDays] = useState("7");

  const userGrowthMonthOptions = useMemo(
    () => {
      const now = new Date();
      return Array.from({ length: 12 }).map((_, index) => {
        const date = subMonths(now, index);
        return {
          value: format(date, "yyyy-MM"),
          label: format(date, "MMM yyyy"),
        };
      });
    },
    []
  );

  const selectedUserGrowthMonthLabel = useMemo(
    () =>
      userGrowthMonthOptions.find(
        (month) => month.value === selectedUserGrowthMonth
      )?.label ?? format(new Date(), "MMM yyyy"),
    [selectedUserGrowthMonth, userGrowthMonthOptions]
  );

  // Betfred Manual Testing State
  const [betfredTestEmail, setBetfredTestEmail] = useState("");
  const [betfredTestResult, setBetfredTestResult] = useState<any>(null);
  const [isBetfredTesting, setIsBetfredTesting] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [selectedCompetitionForStats, setSelectedCompetitionForStats] =
    useState<any>(null);
  const [selectedCompetitionForEdit, setSelectedCompetitionForEdit] =
    useState<any>(null);
  const [selectedUserForStats, setSelectedUserForStats] = useState<any>(null);
  const [selectedUserForProfile, setSelectedUserForProfile] =
    useState<any>(null);
  const [isCompetitionStatsDialogOpen, setIsCompetitionStatsDialogOpen] =
    useState(false);
  const [isCompetitionEditDialogOpen, setIsCompetitionEditDialogOpen] =
    useState(false);
  const [isUserStatsDialogOpen, setIsUserStatsDialogOpen] = useState(false);
  const [isUserProfileDialogOpen, setIsUserProfileDialogOpen] = useState(false);
  const [preSelectedCompetitionId, setPreSelectedCompetitionId] = useState<
    number | null
  >(null);
  const [questionSource, setQuestionSource] = useState("custom");
  const [isVendorDialogOpen, setIsVendorDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<any>(null);
  const [preSelectedVendor, setPreSelectedVendor] = useState<any>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // 2FA state management
  const [showTwoFactorVerification, setShowTwoFactorVerification] =
    useState(false);
  const [twoFactorVerified, setTwoFactorVerified] = useState(false);

  // Define missing mutations to prevent compilation errors
  const addGlobalToCompetitionMutation = { mutate: () => {}, isPending: false };
  const addBrandToCompetitionMutation = { mutate: () => {}, isPending: false };
  const addBehavioralToCompetitionMutation = {
    mutate: () => {},
    isPending: false,
  };
  const editVendorMutation = { mutate: () => {}, isPending: false };
  const addVendorMutation = { mutate: () => {}, isPending: false };

  // Betfred Manual Testing Mutation
  const betfredTestMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await apiRequest("POST", "/api/betfred-verify-email", {
        body: { email },
      });
      return response.json();
    },
    onSuccess: (data) => {
      setBetfredTestResult(data);
      toast({
        title: "Betfred API Test Complete",
        description: data.success
          ? `Email "${betfredTestEmail}" verified successfully!`
          : `Email "${betfredTestEmail}" verification failed: ${data.message}`,
        variant: data.success ? "default" : "destructive",
      });
    },
    onError: (error: any) => {
      setBetfredTestResult({
        success: false,
        message: error.message || "API test failed",
      });
      toast({
        title: "Betfred API Test Failed",
        description: error.message || "Failed to test Betfred API",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsBetfredTesting(false);
    },
  });

  const handleBetfredTest = () => {
    if (!betfredTestEmail.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter an email address to test",
        variant: "destructive",
      });
      return;
    }
    setIsBetfredTesting(true);
    setBetfredTestResult(null);
    betfredTestMutation.mutate(betfredTestEmail.trim());
  };

  const handleLogout = async () => {
    try {
      // Clear all query cache first
      queryClient.clear();
      // Then sign out (this handles navigation internally)
      await signOut();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Organized menu structure with expandable sections
  const [expandedSections, setExpandedSections] = useState<string[]>([
    "main",
    "crm",
    "sms",
  ]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const menuSections = [
    {
      id: "main",
      label: "Dashboard",
      icon: Home,
      alwaysExpanded: true,
      items: [
        { id: "overview", label: "Overview", icon: Home },
        { id: "wain", label: "WAIN AI Chat", icon: Brain },
        { id: "analysis", label: "AI Analysis", icon: MessageCircle },
      ],
    },
    {
      id: "content",
      label: "Content Management",
      icon: Trophy,
      items: [
        { id: "competitions", label: "Competitions", icon: Trophy },
        { id: "challenges", label: "Challenges", icon: Target },
        { id: "questions", label: "Questions", icon: HelpCircle },
        {
          id: "question-categorization",
          label: "Question Categorization",
          icon: Brain,
        },
        { id: "winners", label: "Winner Selection", icon: Award },
        { id: "terms-conditions", label: "Terms & Conditions", icon: FileText },
        { id: "competition-coupons", label: "Coupons", icon: Gift },
      ],
    },
    {
      id: "users",
      label: "User Management",
      icon: Users,
      items: [
        { id: "user-data", label: "User Data Management", icon: Users },
        { id: "user-profile", label: "User Profile Intelligence", icon: User },
        { id: "segmentation", label: "User Segmentation", icon: Target },
        { id: "crm-criteria", label: "User Criteria", icon: Filter },
        { id: "fraud-detection", label: "User Fraud Detection", icon: Shield },
        {
          id: "competition-users",
          label: "Competition Specific Users",
          icon: Trophy,
        },
        { id: "bulk-import", label: "Bulk User Registration", icon: Upload },
        {
          id: "admin-users",
          label: "Admin Users",
          icon: ShieldCheck,
          superAdminOnly: true,
        },
      ],
    },
    {
      id: "crm",
      label: "Marketing Automation",
      icon: Zap,
      items: [
        { id: "journey-builder", label: "User Journeys", icon: Map },
        { id: "crm-campaigns", label: "Campaigns", icon: Zap },
        { id: "email-reports", label: "Email Reports", icon: BarChart3 },
        { id: "crm-analytics", label: "CRM Analytics", icon: BarChart3 },
        { id: "crm-templates", label: "AWS & SMS Templates", icon: Mail },
        { id: "triggers", label: "Trigger Management", icon: Zap },
        { id: "crm-conversions", label: "Bulk Import Conversions", icon: FileText },
        { id: "send-emails", label: "AWS SES Test Sender", icon: Send },
      ],
    },
    {
      id: "sms",
      label: "SMS Management",
      icon: Phone,
      items: [
        { id: "sms-config", label: "SMS & OTP Config", icon: Settings },
        { id: "sms-invite-tracking", label: "Invite Tracking", icon: Send },
        {
          id: "sms-journey-tracking",
          label: "Journey SMS Tracking",
          icon: GitBranch,
        },
      ],
    },
    {
      id: "analytics",
      label: "Analytics & Reporting",
      icon: BarChart3,
      items: [
        { id: "analytics", label: "Platform Analytics", icon: BarChart3 },
        { id: "google-analytics", label: "Google Analytics", icon: Search },
      ],
    },
    {
      id: "integrations",
      label: "Integrations & Tools",
      icon: ExternalLink,
      items: [
        { id: "betfred", label: "Betfred Management", icon: ExternalLink },
        {
          id: "betfred-unique",
          label: "Betfred Unique Verified",
          icon: CheckCircle,
        },
        { id: "betfred-testing", label: "Betfred API Testing", icon: Zap },
      ],
    },
    {
      id: "social",
      label: "Social Media Management",
      icon: Share2,
      items: [
        { id: "social", label: "Social Media", icon: Share2 },
        {
          id: "facebook-lead-forms",
          label: "Facebook Lead Form",
          icon: FileText,
        },
        {
          id: "facebook-testing",
          label: "Facebook Connection Test",
          icon: CheckCircle,
        },
      ],
    },
    {
      id: "system",
      label: "System & Settings",
      icon: Settings,
      items: [
        { id: "profile", label: "Profile & Settings", icon: User },
        { id: "roadmap", label: "Roadmap", icon: Map },
      ],
    },
  ];

  // Remove the redirect useEffect - let the App.tsx routing handle authentication
  // The admin dashboard should load directly for authenticated admin users

  // 2FA status check - use longer stale time to prevent frequent re-verification
  const { data: twoFactorStatus, isLoading: twoFactorLoading } = useQuery({
    queryKey: ["/api/admin/2fa/status"],
    enabled: isAuthenticated && currentUser?.role === "admin",
    retry: 3,
    staleTime: 30 * 60 * 1000, // 30 minutes - prevent frequent re-checking
    refetchOnMount: false, // Don't refetch when component mounts again
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
  });

  // Check if 2FA verification is needed - only when status changes
  useEffect(() => {
    if (
      twoFactorStatus &&
      isAuthenticated &&
      (currentUser as any)?.role === "admin"
    ) {
      console.log("[ADMIN DASHBOARD] 2FA Status:", twoFactorStatus);
      if ((twoFactorStatus as any).requiresSetup) {
        // User needs to set up 2FA - show in profile tab
        console.log("[ADMIN DASHBOARD] 2FA setup required");
        setActiveTab("profile");
      } else if (
        (twoFactorStatus as any).requiresVerification &&
        !twoFactorVerified
      ) {
        // User needs to verify 2FA - only show modal if not already showing
        console.log("[ADMIN DASHBOARD] 2FA verification required");
        if (!showTwoFactorVerification) {
          setShowTwoFactorVerification(true);
        }
      } else {
        console.log("[ADMIN DASHBOARD] 2FA checks passed");
        // Hide modal if verification passed
        setShowTwoFactorVerification(false);
        setTwoFactorVerified(true);
      }
    }
  }, [
    twoFactorStatus,
    isAuthenticated,
    currentUser,
    twoFactorVerified,
    showTwoFactorVerification,
  ]);

  // Individual API queries for admin dashboard data
  const { data: stats, isLoading: statsLoading } = useQuery<StatsData>({
    queryKey: ["/api/admin/stats"],
    enabled:
      isAuthenticated &&
      (currentUser as any)?.role === "admin" &&
      !showTwoFactorVerification,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: users, isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ["/api/admin/users"],
    enabled:
      isAuthenticated &&
      (currentUser as any)?.role === "admin" &&
      !showTwoFactorVerification,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: competitions, isLoading: competitionsLoading } = useQuery<
    Competition[]
  >({
    queryKey: ["/api/admin/competitions"],
    enabled:
      isAuthenticated &&
      currentUser?.role === "admin" &&
      !showTwoFactorVerification,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: analytics, isLoading: analyticsLoading } =
    useQuery<AnalyticsData>({
      queryKey: ["/api/admin/analytics"],
      enabled:
        isAuthenticated &&
        (currentUser as any)?.role === "admin" &&
        !showTwoFactorVerification,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    });

  // Google Analytics real data queries with date range filtering
  const {
    data: gaMetrics,
    isLoading: gaMetricsLoading,
    error: gaMetricsError,
  } = useQuery({
    queryKey: ["/api/admin/google-analytics/metrics", startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const response = await apiRequest(
        "GET",
        `/api/admin/google-analytics/metrics?${params}`
      );
      return response.json();
    },
    enabled:
      isAuthenticated &&
      currentUser?.role === "admin" &&
      !showTwoFactorVerification &&
      activeTab === "google-analytics" &&
      !!import.meta.env.VITE_GA_MEASUREMENT_ID,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    staleTime: 4 * 60 * 1000, // Data is fresh for 4 minutes
  });

  const { data: gaTrafficSources, isLoading: gaTrafficLoading } = useQuery({
    queryKey: [
      "/api/admin/google-analytics/traffic-sources",
      startDate,
      endDate,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const response = await apiRequest(
        "GET",
        `/api/admin/google-analytics/traffic-sources?${params}`
      );
      return response.json();
    },
    enabled:
      isAuthenticated &&
      currentUser?.role === "admin" &&
      !showTwoFactorVerification &&
      activeTab === "google-analytics" &&
      !!import.meta.env.VITE_GA_MEASUREMENT_ID,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 4 * 60 * 1000,
  });

  const { data: gaTopPages, isLoading: gaPagesLoading } = useQuery({
    queryKey: ["/api/admin/google-analytics/top-pages", startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const response = await apiRequest(
        "GET",
        `/api/admin/google-analytics/top-pages?${params}`
      );
      return response.json();
    },
    enabled:
      isAuthenticated &&
      currentUser?.role === "admin" &&
      !showTwoFactorVerification &&
      activeTab === "google-analytics" &&
      !!import.meta.env.VITE_GA_MEASUREMENT_ID,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 4 * 60 * 1000,
  });

  const { data: gaCountries, isLoading: gaCountriesLoading } = useQuery({
    queryKey: ["/api/admin/google-analytics/countries", startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const response = await apiRequest(
        "GET",
        `/api/admin/google-analytics/countries?${params}`
      );
      return response.json();
    },
    enabled:
      isAuthenticated &&
      currentUser?.role === "admin" &&
      !showTwoFactorVerification &&
      activeTab === "google-analytics" &&
      !!import.meta.env.VITE_GA_MEASUREMENT_ID,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 4 * 60 * 1000,
  });

  const { data: gaDevices, isLoading: gaDevicesLoading } = useQuery({
    queryKey: ["/api/admin/google-analytics/devices", startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const response = await apiRequest(
        "GET",
        `/api/admin/google-analytics/devices?${params}`
      );
      return response.json();
    },
    enabled:
      isAuthenticated &&
      currentUser?.role === "admin" &&
      !showTwoFactorVerification &&
      activeTab === "google-analytics" &&
      !!import.meta.env.VITE_GA_MEASUREMENT_ID,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 4 * 60 * 1000,
  });

  const { data: monthlyMetrics, isLoading: metricsLoading } = useQuery({
    queryKey: ["/api/admin/monthly-metrics", selectedUserGrowthMonth],
    queryFn: async () => {
      const params = new URLSearchParams({ month: selectedUserGrowthMonth });
      const response = await apiRequest(
        "GET",
        `/api/admin/monthly-metrics?${params.toString()}`
      );
      return response.json();
    },
    enabled:
      isAuthenticated &&
      currentUser?.role === "admin" &&
      !showTwoFactorVerification,
    staleTime: 300 * 1000, // 5 minutes for metrics
    refetchOnWindowFocus: false,
  });

  const { data: dailyEntryActivity, isLoading: dailyEntryLoading } = useQuery({
    queryKey: ["/api/admin/daily-entry-activity", selectedUserGrowthMonth],
    queryFn: async () => {
      const params = new URLSearchParams({ month: selectedUserGrowthMonth });
      const response = await apiRequest(
        "GET",
        `/api/admin/daily-entry-activity?${params.toString()}`
      );
      return response.json();
    },
    enabled:
      isAuthenticated &&
      currentUser?.role === "admin" &&
      !showTwoFactorVerification,
    staleTime: 300 * 1000, // 5 minutes for daily entry data
    refetchOnWindowFocus: false,
  });

  const { data: inactiveUsersSeries, isLoading: inactiveUsersSeriesLoading } =
    useQuery({
      queryKey: ["/api/admin/inactive-users/series", inactiveUsersDays],
      queryFn: async () => {
        const response = await apiRequest(
          "GET",
          `/api/admin/inactive-users/series?days=${inactiveUsersDays}`
        );
        return response.json();
      },
      enabled:
      isAuthenticated &&
      currentUser?.role === "admin" &&
      !showTwoFactorVerification &&
      activeTab === "overview",
    });

  const {
    data: inactiveUsers30PlusSummary,
    isLoading: inactiveUsers30PlusLoading,
  } = useQuery({
    queryKey: ["/api/admin/inactive-users/30plus-summary"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        "/api/admin/inactive-users/30plus-summary"
      );
      return response.json();
    },
    enabled:
      isAuthenticated &&
      currentUser?.role === "admin" &&
      !showTwoFactorVerification &&
      activeTab === "overview",
    staleTime: 24 * 60 * 60 * 1000, // refresh at most once per day
    refetchOnWindowFocus: false,
  });

  const { data: globalQuestions, isLoading: globalQuestionsLoading } = useQuery(
    {
      queryKey: ["globalQuestions"],
      enabled:
        isAuthenticated &&
        currentUser?.role === "admin" &&
        !showTwoFactorVerification,
      staleTime: 300 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const { data: brandQuestions, isLoading: brandQuestionsLoading } = useQuery({
    queryKey: ["brandQuestions"],
    enabled:
      isAuthenticated &&
      currentUser?.role === "admin" &&
      !showTwoFactorVerification,
    staleTime: 300 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: behavioralQuestions, isLoading: behavioralQuestionsLoading } =
    useQuery({
      queryKey: ["behavioralQuestions"],
      enabled:
        isAuthenticated &&
        currentUser?.role === "admin" &&
        !showTwoFactorVerification,
      staleTime: 300 * 1000,
      refetchOnWindowFocus: false,
    });

  const { data: vendors, isLoading: vendorsLoading } = useQuery({
    queryKey: ["vendors"],
    enabled:
      isAuthenticated &&
      currentUser?.role === "admin" &&
      !showTwoFactorVerification,
    staleTime: 300 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: bugs, isLoading: bugsLoading } = useQuery({
    queryKey: ["bugs"],
    enabled:
      isAuthenticated &&
      currentUser?.role === "admin" &&
      !showTwoFactorVerification,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: betfredSignups, isLoading: betfredSignupsLoading } = useQuery<
    any[]
  >({
    queryKey: ["betfredSignups"],
    enabled:
      isAuthenticated &&
      (currentUser as any)?.role === "admin" &&
      !showTwoFactorVerification,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: recentActivity, isLoading: recentActivityLoading } = useQuery<
    any[]
  >({
    queryKey: ["/api/admin/recent-activity"],
    enabled:
      isAuthenticated &&
      (currentUser as any)?.role === "admin" &&
      !showTwoFactorVerification,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: adminProfile, isLoading: profileLoading } = useQuery<User>({
    queryKey: ["/api/user/profile"],
    enabled:
      isAuthenticated &&
      (currentUser as any)?.role === "admin" &&
      !showTwoFactorVerification,
    staleTime: 300 * 1000,
    refetchOnWindowFocus: false,
  });

  // Combine loading states
  const dashboardLoading =
    statsLoading ||
    usersLoading ||
    competitionsLoading ||
    analyticsLoading ||
    metricsLoading ||
    globalQuestionsLoading ||
    brandQuestionsLoading ||
    behavioralQuestionsLoading ||
    vendorsLoading ||
    bugsLoading ||
    betfredSignupsLoading ||
    profileLoading ||
    recentActivityLoading;

  // Create dashboard object for compatibility with existing code
  const dashboard = {
    stats,
    users,
    competitions,
    analytics,
    monthlyMetrics,
    globalQuestions,
    brandQuestions,
    behavioralQuestions,
    vendors,
    bugs,
    betfredSignups,
    profile: adminProfile,
  };

  // 2FA enforcement is now handled by individual query error handling

  const {
    data: detailedAnalytics,
    isLoading: detailedAnalyticsLoading,
    error: detailedAnalyticsError,
  } = useQuery<DetailedAnalyticsData>({
    queryKey: ["/api/admin/detailed-analytics"],
    enabled:
      activeTab === "analytics" &&
      !!currentUser &&
      (currentUser as any).role === "admin",
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Daily entry / active user data for selected month
  const dailyEntryData = dailyEntryActivity || [];

  // Extract monthly metrics for graphs - now includes real data
  const userGrowthData = (monthlyMetrics as any)?.userGrowth || [];
  const dailyActiveUsersData = dailyEntryData || [];
  const competitionGrowthData =
    (monthlyMetrics as any)?.competitionGrowth || [];
  const entryActivityData = (monthlyMetrics as any)?.entryActivity || [];

  const inactiveUsers30PlusCount =
    inactiveUsers30PlusSummary?.totalInactive30Plus ?? 0;
  const inactiveUsers30PlusSeries =
    inactiveUsers30PlusSummary?.series || [];

  const { data: questions } = useQuery<GlobalQuestion[]>({
    queryKey: ["/api/admin/questions", selectedCompetition?.id],
    enabled: !!selectedCompetition?.id,
  });

  const { data: competitionStats } = useQuery<StatsData>({
    queryKey: [
      "/api/admin/competitions",
      selectedCompetitionForStats?.id,
      "stats",
    ],
    enabled: !!selectedCompetitionForStats?.id,
  });

  const { data: competitionEntries } = useQuery<any[]>({
    queryKey: [
      "/api/admin/competitions",
      selectedCompetitionForStats?.id,
      "entries",
    ],
    enabled: !!selectedCompetitionForStats?.id,
  });

  const { data: competitionShares } = useQuery<any[]>({
    queryKey: [
      "/api/admin/competitions",
      selectedCompetitionForStats?.id,
      "shares",
    ],
    enabled: !!selectedCompetitionForStats?.id,
  });

  const { data: userStats } = useQuery<any>({
    queryKey: ["/api/admin/users", selectedUserForStats?.id, "stats"],
    enabled: !!selectedUserForStats?.id,
  });

  const { data: userCompetitions } = useQuery<any[]>({
    queryKey: ["/api/admin/users", selectedUserForStats?.id, "competitions"],
    enabled: !!selectedUserForStats?.id,
  });

  const { data: userShares } = useQuery<any[]>({
    queryKey: ["/api/admin/users", selectedUserForStats?.id, "shares"],
    enabled: !!selectedUserForStats?.id,
  });

  // Refetch functions for individual queries
  const refetchDashboard = () =>
    queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
  const refetchCompetitions = () =>
    queryClient.invalidateQueries({ queryKey: ["/api/admin/competitions"] });
  const refetchUsers = () =>
    queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
  const refetchBugs = () =>
    queryClient.invalidateQueries({ queryKey: ["bugs"] });
  const refetchAdminProfile = () =>
    queryClient.invalidateQueries({ queryKey: ["/api/user/profile"] });

  // Initialize admin profile form
  const adminForm = useForm<AdminProfileForm>({
    resolver: zodResolver(adminProfileSchema),
    defaultValues: {
      firstName: adminProfile?.firstName || "",
      lastName: adminProfile?.lastName || "",
      phone: adminProfile?.phone || "",
      location: adminProfile?.location || "",
      interests: Array.isArray(adminProfile?.interests)
        ? adminProfile.interests.join(", ")
        : adminProfile?.interests || "",
      gender:
        (adminProfile?.gender as
          | ""
          | "male"
          | "female"
          | "other"
          | "prefer_not_to_say") || "",
      dateOfBirth: adminProfile?.dateOfBirth || "",
      marketingConsent: false,
    },
  });

  // Update form when profile data loads
  useEffect(() => {
    if (adminProfile) {
      adminForm.reset({
        firstName: adminProfile.firstName || "",
        lastName: adminProfile.lastName || "",
        phone: adminProfile.phone || "",
        location: adminProfile.location || "",
        interests: Array.isArray(adminProfile.interests)
          ? adminProfile.interests.join(", ")
          : adminProfile.interests || "",
        gender:
          (adminProfile.gender as
            | ""
            | "male"
            | "female"
            | "other"
            | "prefer_not_to_say") || "",
        dateOfBirth: adminProfile.dateOfBirth || "",
        marketingConsent: adminProfile.marketingConsent || false,
      });
    }
  }, [adminProfile, adminForm]);

  // Admin profile update mutation
  const updateAdminProfileMutation = useMutation({
    mutationFn: async (profileData: AdminProfileForm) => {
      const response = await apiRequest("PUT", "/api/admin/profile", {
        body: profileData,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/dashboard-optimized"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      setIsEditingProfile(false);
      toast({
        title: "Profile Updated",
        description: "Your admin profile has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Bug synchronization - trigger for all admin users
  const syncBugsMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/admin/sync-bugs");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/dashboard-optimized"],
      });
      toast({
        title: "Bug Sync Complete",
        description: "All admin users now have synchronized bug data.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Bug sync failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const createCompetitionMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/competitions", { body: data });
      return res.json();
    },
    onSuccess: () => {
      // Force complete refresh of all related data like the other mutations
      queryClient.removeQueries({
        queryKey: ["/api/admin/dashboard-optimized"],
      });
      queryClient.removeQueries({ queryKey: ["/api/admin/competitions"] });
      queryClient.removeQueries({ queryKey: ["/api/competitions/featured"] });

      // Force immediate refetch
      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: ["/api/admin/dashboard-optimized"],
        });
        queryClient.refetchQueries({
          queryKey: ["/api/competitions/featured"],
        });
        refetchDashboard();
        refetchCompetitions();
      }, 100);

      toast({ title: "Competition created successfully!" });
      setIsCreateDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error creating competition",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const editCompetitionMutation = useMutation({
    mutationFn: async (data: any) => {
      // Ensure all fields are properly formatted for the server
      const processedData = {
        ...data,
        // Convert dates to strings if they're Date objects
        startDate:
          data.startDate instanceof Date
            ? data.startDate.toISOString().split("T")[0]
            : data.startDate,
        endDate:
          data.endDate instanceof Date
            ? data.endDate.toISOString().split("T")[0]
            : data.endDate,
        // Ensure times are strings
        startTime: String(data.startTime || "07:00"),
        endTime: String(data.endTime || "23:59"),
        // Ensure other fields are properly typed
        title: String(data.title || ""),
        category: String(data.category || ""),
        type: String(data.type || ""),
        shareText: String(data.shareText || ""),
        prize1: String(data.prize1 || ""),
        prize2: String(data.prize2 || ""),
        prize3: String(data.prize3 || ""),
        bannerImageUrl: String(data.bannerImageUrl || ""),
      };
      console.log("Processed competition data:", processedData);
      const res = await apiRequest(
        "PUT",
        `/api/competitions/${selectedCompetitionForEdit?.id}`,
        { body: processedData }
      );
      return res.json();
    },
    onSuccess: () => {
      // Force complete refresh of all related data like the other mutations
      queryClient.removeQueries({
        queryKey: ["/api/admin/dashboard-optimized"],
      });
      queryClient.removeQueries({ queryKey: ["/api/admin/competitions"] });
      queryClient.removeQueries({ queryKey: ["/api/competitions/featured"] });

      // Force immediate refetch
      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: ["/api/admin/dashboard-optimized"],
        });
        queryClient.refetchQueries({
          queryKey: ["/api/competitions/featured"],
        });
        refetchDashboard();
        refetchCompetitions();
      }, 100);

      toast({ title: "Competition updated successfully!" });
      setIsCompetitionEditDialogOpen(false);
      setSelectedCompetitionForEdit(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error updating competition",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const duplicateCompetitionMutation = useMutation({
    mutationFn: async (competitionId: number) => {
      const res = await apiRequest(
        "POST",
        `/api/admin/competitions/${competitionId}/duplicate`
      );
      return res.json();
    },
    onSuccess: () => {
      // Force refresh competitions data with additional cache clearing
      queryClient.removeQueries({ queryKey: ["/api/admin/competitions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/competitions"] });
      refetchCompetitions();
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      toast({ title: "Competition duplicated successfully!" });
      // Delay to ensure backend has time to commit the data
      setTimeout(() => {
        refetchCompetitions();
      }, 500);
    },
    onError: (error: any) => {
      toast({
        title: "Error duplicating competition",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateCompetitionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await apiRequest("PUT", `/api/competitions/${id}`, {
        body: data,
      });
      return res.json();
    },
    onSuccess: () => {
      // Force complete refresh of all related data
      queryClient.removeQueries({
        queryKey: ["/api/admin/dashboard-optimized"],
      });
      queryClient.removeQueries({ queryKey: ["/api/admin/competitions"] });
      queryClient.removeQueries({ queryKey: ["/api/competitions/featured"] });

      // Force immediate refetch
      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: ["/api/admin/dashboard-optimized"],
        });
        queryClient.refetchQueries({
          queryKey: ["/api/competitions/featured"],
        });
        refetchDashboard();
      }, 100);

      toast({ title: "Competition updated successfully!" });
      setIsEditDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error updating competition",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteCompetitionMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/competitions/${id}`);
      // Don't try to parse JSON for 204 responses
      if (res.status === 204) {
        return {};
      }
      return res.json();
    },
    onSuccess: () => {
      // Force complete refresh of all related data
      queryClient.removeQueries({
        queryKey: ["/api/admin/dashboard-optimized"],
      });
      queryClient.removeQueries({ queryKey: ["/api/admin/competitions"] });
      queryClient.removeQueries({ queryKey: ["/api/admin/stats"] });
      queryClient.removeQueries({ queryKey: ["/api/competitions/featured"] });

      // Force immediate refetch
      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: ["/api/admin/dashboard-optimized"],
        });
        queryClient.refetchQueries({ queryKey: ["/api/admin/competitions"] });
        queryClient.refetchQueries({ queryKey: ["/api/admin/stats"] });
        refetchDashboard();
      }, 100);

      toast({ title: "Competition deleted successfully!" });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting competition",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const addQuestionMutation = useMutation({
    mutationFn: async ({
      competitionId,
      data,
    }: {
      competitionId: number;
      data: any;
    }) => {
      const res = await apiRequest(
        "POST",
        `/api/competitions/${competitionId}/questions`,
        data
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/questions", selectedCompetition.id],
      });
      toast({ title: "Question added successfully!" });
      setIsQuestionDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error adding question",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Competition creation form setup
  const competitionForm = useForm<CompetitionForm>({
    resolver: zodResolver(competitionFormSchema),
    defaultValues: {
      title: "",
      category: "general",
      type: "weekly",
      status: "draft",
      startDate: new Date().toISOString().split("T")[0],
      startTime: "07:00",
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      endTime: "23:59",
      prize1: "",
      prize2: "",
      prize3: "",
      bannerImageUrl: "",
      shareText: "",
      daysOfWeek: [],
      createFacebookForm: false,
      facebookPageId: "",
      betfredEnabled: true,
    },
  });

  // Competition edit form setup
  const competitionEditForm = useForm<CompetitionForm>({
    resolver: zodResolver(competitionFormSchema),
    defaultValues: {
      title: "",
      category: "",
      type: "",
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      prize1: "",
      prize2: "",
      prize3: "",
      bannerImageUrl: "",
      shareText: "",
      daysOfWeek: [],
      betfredEnabled: true,
    },
  });

  // Populate edit form when competition is selected
  useEffect(() => {
    if (selectedCompetitionForEdit) {
      competitionEditForm.reset({
        title: selectedCompetitionForEdit.title || "",
        category: selectedCompetitionForEdit.category || "",
        type: selectedCompetitionForEdit.type || "",
        startDate: selectedCompetitionForEdit.startDate
          ? new Date(selectedCompetitionForEdit.startDate)
              .toISOString()
              .split("T")[0]
          : new Date().toISOString().split("T")[0],
        startTime: selectedCompetitionForEdit.startTime || "07:00",
        endDate: selectedCompetitionForEdit.endDate
          ? new Date(selectedCompetitionForEdit.endDate)
              .toISOString()
              .split("T")[0]
          : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
              .toISOString()
              .split("T")[0],
        endTime: selectedCompetitionForEdit.endTime || "23:59",
        prize1: selectedCompetitionForEdit.prize1 || "",
        prize2: selectedCompetitionForEdit.prize2 || "",
        prize3: selectedCompetitionForEdit.prize3 || "",
        bannerImageUrl: selectedCompetitionForEdit.bannerImageUrl || "",
        shareText: selectedCompetitionForEdit.shareText || "",
        daysOfWeek: selectedCompetitionForEdit.daysOfWeek || [],
        betfredEnabled: selectedCompetitionForEdit.betfredEnabled !== false,
      });
    }
  }, [selectedCompetitionForEdit, competitionEditForm]);

  // Competition Users Management Tab Component
  function CompetitionUsersTab({
    preSelectedCompetitionId,
  }: {
    preSelectedCompetitionId?: number | null;
  }) {
    const [selectedCompetitionId, setSelectedCompetitionId] = useState<
      number | null
    >(null);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [isExportingUsers, setIsExportingUsers] = useState(false);
    const { toast } = useToast();
    const [withAnswersOnly, setWithAnswersOnly] = useState(false);

    // Debounce search input
    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedSearch(search);
        setPage(1); // Reset to first page on search
      }, 500);
      return () => clearTimeout(timer);
    }, [search]);

    // Fetch all competitions
    const { data: competitions } = useQuery({
      queryKey: ["/api/admin/competitions"],
    });

    // Set pre-selected competition when prop changes
    useEffect(() => {
      if (preSelectedCompetitionId) {
        setSelectedCompetitionId(preSelectedCompetitionId);
        setPage(1);
        setSearch("");
      }
    }, [preSelectedCompetitionId]);

    // Reset page when competition changes
    useEffect(() => {
      setPage(1);
      setSearch("");
    }, [selectedCompetitionId]);

    useEffect(() => {
      setPage(1);
    }, [withAnswersOnly]);

    const handleExportCompetitionUsers = async () => {
      if (!selectedCompetitionId) return;
      setIsExportingUsers(true);
      try {
        const params = new URLSearchParams();
        if (debouncedSearch) {
          params.set("search", debouncedSearch);
        }
        if (withAnswersOnly) {
          params.set("withAnswersOnly", "true");
        }
        const queryString = params.toString() ? `?${params.toString()}` : "";
        const response = await apiRequest(
          "GET",
          `/api/admin/competitions/${selectedCompetitionId}/users/export${queryString}`
        );
        const blob = await response.blob();
        const contentDisposition = response.headers.get("content-disposition");
        const filenameMatch =
          contentDisposition && contentDisposition.match(/filename="(.+)"/);
        const filename =
          filenameMatch?.[1] ||
          `competition-${selectedCompetitionId}-users.csv`;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : "Unable to export users";
        toast({
          title: "Export failed",
          description: message,
          variant: "destructive",
        });
      } finally {
        setIsExportingUsers(false);
      }
    };

    // Fetch users for selected competition with pagination
    const buildQueryUrl = () => {
      if (!selectedCompetitionId) return null;
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(debouncedSearch && { search: debouncedSearch }),
      });
      if (withAnswersOnly) {
        params.set("withAnswersOnly", "true");
      }
      return `/api/admin/competitions/${selectedCompetitionId}/users-with-answers?${params}`;
    };

    const competitionUsersQueryUrl = buildQueryUrl();

    const {
      data: usersResponse,
      isLoading,
      error,
    } = useQuery({
      queryKey: competitionUsersQueryUrl
        ? [competitionUsersQueryUrl]
        : ["competition-users-disabled"],
      enabled: !!competitionUsersQueryUrl,
    });

    // Fetch questions for selected competition
    const { data: questionsData } = useQuery({
      queryKey: [`/api/competitions/${selectedCompetitionId}/questions`],
      enabled: !!selectedCompetitionId,
    });

    const usersData = usersResponse?.data || [];
    const total = usersResponse?.total || 0;
    const totalPages = usersResponse?.totalPages || 0;

    return (
      <Card>
        <CardHeader>
          <CardTitle>Competition Users</CardTitle>
          <CardDescription>
            View users who participated in competitions and their answers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Competition Selector */}
          <div className="space-y-2">
            <Label>Select Competition</Label>
            <Select
              value={selectedCompetitionId?.toString() || ""}
              onValueChange={(value) => setSelectedCompetitionId(Number(value))}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose a competition..." />
              </SelectTrigger>
              <SelectContent>
                {competitions?.map((comp: any) => (
                  <SelectItem key={comp.id} value={comp.id.toString()}>
                    {comp.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filters and Controls */}
          {selectedCompetitionId && (
            <>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                {/* Search Input */}
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by name, email, or phone..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    id="with-answers-only"
                    checked={withAnswersOnly}
                    onCheckedChange={(checked) =>
                      setWithAnswersOnly(Boolean(checked))
                    }
                  />
                  <Label htmlFor="with-answers-only" className="text-sm mb-0">
                    Show only participants with answers
                  </Label>
                </div>

                {/* Page Size Selector */}
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600 whitespace-nowrap">
                    Show:
                  </Label>
                  <Select
                    value={limit.toString()}
                    onValueChange={(value) => {
                      setLimit(Number(value));
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                      <SelectItem value="200">200</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCompetitionUsers}
                  disabled={isExportingUsers || isLoading}
                  className="whitespace-nowrap"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {isExportingUsers ? "Exporting..." : "Export CSV"}
                </Button>
              </div>

              {isLoading && (
                <div className="flex justify-center py-8">
                  <div className="text-gray-500">Loading participants...</div>
                </div>
              )}

              {error && (
                <div className="text-center py-8 text-red-500">
                  Error loading participants: {(error as any).message}
                </div>
              )}

              {!isLoading && !error && total === 0 && (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    {debouncedSearch ? "No Results Found" : "No Participants"}
                  </p>
                  <p className="text-gray-500">
                    {debouncedSearch
                      ? "Try adjusting your search terms."
                      : "No users have entered this competition yet."}
                  </p>
                </div>
              )}

              {!isLoading && usersData && usersData.length > 0 && (
                <div className="space-y-4">
                  {/* Results Info */}
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div>
                      Showing {(page - 1) * limit + 1} to{" "}
                      {Math.min(page * limit, total)} of {total} participant
                      {total !== 1 ? "s" : ""}
                    </div>
                  </div>

                  <div className="border rounded-lg overflow-x-auto">
                    <Table className="min-w-full">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="px-2">Name</TableHead>
                          <TableHead className="px-2">Email</TableHead>
                          <TableHead className="px-2">Phone</TableHead>
                          <TableHead className="px-2">Date of Birth</TableHead>
                          {questionsData?.map((question: any) => (
                            <TableHead
                              key={question.id}
                              className="px-2 min-w-[150px]"
                            >
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger className="text-left">
                                    {question.questionText.length > 25
                                      ? `${question.questionText.substring(
                                          0,
                                          25
                                        )}...`
                                      : question.questionText}
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-md">
                                    <p>{question.questionText}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {usersData.map((user: any) => (
                          <TableRow key={user.id}>
                            <TableCell className="px-2 font-medium">
                              {user.firstName} {user.lastName}
                            </TableCell>
                            <TableCell className="px-2">{user.email}</TableCell>
                            <TableCell className="px-2">
                              {user.phone || "N/A"}
                            </TableCell>
                            <TableCell className="px-2">
                              {user.dateOfBirth
                                ? new Date(
                                    user.dateOfBirth
                                  ).toLocaleDateString()
                                : "N/A"}
                            </TableCell>
                            {questionsData?.map((question: any) => {
                              const answer = user.answers.find(
                                (a: any) => a.questionId === question.id
                              );
                              return (
                                <TableCell key={question.id} className="px-2">
                                  <span
                                    className={
                                      answer
                                        ? "text-gray-900"
                                        : "text-gray-400 italic"
                                    }
                                  >
                                    {answer ? answer.answerText : "No Answer"}
                                  </span>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t pt-4">
                      <div className="text-sm text-gray-600">
                        Page {page} of {totalPages}
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(1)}
                          disabled={page === 1}
                        >
                          <ChevronsLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(page - 1)}
                          disabled={page === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>

                        {/* Page Numbers */}
                        <div className="flex items-center gap-1">
                          {Array.from(
                            { length: Math.min(5, totalPages) },
                            (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (page <= 3) {
                                pageNum = i + 1;
                              } else if (page >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = page - 2 + i;
                              }

                              return (
                                <Button
                                  key={pageNum}
                                  variant={
                                    page === pageNum ? "default" : "outline"
                                  }
                                  size="sm"
                                  onClick={() => setPage(pageNum)}
                                  className="w-9"
                                >
                                  {pageNum}
                                </Button>
                              );
                            }
                          )}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(page + 1)}
                          disabled={page === totalPages}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(totalPages)}
                          disabled={page === totalPages}
                        >
                          <ChevronsRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  // Question Response Chart Component
  function QuestionResponseChart({ questionData }: { questionData: any }) {
    const { questionText, questionType, totalResponses, responses } =
      questionData;

    // Colors for the charts (Google Forms style)
    const COLORS = [
      "#4285F4",
      "#EA4335",
      "#FBBC04",
      "#34A853",
      "#FF6D01",
      "#46BDC6",
      "#7BAAF7",
      "#F07B72",
      "#FAD165",
      "#71C287",
    ];

    const colorForAnswer = (answer: string, index: number) => {
      const normalized = (answer || "").toLowerCase().trim();
      if (normalized === "no") return "#EA4335";
      if (normalized === "yes") return "#4285F4";
      return COLORS[index % COLORS.length];
    };

    // Decide which chart to use based on number of unique answers
    const shouldUsePieChart = responses.length <= 5;

    if (totalResponses === 0) {
      return (
        <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
          No responses yet
        </div>
      );
    }

    // For open-ended questions with many unique answers, show a table instead
    if (questionType === "open" && responses.length > 10) {
      return (
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            {totalResponses} response{totalResponses !== 1 ? "s" : ""}
          </div>
          <div className="bg-white border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Response</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                  <TableHead className="text-right">Percentage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {responses.slice(0, 20).map((resp: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell className="max-w-md truncate">
                      {resp.answer}
                    </TableCell>
                    <TableCell className="text-right">{resp.count}</TableCell>
                    <TableCell className="text-right">
                      {resp.percentage}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {responses.length > 20 && (
              <div className="p-3 text-sm text-gray-500 text-center bg-gray-50 border-t">
                Showing top 20 of {responses.length} unique responses
              </div>
            )}
          </div>
        </div>
      );
    }

    // Use Pie Chart for few options
    if (shouldUsePieChart) {
      return (
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            {totalResponses} response{totalResponses !== 1 ? "s" : ""}
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={responses}
                dataKey="count"
                nameKey="answer"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ answer, percentage }) => `${answer} (${percentage}%)`}
              >
                {responses.map((entry: any, index: number) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={colorForAnswer(entry.answer, index)}
                  />
                ))}
              </Pie>
              <RechartsTooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>

          {/* Response breakdown */}
          <div className="space-y-2">
            {responses.map((resp: any, index: number) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-gray-50 rounded"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: colorForAnswer(resp.answer, index) }}
                  />
                  <span className="text-sm">{resp.answer}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">{resp.count}</span>
                  <span className="text-sm text-gray-500">
                    {resp.percentage}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Use Bar Chart for many options
    return (
      <div className="space-y-4">
        <div className="text-sm text-gray-600">
          {totalResponses} response{totalResponses !== 1 ? "s" : ""}
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={responses} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis dataKey="answer" type="category" width={180} interval={0} />
            <RechartsTooltip />
            <Bar dataKey="count" fill="#4285F4">
              {responses.map((entry: any, index: number) => (
                <Cell
                  key={`cell-${index}`}
                  fill={colorForAnswer(entry.answer, index)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* Response breakdown */}
        <div className="space-y-2">
          {responses.map((resp: any, index: number) => (
            <div
              key={index}
              className="flex items-center justify-between p-2 bg-gray-50 rounded"
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: colorForAnswer(resp.answer, index) }}
                />
                <span className="text-sm">{resp.answer}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">{resp.count}</span>
                <span className="text-sm text-gray-500">
                  {resp.percentage}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Competition Summary Tab Component
  function CompetitionSummaryTab({
    competitionId,
    uniqueUsers,
    onViewUsers,
  }: {
    competitionId?: number;
    uniqueUsers: number;
    onViewUsers?: () => void;
  }) {
    const {
      data: responsesData,
      isLoading,
      error,
    } = useQuery({
      queryKey: [`/api/admin/competitions/${competitionId}/responses`],
      enabled: !!competitionId,
    });

    if (isLoading) {
      return (
        <div className="flex justify-center py-8">
          <div className="text-gray-500">Loading response summary...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8 text-red-500">
          Error loading responses: {(error as any).message}
        </div>
      );
    }

    if (!responsesData || responsesData.length === 0) {
      return (
        <div className="text-center py-8">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-2">
            No Questions Yet
          </p>
          <p className="text-gray-500">
            Questions haven't been added to this competition yet.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        {/* Unique Users Count at the top */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg border-2 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <UserCheck className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Unique Users
                </p>
                <p className="text-3xl font-bold text-gray-900">
                  {uniqueUsers}
                </p>
              </div>
            </div>
            {onViewUsers && (
              <Button
                variant="outline"
                onClick={onViewUsers}
                className="bg-white hover:bg-gray-50"
              >
                <Users className="h-4 w-4 mr-2" />
                View Users
              </Button>
            )}
          </div>
        </div>

        {/* Questions and Responses */}
        {responsesData.map((questionData: any, index: number) => (
          <Card key={questionData.questionId} className="border-2">
            <CardHeader>
              <CardTitle className="text-lg">Question {index + 1}</CardTitle>
              <CardDescription className="text-base mt-2">
                {questionData.questionText}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <QuestionResponseChart questionData={questionData} />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Competition Stats Component
  function CompetitionStatsContent({
    competitionId,
    onViewUsers,
  }: {
    competitionId?: number;
    onViewUsers?: () => void;
  }) {
    const {
      data: statsData,
      isLoading,
      error,
    } = useQuery({
      queryKey: [`/api/admin/competitions/${competitionId}/stats`],
      enabled: !!competitionId,
    });

    if (isLoading) {
      return (
        <div className="flex justify-center py-8">
          <div className="text-gray-500">Loading statistics...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8 text-red-500">
          Error loading statistics: {error.message}
        </div>
      );
    }

    if (!statsData) {
      return (
        <div className="text-center py-8 text-gray-500">
          No statistics available
        </div>
      );
    }

    return (
      <CompetitionSummaryTab
        competitionId={competitionId}
        uniqueUsers={statsData.uniqueUsers || 0}
        onViewUsers={onViewUsers}
      />
    );
  }

  const toggleUserStatusMutation = useMutation({
    mutationFn: async ({
      userId,
      isBanned,
    }: {
      userId: number;
      isBanned: boolean;
    }) => {
      const res = await apiRequest("PUT", `/api/admin/users/${userId}/status`, {
        body: { isBanned },
      });
      return res.json();
    },
    onSuccess: (data, variables) => {
      // Force complete refresh of dashboard data
      queryClient.removeQueries({
        queryKey: ["/api/admin/dashboard-optimized"],
      });
      queryClient.removeQueries({ queryKey: ["/api/admin/users"] });

      // Force immediate refetch with updated user data
      setTimeout(() => {
        refetchDashboard();
        queryClient.refetchQueries({
          queryKey: ["/api/admin/dashboard-optimized"],
        });
        queryClient.refetchQueries({ queryKey: ["/api/admin/users"] });
      }, 200);

      const statusText = variables.isBanned ? "deactivated" : "activated";
      toast({ title: `User account ${statusText} successfully!` });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating user status",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Question Import Mutation
  const importQuestionsMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiRequest("POST", "/api/admin/questions/import", {
        body: formData as any,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      setImportResult(data);
      // Refresh all question queries
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/global-questions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/brand-questions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/behavioral-questions"],
      });

      toast({
        title: "Questions Import Complete",
        description: data.message,
        variant: data.success ? "default" : "destructive",
      });
    },
    onError: (error: any) => {
      setImportResult({ success: false, message: error.message });
      toast({
        title: "Import Failed",
        description: error.message || "Failed to import questions",
        variant: "destructive",
      });
    },
  });

  const createUserMutation = useMutation({
    mutationFn: async (userData: any) => {
      const res = await apiRequest("POST", "/api/admin/create-admin-user", {
        body: userData,
      });
      return res.json();
    },
    onSuccess: () => {
      // Refresh user data
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/dashboard-optimized"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({
        title: "Admin user created successfully!",
        description: "The new admin account has been created and activated.",
      });
      // Close dialog by finding and clicking the close button
      setTimeout(() => {
        const dialogClose = document.querySelector(
          '[role="dialog"] button[aria-label="Close"]'
        ) as HTMLButtonElement;
        if (dialogClose) dialogClose.click();
      }, 100);
    },
    onError: (error: any) => {
      toast({
        title: "Error creating admin user",
        description: error.message || "Failed to create admin user account",
        variant: "destructive",
      });
    },
  });

  // Handle 2FA verification flow
  const handleTwoFactorSuccess = () => {
    console.log(
      "[ADMIN DASHBOARD] 2FA verification success callback triggered"
    );
    setTwoFactorVerified(true);
    setShowTwoFactorVerification(false);
    // Invalidate and refetch 2FA status to get updated state
    queryClient.invalidateQueries({ queryKey: ["/api/admin/2fa/status"] });
    // Refetch dashboard data after successful verification
    refetchDashboard();
    toast({
      title: "2FA Verified",
      description: "Access granted to admin dashboard",
    });
  };

  if (isLoading || twoFactorLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Debug logging for 2FA modal state
  console.log("[ADMIN DASHBOARD] Modal state:", {
    showTwoFactorVerification,
    userRole: user?.role,
    shouldShow: showTwoFactorVerification && user?.role === "admin",
  });

  // Show the two-factor verification modal if admin requires 2FA verification
  if (showTwoFactorVerification && user?.role === "admin") {
    console.log("[ADMIN DASHBOARD] Rendering 2FA verification modal");
    return (
      <TwoFactorVerification
        onVerificationSuccess={handleTwoFactorSuccess}
        onCancel={() => {
          setShowTwoFactorVerification(false);
          // Redirect to homepage
          window.location.href = "/";
        }}
      />
    );
  }

  // Remove authentication check - let App.tsx handle routing
  // Admin dashboard will only be rendered for authenticated admin users via routing

  const filteredUsers = users?.filter(
    (user: any) =>
      (user.username?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (user.email?.toLowerCase() || "").includes(searchTerm.toLowerCase())
  );

  const handleCreateUser = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const userData = {
      email: formData.get("email"),
      username: formData.get("username"),
      firstName: formData.get("firstName"),
      lastName: formData.get("lastName"),
      password: formData.get("password"),
      phone: formData.get("phone") || undefined,
      role: formData.get("role") || "admin",
    };
    createUserMutation.mutate(userData);
  };

  const handleCreateCompetition = (data: CompetitionForm) => {
    // Combine start date and time
    const startDateTime = new Date(`${data.startDate}T${data.startTime}:00`);
    const endDateTime = new Date(`${data.endDate}T${data.endTime}:00`);

    const competitionData = {
      title: data.title,
      description: data.shareText || "", // Use shareText as description fallback
      category: data.category,
      type: data.type,
      status: data.status || "draft",
      daysOfWeek: data.daysOfWeek || [],
      prize1: data.prize1?.trim() || undefined,
      prize2: data.prize2?.trim() || undefined,
      prize3: data.prize3?.trim() || undefined,
      prizeValue: 0,
      startDate: startDateTime.toISOString(),
      endDate: endDateTime.toISOString(),
      startTime: data.startTime, // Send separate time field for UI display
      endTime: data.endTime, // Send separate time field for UI display
      bannerImageUrl: data.bannerImageUrl || null,
      shareText: data.shareText,
      isActive: data.status === "active",
      betfredEnabled: data.betfredEnabled !== false, // Use form value, default to true
      createFacebookForm: data.createFacebookForm || false,
      facebookPageId: data.facebookPageId?.trim() || undefined,
    };

    createCompetitionMutation.mutate(competitionData);
  };

  const handleAddQuestionToCompetition = (
    source: string,
    questionId: number
  ) => {
    if (!selectedCompetition) return;

    switch (source) {
      case "global":
        (addGlobalToCompetitionMutation as any).mutate({
          competitionId: selectedCompetition.id,
          globalQuestionId: questionId,
        });
        break;
      case "brand":
        (addBrandToCompetitionMutation as any).mutate({
          competitionId: selectedCompetition.id,
          brandQuestionId: questionId,
        });
        break;
      case "behavioral":
        (addBehavioralToCompetitionMutation as any).mutate({
          competitionId: selectedCompetition.id,
          behavioralQuestionId: questionId,
        });
        break;
    }
  };

  const handleUpdateCompetition = (formData: FormData) => {
    if (!selectedCompetition) return;

    // Get form values
    const startDate = formData.get("startDate") as string;
    const startTime = formData.get("startTime") as string;
    const endDate = formData.get("endDate") as string;
    const endTime = formData.get("endTime") as string;

    console.log("[DEBUG] Form values captured:", {
      startDate,
      startTime,
      endDate,
      endTime,
    });

    // Collect prize inputs for separate fields
    const prize1 =
      ((formData.get("prize-0") as string) || "").trim() || undefined;
    const prize2 =
      ((formData.get("prize-1") as string) || "").trim() || undefined;
    const prize3 =
      ((formData.get("prize-2") as string) || "").trim() || undefined;

    const data = {
      title: formData.get("title"),
      description: formData.get("shareText") || "", // Use shareText as description fallback
      category: formData.get("category"),
      type: formData.get("type"),
      prize1: prize1,
      prize2: prize2,
      prize3: prize3,
      prizeValue: 0,
      startDate: startDate,
      endDate: endDate,
      startTime: startTime,
      endTime: endTime,
      bannerImageUrl: formData.get("bannerImageUrl"),
      shareText: formData.get("shareText"),
      isActive: formData.get("isActive") === "true",
    };

    console.log("[DEBUG] Data being sent to API:", data);
    updateCompetitionMutation.mutate({ id: selectedCompetition.id, data });
  };

  const handleAddQuestion = (formData: FormData) => {
    const data = {
      questionText: formData.get("questionText"),
      questionType: formData.get("questionType"),
      options: formData.get("options")
        ? (formData.get("options") as string).split("\n").filter(Boolean)
        : [],
      // HIDDEN: Correct answer removed per user request
      // correctAnswer: formData.get('correctAnswer'),
      isRequired: formData.get("isRequired") === "true",
    };
    addQuestionMutation.mutate({ competitionId: selectedCompetition.id, data });
  };

  const exportUsersCSV = () => {
    if (!users?.length) return;

    const csvContent = [
      ["Username", "Email", "Created At", "Total Entries"].join(","),
      ...users.map((user: any) =>
        [
          user.username,
          user.email,
          new Date(user.createdAt).toLocaleDateString(),
          user.totalEntries || 0,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users_export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAnalyticsCSV = () => {
    if (!analytics) return;

    const csvContent = [
      ["Metric", "Value"].join(","),
      ["Facebook Shares", analytics.facebookShares || 0].join(","),
      ["X Shares", analytics.xShares || 0].join(","),
      ["WhatsApp Shares", analytics.whatsappShares || 0].join(","),
      ["Average Entries Per User", analytics.avgEntriesPerUser || 0].join(","),
      ["Most Popular Category", analytics.popularCategory || "N/A"].join(","),
      ["Active Users Today", analytics.activeUsersToday || 0].join(","),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "analytics_export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Sidebar component for reuse
  const SidebarContent = () => (
    <>
      {/* Admin Header in Sidebar */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white p-4 sm:p-6 text-center">
        <h1 className="text-xl sm:text-2xl font-bold mb-2">Admin Dashboard</h1>
        <p className="text-indigo-100 text-xs sm:text-sm">
          Manage competitions, users, and analytics
        </p>
      </div>

      {/* Admin Menu */}
      <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-600">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
          Admin Menu
        </h3>
      </div>
      <nav className="p-2 flex-1 overflow-y-auto">
        {menuSections.map((section) => {
          const SectionIcon = section.icon;
          const isExpanded =
            section.alwaysExpanded || expandedSections.includes(section.id);

          return (
            <div key={section.id} className="mb-2">
              {/* Section Header */}
              <button
                onClick={() =>
                  !section.alwaysExpanded && toggleSection(section.id)
                }
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors ${
                  !section.alwaysExpanded
                    ? "hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    : ""
                }`}
                disabled={section.alwaysExpanded}
              >
                <SectionIcon className="h-4 w-4" />
                <span className="flex-1">{section.label}</span>
                {!section.alwaysExpanded && (
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  />
                )}
              </button>

              {/* Section Items */}
              {isExpanded && (
                <div className="ml-2 mt-1 space-y-1">
                  {section.items.map((item) => {
                    // Hide superadmin-only items from regular admins
                    if (
                      item.superAdminOnly &&
                      (user as any)?.role !== "superadmin"
                    ) {
                      return null;
                    }

                    const ItemIcon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          setActiveTab(item.id);
                          setSidebarOpen(false); // Close mobile sidebar when item is selected
                        }}
                        className={`w-full flex items-center gap-3 px-3 sm:px-4 py-2 sm:py-3 text-left rounded-lg transition-colors mb-1 ${
                          activeTab === item.id
                            ? "bg-indigo-50 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 font-medium"
                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                        }`}
                        data-testid={`nav-${item.id}`}
                      >
                        <ItemIcon className="h-5 w-5 flex-shrink-0" />
                        <span className="text-sm font-medium">
                          {item.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User Info at Bottom */}
      <div className="mt-auto p-3 sm:p-4 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white font-medium text-sm">
              {user?.username?.[0]?.toUpperCase() || "A"}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {user?.username || "Admin"}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {user?.email || "admin@example.com"}
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            handleLogout();
            setSidebarOpen(false);
          }}
          className="w-full mt-3 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
        >
          Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      {/* Desktop Sidebar - Hidden on mobile */}
      <div className="hidden lg:flex w-64 bg-white dark:bg-gray-800 shadow-lg border-r border-gray-200 dark:border-gray-600 flex-shrink-0 flex-col">
        <SidebarContent />
      </div>

      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent
          side="left"
          className="w-64 p-0 flex flex-col dark:bg-gray-800"
        >
          <SidebarContent />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Mobile Header with Menu Button */}
        <div className="lg:hidden bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-600 p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">
                Admin Dashboard
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                Manage your platform
              </p>
            </div>
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="lg:hidden hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
            </Sheet>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {activeTab === "wain" && <WainChatInterface />}

          {activeTab === "overview" && (
            <div className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Card className="cursor-help">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">
                            Total PrizeDrop Users
                          </CardTitle>
                          <Users className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold">
                            {stats?.totalUsers || 0}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            All registered users
                          </p>
                        </CardContent>
                      </Card>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Total number of users registered on the PrizeDrop platform
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Card className="cursor-help">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">
                            Total Active Users
                          </CardTitle>
                          <UserCheck className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold">
                            {stats?.totalActiveUsers || 0}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Active platform users
                          </p>
                        </CardContent>
                      </Card>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Active users excluding bulk imports (source != bulk_import)
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Card className="cursor-help">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">
                            Betfred Unique Verified
                          </CardTitle>
                          <ExternalLink className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold">
                            {stats?.uniqueVerifiedBetfredSignups || 0}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Unique verified signups
                          </p>
                        </CardContent>
                      </Card>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Number of unique users with verified Betfred account signups
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Card className="cursor-help">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">
                            Total Shares
                          </CardTitle>
                          <TrendingUp className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold">
                            {stats?.totalShares || 0}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Social media shares
                          </p>
                        </CardContent>
                      </Card>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Total social media shares and referrals from all users
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {/* Performance Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <div>
                      <CardTitle>Daily New Users</CardTitle>
                      <CardDescription>
                        {selectedUserGrowthMonthLabel}
                      </CardDescription>
                    </div>
                    <Select
                      value={selectedUserGrowthMonth}
                      onValueChange={setSelectedUserGrowthMonth}
                    >
                      <SelectTrigger className="w-[140px] sm:w-[180px]">
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent>
                        {userGrowthMonthOptions.map((month) => (
                          <SelectItem key={month.value} value={month.value}>
                            {month.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardHeader>
                  <CardContent>
                    {userGrowthData && userGrowthData.length > 0 ? (
                      <div className="h-48 sm:h-48 sm:h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={userGrowthData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="day" />
                            <YAxis />
                            <RechartsTooltip />
                            <Area
                              type="monotone"
                              dataKey="users"
                              stroke="#3b82f6"
                              fill="#3b82f6"
                              fillOpacity={0.3}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-48 sm:h-48 sm:h-64 text-gray-500">
                        <TrendingUp className="h-8 w-8 mr-2" />
                        <span>No user growth data available</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <div>
                      <CardTitle>Daily Active Users</CardTitle>
                      <CardDescription>
                        {selectedUserGrowthMonthLabel}
                      </CardDescription>
                    </div>
                    <Select
                      value={selectedUserGrowthMonth}
                      onValueChange={setSelectedUserGrowthMonth}
                    >
                      <SelectTrigger className="w-[140px] sm:w-[180px]">
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent>
                        {userGrowthMonthOptions.map((month) => (
                          <SelectItem key={month.value} value={month.value}>
                            {month.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardHeader>
                  <CardContent>
                    {dailyActiveUsersData && dailyActiveUsersData.length > 0 ? (
                      <div className="h-48 sm:h-48 sm:h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={dailyActiveUsersData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="day" />
                            <YAxis />
                            <RechartsTooltip />
                            <Line
                              type="monotone"
                              dataKey="activeUsers"
                              stroke="#10b981"
                              strokeWidth={2}
                              name="Active Users"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-48 sm:h-64 text-gray-500">
                        <BarChart3 className="h-8 w-8 mr-2" />
                        <span>No active user data available</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <div>
                      <CardTitle>30+ Day Inactive Users</CardTitle>
                      <CardDescription>
                        Users who have not logged in for more than 30 days, over the last 7 days
                      </CardDescription>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {inactiveUsers30PlusLoading ? (
                      <div className="flex items-center justify-center h-48 sm:h-64 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                      </div>
                    ) : (
                      <div className="h-48 sm:h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={inactiveUsers30PlusSeries}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis allowDecimals={false} />
                            <RechartsTooltip />
                            <Bar
                              dataKey="count"
                              name="30+ day inactive users"
                              fill="#ef4444"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                        <div className="mt-2 text-sm text-gray-600">
                          Latest: {inactiveUsers30PlusCount.toLocaleString()} users inactive 30+ days
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <div>
                      <CardTitle>Inactive vs Active Users</CardTitle>
                      <CardDescription>
                        Users inactive for N+ days vs remaining active users
                      </CardDescription>
                    </div>
                    <Select
                      value={inactiveUsersDays}
                      onValueChange={setInactiveUsersDays}
                    >
                      <SelectTrigger className="w-[140px] sm:w-[180px]">
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">Last 7 days</SelectItem>
                        <SelectItem value="30">Last 30 days</SelectItem>
                        <SelectItem value="90">Last 90 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </CardHeader>
                  <CardContent>
                    {inactiveUsersSeriesLoading ? (
                      <div className="flex items-center justify-center h-48 sm:h-64 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                      </div>
                    ) : inactiveUsersSeries?.series &&
                      inactiveUsersSeries.series.length > 0 ? (
                      <div className="h-48 sm:h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={inactiveUsersSeries.series}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <RechartsTooltip />
                            <Bar
                              dataKey="inactive"
                              name="Inactive Users"
                              fill="#ef4444"
                            />
                            <Bar
                              dataKey="active"
                              name="Active Users"
                              fill="#10b981"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-48 sm:h-64 text-gray-500">
                        <UserX className="h-8 w-8 mr-2" />
                        <span>No inactive user data available</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {activeTab === "challenges" && <ChallengeManagementTab />}

          {activeTab === "competitions" && (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Competition Management</CardTitle>
                    <CardDescription>
                      Create, edit, and manage competitions
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Dialog
                      open={isBulkUploadDialogOpen}
                      onOpenChange={setIsBulkUploadDialogOpen}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Bulk Upload
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <BulkUploadComponent
                          onClose={() => setIsBulkUploadDialogOpen(false)}
                        />
                      </DialogContent>
                    </Dialog>
                    <Dialog
                      open={isCreateDialogOpen}
                      onOpenChange={setIsCreateDialogOpen}
                    >
                      <DialogTrigger asChild>
                        <Button className="primary-gradient">
                          <Plus className="h-4 w-4 mr-2" />
                          Create Competition
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Create New Competition</DialogTitle>
                          <DialogDescription>
                            Fill in the details for the new competition
                          </DialogDescription>
                        </DialogHeader>
                        <Form {...competitionForm}>
                          <form
                            onSubmit={competitionForm.handleSubmit(
                              handleCreateCompetition
                            )}
                            className="space-y-4"
                          >
                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={competitionForm.control}
                                name="title"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Title</FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        data-testid="input-competition-title"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={competitionForm.control}
                                name="category"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Category</FormLabel>
                                    <Select
                                      onValueChange={field.onChange}
                                      defaultValue={field.value}
                                    >
                                      <FormControl>
                                        <SelectTrigger data-testid="select-competition-category">
                                          <SelectValue placeholder="Select category" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="gaming">
                                          Gaming
                                        </SelectItem>
                                        <SelectItem value="lifestyle">
                                          Lifestyle
                                        </SelectItem>
                                        <SelectItem value="sports">
                                          Sports
                                        </SelectItem>
                                        <SelectItem value="fitness">
                                          Fitness
                                        </SelectItem>
                                        <SelectItem value="travel">
                                          Travel
                                        </SelectItem>
                                        <SelectItem value="general">
                                          General
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={competitionForm.control}
                                name="type"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Type</FormLabel>
                                    <Select
                                      onValueChange={field.onChange}
                                      defaultValue={field.value}
                                    >
                                      <FormControl>
                                        <SelectTrigger data-testid="select-competition-type">
                                          <SelectValue placeholder="Select type" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="daily">
                                          Daily
                                        </SelectItem>
                                        <SelectItem value="weekly">
                                          Weekly
                                        </SelectItem>
                                        <SelectItem value="monthly">
                                          Monthly
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={competitionForm.control}
                                name="status"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Status</FormLabel>
                                    <Select
                                      onValueChange={field.onChange}
                                      defaultValue={field.value}
                                    >
                                      <FormControl>
                                        <SelectTrigger data-testid="select-competition-status">
                                          <SelectValue placeholder="Select status" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="draft">
                                          Draft
                                        </SelectItem>
                                        <SelectItem value="active">
                                          Active
                                        </SelectItem>
                                        <SelectItem value="ended">
                                          Ended
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={competitionForm.control}
                                name="startDate"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Start Date</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="date"
                                        {...field}
                                        data-testid="input-competition-start-date"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={competitionForm.control}
                                name="endDate"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>End Date</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="date"
                                        {...field}
                                        data-testid="input-competition-end-date"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={competitionForm.control}
                                name="startTime"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Start Time</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="time"
                                        {...field}
                                        data-testid="input-competition-start-time"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={competitionForm.control}
                                name="endTime"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>End Time</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="time"
                                        {...field}
                                        data-testid="input-competition-end-time"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <div className="space-y-3">
                              <FormField
                                control={competitionForm.control}
                                name="prize1"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Prize 1 (Main Prize)</FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        placeholder="e.g., R100 Airtime"
                                        data-testid="input-competition-prize1"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={competitionForm.control}
                                name="prize2"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Prize 2 (Optional)</FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        placeholder="e.g., R50 Voucher"
                                        data-testid="input-competition-prize2"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={competitionForm.control}
                                name="prize3"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Prize 3 (Optional)</FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        placeholder="e.g., R25 Credit"
                                        data-testid="input-competition-prize3"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <FormField
                              control={competitionForm.control}
                              name="bannerImageUrl"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>
                                    Banner Image URL (Optional)
                                  </FormLabel>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      placeholder="https://example.com/image.jpg"
                                      data-testid="input-competition-banner"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={competitionForm.control}
                              name="shareText"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Tagline (Card Display)</FormLabel>
                                  <FormControl>
                                    <Textarea
                                      {...field}
                                      placeholder="Join this exciting competition for your chance to win amazing prizes!"
                                      data-testid="input-competition-sharetext"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={competitionForm.control}
                              name="termsAndConditionsDialog"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>
                                    Competition-Specific Terms & Conditions
                                    (Optional)
                                  </FormLabel>
                                  <FormControl>
                                    <Textarea
                                      {...field}
                                      placeholder="Enter specific terms and conditions for this competition. Users will see this in a popup dialogue before entering. Leave blank to use global T&Cs only."
                                      className="min-h-[120px]"
                                      data-testid="input-competition-terms-dialog"
                                    />
                                  </FormControl>
                                  <div className="text-sm text-muted-foreground">
                                    Optional: Add competition-specific terms
                                    that will appear in a popup dialogue. If
                                    left blank, only the global terms will be
                                    shown.
                                  </div>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <div className="space-y-4 border-t pt-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="text-sm font-medium">
                                    Betfred Bonus Integration
                                  </h4>
                                  <p className="text-sm text-muted-foreground">
                                    Allow users to get bonus entries by signing up with Betfred
                                  </p>
                                </div>
                              </div>
                              <FormField
                                control={competitionForm.control}
                                name="betfredEnabled"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                    <div className="space-y-0.5">
                                      <FormLabel className="text-base">
                                        Enable Betfred Bonus
                                      </FormLabel>
                                      <div className="text-sm text-muted-foreground">
                                        Users can earn 50 bonus entries by verifying their Betfred account
                                      </div>
                                    </div>
                                    <FormControl>
                                      <Switch
                                        checked={field.value !== false}
                                        onCheckedChange={field.onChange}
                                        data-testid="switch-betfred-enabled"
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                            </div>

                            <div className="space-y-4 border-t pt-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="text-sm font-medium">
                                    Facebook Lead Ads Integration
                                  </h4>
                                  <p className="text-sm text-muted-foreground">
                                    Automatically create a Facebook Lead Form
                                    for this competition
                                  </p>
                                </div>
                              </div>
                              <FormField
                                control={competitionForm.control}
                                name="createFacebookForm"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                    <div className="space-y-0.5">
                                      <FormLabel className="text-base">
                                        Auto-create Facebook Lead Form
                                      </FormLabel>
                                      <div className="text-sm text-muted-foreground">
                                        Create a Facebook form with all
                                        competition questions
                                      </div>
                                    </div>
                                    <FormControl>
                                      <Switch
                                        checked={field.value || false}
                                        onCheckedChange={field.onChange}
                                        data-testid="switch-create-facebook-form"
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                              {competitionForm.watch("createFacebookForm") && (
                                <FormField
                                  control={competitionForm.control}
                                  name="facebookPageId"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>
                                        Facebook Page ID (Optional)
                                      </FormLabel>
                                      <FormControl>
                                        <Input
                                          {...field}
                                          placeholder="Leave blank to use default page"
                                          data-testid="input-facebook-page-id"
                                        />
                                      </FormControl>
                                      <div className="text-sm text-muted-foreground">
                                        If not provided, the default Facebook
                                        page will be used
                                      </div>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              )}
                            </div>

                            <div className="flex justify-end gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  setIsCreateDialogOpen(false);
                                  competitionForm.reset();
                                }}
                                data-testid="button-cancel-competition"
                              >
                                Cancel
                              </Button>
                              <Button
                                type="submit"
                                disabled={createCompetitionMutation.isPending}
                                data-testid="button-submit-competition"
                              >
                                {createCompetitionMutation.isPending
                                  ? "Creating..."
                                  : "Create Competition"}
                              </Button>
                            </div>
                          </form>
                        </Form>
                      </DialogContent>
                    </Dialog>

                    {/* Competition Stats Dialog */}
                    <Dialog
                      open={isCompetitionStatsDialogOpen}
                      onOpenChange={setIsCompetitionStatsDialogOpen}
                    >
                      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Competition Statistics</DialogTitle>
                          <DialogDescription>
                            Response summary for "
                            {selectedCompetitionForStats?.title}"
                          </DialogDescription>
                        </DialogHeader>
                        <CompetitionStatsContent
                          competitionId={selectedCompetitionForStats?.id}
                          onViewUsers={() => {
                            setIsCompetitionStatsDialogOpen(false);
                            setPreSelectedCompetitionId(
                              selectedCompetitionForStats?.id
                            );
                            setActiveTab("competition-users");
                          }}
                        />
                      </DialogContent>
                    </Dialog>

                    {/* Edit Competition Dialog */}
                    <Dialog
                      open={isCompetitionEditDialogOpen}
                      onOpenChange={setIsCompetitionEditDialogOpen}
                    >
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Edit Competition</DialogTitle>
                          <DialogDescription>
                            Update the competition details
                          </DialogDescription>
                        </DialogHeader>
                        <Form {...competitionEditForm}>
                          <form
                            onSubmit={competitionEditForm.handleSubmit((data) =>
                              editCompetitionMutation.mutate(data)
                            )}
                            className="space-y-4"
                          >
                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={competitionEditForm.control}
                                name="title"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Title</FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        placeholder="Competition title"
                                        data-testid="input-edit-competition-title"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={competitionEditForm.control}
                                name="category"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Category</FormLabel>
                                    <Select
                                      onValueChange={field.onChange}
                                      value={field.value}
                                    >
                                      <FormControl>
                                        <SelectTrigger data-testid="select-edit-competition-category">
                                          <SelectValue placeholder="Select category" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="gaming">
                                          Gaming
                                        </SelectItem>
                                        <SelectItem value="lifestyle">
                                          Lifestyle
                                        </SelectItem>
                                        <SelectItem value="sports">
                                          Sports
                                        </SelectItem>
                                        <SelectItem value="technology">
                                          Technology
                                        </SelectItem>
                                        <SelectItem value="entertainment">
                                          Entertainment
                                        </SelectItem>
                                        <SelectItem value="food">
                                          Food & Drink
                                        </SelectItem>
                                        <SelectItem value="travel">
                                          Travel
                                        </SelectItem>
                                        <SelectItem value="fitness">
                                          Fitness
                                        </SelectItem>
                                        <SelectItem value="fashion">
                                          Fashion
                                        </SelectItem>
                                        <SelectItem value="music">
                                          Music
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={competitionEditForm.control}
                                name="type"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Type</FormLabel>
                                    <Select
                                      onValueChange={field.onChange}
                                      value={field.value}
                                    >
                                      <FormControl>
                                        <SelectTrigger data-testid="select-edit-competition-type">
                                          <SelectValue placeholder="Select type" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="daily">
                                          Daily
                                        </SelectItem>
                                        <SelectItem value="weekly">
                                          Weekly
                                        </SelectItem>
                                        <SelectItem value="monthly">
                                          Monthly
                                        </SelectItem>
                                        <SelectItem value="special">
                                          Special Event
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={competitionEditForm.control}
                                name="shareText"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Share Text</FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        placeholder="Share text for social media"
                                        data-testid="input-edit-competition-share-text"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={competitionEditForm.control}
                                name="startDate"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Start Date</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="date"
                                        {...field}
                                        data-testid="input-edit-competition-start-date"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={competitionEditForm.control}
                                name="startTime"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Start Time</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="time"
                                        {...field}
                                        data-testid="input-edit-competition-start-time"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={competitionEditForm.control}
                                name="endDate"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>End Date</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="date"
                                        {...field}
                                        data-testid="input-edit-competition-end-date"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={competitionEditForm.control}
                                name="endTime"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>End Time</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="time"
                                        {...field}
                                        data-testid="input-edit-competition-end-time"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <div className="space-y-3">
                              <FormField
                                control={competitionEditForm.control}
                                name="prize1"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Prize 1 (Main Prize)</FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        placeholder="e.g., R100 Airtime"
                                        data-testid="input-edit-competition-prize1"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={competitionEditForm.control}
                                name="prize2"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Prize 2 (Optional)</FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        placeholder="e.g., R50 Voucher"
                                        data-testid="input-edit-competition-prize2"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={competitionEditForm.control}
                                name="prize3"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Prize 3 (Optional)</FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        placeholder="e.g., R25 Credit"
                                        data-testid="input-edit-competition-prize3"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <FormField
                              control={competitionEditForm.control}
                              name="bannerImageUrl"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>
                                    Banner Image URL (Optional)
                                  </FormLabel>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      placeholder="https://example.com/banner.jpg"
                                      data-testid="input-edit-competition-banner"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <div className="space-y-4 border-t pt-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="text-sm font-medium">
                                    Betfred Bonus Integration
                                  </h4>
                                  <p className="text-sm text-muted-foreground">
                                    Allow users to get bonus entries by signing up with Betfred
                                  </p>
                                </div>
                              </div>
                              <FormField
                                control={competitionEditForm.control}
                                name="betfredEnabled"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                    <div className="space-y-0.5">
                                      <FormLabel className="text-base">
                                        Enable Betfred Bonus
                                      </FormLabel>
                                      <div className="text-sm text-muted-foreground">
                                        Users can earn 50 bonus entries by verifying their Betfred account
                                      </div>
                                    </div>
                                    <FormControl>
                                      <Switch
                                        checked={field.value !== false}
                                        onCheckedChange={field.onChange}
                                        data-testid="switch-edit-betfred-enabled"
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                            </div>

                            <div className="flex justify-end gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  setIsCompetitionEditDialogOpen(false);
                                  setSelectedCompetitionForEdit(null);
                                  competitionEditForm.reset();
                                }}
                                data-testid="button-cancel-edit-competition"
                              >
                                Cancel
                              </Button>
                              <Button
                                type="submit"
                                disabled={editCompetitionMutation.isPending}
                                data-testid="button-submit-edit-competition"
                              >
                                {editCompetitionMutation.isPending
                                  ? "Updating..."
                                  : "Update Competition"}
                              </Button>
                            </div>
                          </form>
                        </Form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Prize</TableHead>
                      <TableHead>Entries</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {competitions?.map((competition: any) => (
                      <TableRow key={competition.id}>
                        <TableCell className="font-medium">
                          {competition.title}
                        </TableCell>
                        <TableCell>{competition.category}</TableCell>
                        <TableCell>{competition.type}</TableCell>
                        <TableCell>{competition.prize}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-sm font-semibold"
                            onClick={() => {
                              setPreSelectedCompetitionId(competition.id);
                              setActiveTab("competition-users");
                            }}
                          >
                            <span>
                              {competition.participantCount ??
                                competition.entryCount ??
                                0}
                            </span>
                            <span className="text-xs text-gray-500">
                              View participants
                            </span>
                          </Button>
                        </TableCell>
                        <TableCell>
                          {competition.endDate
                            ? new Date(
                                competition.endDate
                              ).toLocaleDateString() +
                              ", " +
                              (competition.endTime ||
                                new Date(
                                  competition.endDate
                                ).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false,
                                }))
                            : ""}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const status = getCompetitionStatus(competition);
                            return (
                              <Badge variant={getStatusBadgeVariant(status)}>
                                {status}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedCompetitionForStats(competition);
                                setIsCompetitionStatsDialogOpen(true);
                              }}
                              title="View detailed statistics"
                              data-testid="button-stats"
                            >
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedCompetitionForEdit(competition);
                                setIsCompetitionEditDialogOpen(true);
                              }}
                              title="Edit competition"
                              data-testid="button-edit-competition"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedCompetition(competition);
                                setActiveTab("questions");
                              }}
                            >
                              <HelpCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                duplicateCompetitionMutation.mutate(
                                  competition.id
                                )
                              }
                              title="Duplicate competition"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="outline" size="sm">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Delete Competition
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete this
                                    competition? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() =>
                                      deleteCompetitionMutation.mutate(
                                        competition.id
                                      )
                                    }
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {activeTab === "users" && (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>User Management</CardTitle>
                    <CardDescription>
                      View and manage user accounts
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline">
                          <Plus className="h-4 w-4 mr-2" />
                          Add Admin User
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Create New Admin User</DialogTitle>
                          <DialogDescription>
                            Add a new admin user account to the system
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreateUser}>
                          <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="email">Email</Label>
                                <Input name="email" type="email" required />
                              </div>
                              <div>
                                <Label htmlFor="username">Username</Label>
                                <Input name="username" required />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="firstName">First Name</Label>
                                <Input name="firstName" required />
                              </div>
                              <div>
                                <Label htmlFor="lastName">Last Name</Label>
                                <Input name="lastName" required />
                              </div>
                            </div>
                            <div>
                              <Label htmlFor="password">Password</Label>
                              <Input name="password" type="password" required />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="phone">Phone (optional)</Label>
                                <Input name="phone" type="tel" />
                              </div>
                              <div>
                                <Label htmlFor="role">Role</Label>
                                <select
                                  name="role"
                                  className="w-full p-2 border border-gray-300 rounded-md"
                                >
                                  <option value="admin">Admin</option>
                                  <option value="superadmin">Superadmin</option>
                                </select>
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline">
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              disabled={createUserMutation?.isPending}
                            >
                              {createUserMutation?.isPending
                                ? "Creating..."
                                : "Create Admin User"}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                    <Button onClick={exportUsersCSV} variant="outline">
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Username</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created At</TableHead>
                      <TableHead>Total Entries</TableHead>
                      {/* Actions column removed - functionality available in User Data Management tab */}
                      {/* <TableHead>Actions</TableHead> */}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers?.map((user: any) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          {user.username}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge
                            variant={user.isBanned ? "destructive" : "default"}
                          >
                            {user.isBanned ? "Deactivated" : "Active"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(user.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{user.totalEntries || 0}</TableCell>
                        {/* Actions column removed - functionality available in User Data Management tab */}
                        {/*
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedUserForStats(user);
                                setIsUserStatsDialogOpen(true);
                              }}
                              title="View detailed analytics"
                            >
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedUserForProfile(user);
                                setIsUserProfileDialogOpen(true);
                              }}
                              title="View complete profile"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className={user.isBanned ? "text-green-600 hover:text-green-700 border-green-300 hover:border-green-400" : "text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"}
                                  title={user.isBanned ? "Activate account" : "Deactivate account"}
                                >
                                  {user.isBanned ? (
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  ) : (
                                    <Ban className="h-4 w-4" />
                                  )}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    {user.isBanned ? "Activate User Account" : "Deactivate User Account"}
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {user.isBanned
                                      ? `Are you sure you want to activate ${user.username}'s account? They will regain access to the platform.`
                                      : `Are you sure you want deactivate ${user.username}'s account? They will lose access to the platform.`
                                    }
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => toggleUserStatusMutation.mutate({ userId: user.id, isBanned: !user.isBanned })}
                                    className={user.isBanned ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
                                  >
                                    {user.isBanned ? "Activate" : "Deactivate"}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                        */}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {activeTab === "google-analytics" && <GoogleAnalyticsDashboard />}

          {activeTab === "tracking-codes" && (
            <div>
              <AdminTrackingCodes />
            </div>
          )}

          {activeTab === "vendors" && (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Vendor Management</CardTitle>
                    <CardDescription>
                      Manage vendors and link them to brand questions
                    </CardDescription>
                  </div>
                  <Dialog
                    open={isVendorDialogOpen}
                    onOpenChange={setIsVendorDialogOpen}
                  >
                    <DialogTrigger asChild>
                      <Button onClick={() => setEditingVendor(null)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Vendor
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>
                          {editingVendor ? "Edit Vendor" : "Add New Vendor"}
                        </DialogTitle>
                        <DialogDescription>
                          Create or edit vendor information that can be linked
                          to brand questions
                        </DialogDescription>
                      </DialogHeader>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          const formData = new FormData(
                            e.target as HTMLFormElement
                          );
                          const vendorData = {
                            name: formData.get("name") as string,
                            description: formData.get("description") as string,
                            website: formData.get("website") as string,
                            industry: formData.get("industry") as string,
                          };

                          if (editingVendor) {
                            (editVendorMutation as any).mutate({
                              id: editingVendor.id,
                              ...vendorData,
                            });
                          } else {
                            (addVendorMutation as any).mutate(vendorData);
                          }
                        }}
                      >
                        <div className="grid gap-4 py-4">
                          <div>
                            <Label htmlFor="name">Vendor Name</Label>
                            <Input
                              name="name"
                              defaultValue={editingVendor?.name || ""}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="description">Description</Label>
                            <Textarea
                              name="description"
                              defaultValue={editingVendor?.description || ""}
                            />
                          </div>
                          <div>
                            <Label htmlFor="website">Website</Label>
                            <Input
                              name="website"
                              type="url"
                              defaultValue={editingVendor?.website || ""}
                            />
                          </div>
                          <div>
                            <Label htmlFor="industry">Industry</Label>
                            <Select
                              name="industry"
                              defaultValue={editingVendor?.industry || ""}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select industry" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="technology">
                                  Technology
                                </SelectItem>
                                <SelectItem value="fashion">Fashion</SelectItem>
                                <SelectItem value="food">
                                  Food & Beverage
                                </SelectItem>
                                <SelectItem value="automotive">
                                  Automotive
                                </SelectItem>
                                <SelectItem value="healthcare">
                                  Healthcare
                                </SelectItem>
                                <SelectItem value="finance">Finance</SelectItem>
                                <SelectItem value="retail">Retail</SelectItem>
                                <SelectItem value="entertainment">
                                  Entertainment
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsVendorDialogOpen(false)}
                          >
                            Cancel
                          </Button>
                          <Button type="submit">
                            {editingVendor ? "Update" : "Create"} Vendor
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Website</TableHead>
                      <TableHead>Brand Questions</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(vendors as any[])?.map((vendor: any) => (
                      <TableRow key={vendor.id}>
                        <TableCell className="font-medium">
                          {vendor.name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{vendor.industry}</Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {vendor.description}
                        </TableCell>
                        <TableCell>
                          {vendor.website && (
                            <a
                              href={vendor.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              Visit
                            </a>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {vendor.brand_questions_count || 0} questions
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setPreSelectedVendor(vendor);
                                setActiveTab("questions");
                              }}
                              title="Add Brand Question"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingVendor(vendor);
                                setIsVendorDialogOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="outline" size="sm">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Delete Vendor
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete this vendor?
                                    This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => {
                                      apiRequest(
                                        "DELETE",
                                        `/api/admin/vendors/${vendor.id}`
                                      )
                                        .then(() => {
                                          queryClient.invalidateQueries({
                                            queryKey: ["/api/admin/vendors"],
                                          });
                                          toast({
                                            title:
                                              "Vendor deleted successfully!",
                                          });
                                        })
                                        .catch((error) => {
                                          toast({
                                            title: "Error deleting vendor",
                                            description: error.message,
                                            variant: "destructive",
                                          });
                                        });
                                    }}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {activeTab === "betfred" && <BetfredManagementTab />}

          {activeTab === "betfred-unique" && <BetfredUniqueVerifiedTab />}

          {activeTab === "terms-conditions" && <TermsAndConditionsTab />}

          {activeTab === "user-data" && (
            <UserDataManagementTab
              onViewProfile={(user) => {
                setSelectedUserForProfile(user);
                setActiveTab("user-profile");
              }}
            />
          )}

          {activeTab === "bulk-import" && <BulkImportPage />}

          {activeTab === "user-profile" && (
            <UserProfileTab
              user={selectedUserForProfile}
              onBack={() => setActiveTab("user-data")}
            />
          )}

          {activeTab === "fraud-detection" && <FraudDetectionTab />}

          {activeTab === "competition-users" && (
            <CompetitionUsersTab
              preSelectedCompetitionId={preSelectedCompetitionId}
            />
          )}

          {activeTab === "segmentation" && <AdminSegmentation />}

          {activeTab === "analytics" && <SocialSharesAnalyticsTab />}

          {activeTab === "social" && <SocialMediaManagement />}

          {activeTab === "profile" && (
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-4">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <User className="h-6 w-6" />
                  Profile & Settings
                </h2>
                <p className="text-gray-600 mt-1">
                  Manage admin profile and system settings
                </p>
              </div>

              <Tabs defaultValue="profile" className="space-y-4">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="profile">My Profile</TabsTrigger>
                  <TabsTrigger value="settings">System Settings</TabsTrigger>
                </TabsList>

                <TabsContent value="profile" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>My Profile</CardTitle>
                          <CardDescription>
                            Admin account information and personal details
                          </CardDescription>
                        </div>
                        <div className="flex gap-2">
                          {!isEditingProfile ? (
                            <Button
                              onClick={() => setIsEditingProfile(true)}
                              variant="outline"
                              className="flex items-center gap-2"
                            >
                              <Edit className="h-4 w-4" />
                              Edit Profile
                            </Button>
                          ) : (
                            <>
                              <Button
                                onClick={() => {
                                  setIsEditingProfile(false);
                                  adminForm.reset();
                                }}
                                variant="outline"
                                className="flex items-center gap-2"
                              >
                                <X className="h-4 w-4" />
                                Cancel
                              </Button>
                              <Button
                                onClick={adminForm.handleSubmit((data) =>
                                  updateAdminProfileMutation.mutate(data)
                                )}
                                disabled={updateAdminProfileMutation.isPending}
                                className="flex items-center gap-2"
                              >
                                <Save className="h-4 w-4" />
                                Save Changes
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Account Details - Non-editable */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <User className="h-5 w-5" />
                              Account Details
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div>
                              <Label className="text-sm font-medium text-gray-600">
                                Username
                              </Label>
                              <p className="text-sm font-mono bg-gray-100 px-3 py-2 rounded">
                                {user?.username || "N/A"}
                              </p>
                            </div>
                            <div>
                              <Label className="text-sm font-medium text-gray-600">
                                Email
                              </Label>
                              <p className="text-sm font-mono bg-gray-100 px-3 py-2 rounded">
                                {user?.email || "N/A"}
                              </p>
                            </div>
                            <div>
                              <Label className="text-sm font-medium text-gray-600">
                                Role
                              </Label>
                              <Badge
                                variant="default"
                                className="bg-indigo-600"
                              >
                                Admin
                              </Badge>
                            </div>
                            <div>
                              <Label className="text-sm font-medium text-gray-600">
                                Account Status
                              </Label>
                              <Badge variant="default" className="bg-green-600">
                                Active
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Personal Information - Editable */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <Settings className="h-5 w-5" />
                              Personal Information
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {!isEditingProfile ? (
                              <div className="space-y-4">
                                <div>
                                  <Label className="text-sm font-medium text-gray-600">
                                    Full Name
                                  </Label>
                                  <p className="text-sm bg-gray-50 px-3 py-2 rounded">
                                    {adminProfile?.firstName ||
                                    adminProfile?.lastName
                                      ? `${adminProfile.firstName || ""} ${
                                          adminProfile.lastName || ""
                                        }`.trim()
                                      : "Not provided"}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-sm font-medium text-gray-600">
                                    Phone
                                  </Label>
                                  <p className="text-sm bg-gray-50 px-3 py-2 rounded">
                                    {adminProfile?.phone || "Not provided"}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-sm font-medium text-gray-600">
                                    Location
                                  </Label>
                                  <p className="text-sm bg-gray-50 px-3 py-2 rounded">
                                    {adminProfile?.location || "Not provided"}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-sm font-medium text-gray-600">
                                    Date of Birth
                                  </Label>
                                  <p className="text-sm bg-gray-50 px-3 py-2 rounded">
                                    {adminProfile?.dateOfBirth
                                      ? new Date(
                                          adminProfile.dateOfBirth
                                        ).toLocaleDateString()
                                      : "Not provided"}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-sm font-medium text-gray-600">
                                    Gender
                                  </Label>
                                  <p className="text-sm bg-gray-50 px-3 py-2 rounded">
                                    {adminProfile?.gender || "Not provided"}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-sm font-medium text-gray-600">
                                    Interests
                                  </Label>
                                  <p className="text-sm bg-gray-50 px-3 py-2 rounded">
                                    {adminProfile?.interests || "Not provided"}
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <Form {...adminForm}>
                                <form className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <FormField
                                      control={adminForm.control}
                                      name="firstName"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>First Name</FormLabel>
                                          <FormControl>
                                            <Input
                                              placeholder="Enter first name"
                                              {...field}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={adminForm.control}
                                      name="lastName"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Last Name</FormLabel>
                                          <FormControl>
                                            <Input
                                              placeholder="Enter last name"
                                              {...field}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                  <FormField
                                    control={adminForm.control}
                                    name="phone"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Phone Number</FormLabel>
                                        <FormControl>
                                          <Input
                                            placeholder="Enter phone number"
                                            {...field}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={adminForm.control}
                                    name="location"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Location</FormLabel>
                                        <FormControl>
                                          <Input
                                            placeholder="Enter location"
                                            {...field}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={adminForm.control}
                                    name="dateOfBirth"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Date of Birth</FormLabel>
                                        <FormControl>
                                          <Input type="date" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={adminForm.control}
                                    name="gender"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Gender</FormLabel>
                                        <Select
                                          onValueChange={field.onChange}
                                          defaultValue={field.value}
                                        >
                                          <FormControl>
                                            <SelectTrigger>
                                              <SelectValue placeholder="Select gender" />
                                            </SelectTrigger>
                                          </FormControl>
                                          <SelectContent>
                                            <SelectItem value="male">
                                              Male
                                            </SelectItem>
                                            <SelectItem value="female">
                                              Female
                                            </SelectItem>
                                            <SelectItem value="other">
                                              Other
                                            </SelectItem>
                                            <SelectItem value="prefer_not_to_say">
                                              Prefer not to say
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={adminForm.control}
                                    name="interests"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Interests</FormLabel>
                                        <FormControl>
                                          <Textarea
                                            placeholder="Enter your interests"
                                            {...field}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={adminForm.control}
                                    name="marketingConsent"
                                    render={({ field }) => (
                                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                        <div className="space-y-0.5">
                                          <FormLabel className="text-base">
                                            Marketing Communications
                                          </FormLabel>
                                          <div className="text-sm text-muted-foreground">
                                            Receive updates about new features
                                            and platform news
                                          </div>
                                        </div>
                                        <FormControl>
                                          <Switch
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                          />
                                        </FormControl>
                                      </FormItem>
                                    )}
                                  />
                                </form>
                              </Form>
                            )}
                          </CardContent>
                        </Card>
                      </div>

                      {/* 2FA Security Section */}
                      <div className="mt-6">
                        <TwoFactorSetup />
                      </div>

                      {/* Admin Activity Stats */}
                      <div className="mt-6">
                        <Card>
                          <CardHeader>
                            <CardTitle>Admin Activity</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                              <div className="text-center p-4 bg-blue-50 rounded-lg">
                                <div className="text-2xl font-bold text-blue-600">
                                  {stats?.totalCompetitions || 0}
                                </div>
                                <div className="text-sm text-blue-800">
                                  Competitions Managed
                                </div>
                              </div>
                              <div className="text-center p-4 bg-green-50 rounded-lg">
                                <div className="text-2xl font-bold text-green-600">
                                  {stats?.totalUsers || 0}
                                </div>
                                <div className="text-sm text-green-800">
                                  Users Managed
                                </div>
                              </div>
                              <div className="text-center p-4 bg-blue-50 rounded-lg">
                                <div className="text-2xl font-bold text-blue-900">
                                  {stats?.totalEntries || 0}
                                </div>
                                <div className="text-sm text-blue-900">
                                  Total Entries
                                </div>
                              </div>
                              <div className="text-center p-4 bg-orange-50 rounded-lg">
                                <div className="text-2xl font-bold text-orange-600">
                                  {stats?.totalShares || 0}
                                </div>
                                <div className="text-sm text-orange-800">
                                  Social Shares
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Quick Actions */}
                      <div className="mt-6">
                        <Card>
                          <CardHeader>
                            <CardTitle>Quick Actions</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex flex-wrap gap-3">
                              <Button
                                variant="outline"
                                onClick={() => setActiveTab("competitions")}
                                className="flex items-center gap-2"
                              >
                                <Trophy className="h-4 w-4" />
                                Manage Competitions
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setActiveTab("users")}
                                className="flex items-center gap-2"
                              >
                                <Users className="h-4 w-4" />
                                Manage Users
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setActiveTab("analytics")}
                                className="flex items-center gap-2"
                              >
                                <BarChart3 className="h-4 w-4" />
                                View Analytics
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setActiveTab("crm-templates")}
                                className="flex items-center gap-2"
                              >
                                <Mail className="h-4 w-4" />
                                CRM Templates
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setActiveTab("crm-campaigns")}
                                className="flex items-center gap-2"
                              >
                                <Zap className="h-4 w-4" />
                                CRM Campaigns
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setActiveTab("crm-journeys")}
                                className="flex items-center gap-2"
                              >
                                <Users className="h-4 w-4" />
                                User Journeys
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setActiveTab("crm-analytics")}
                                className="flex items-center gap-2"
                              >
                                <BarChart3 className="h-4 w-4" />
                                CRM Analytics
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setActiveTab("crm-conversions")}
                                className="flex items-center gap-2"
                              >
                                <FileText className="h-4 w-4" />
                                Bulk Import Conversions
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => syncBugsMutation.mutate()}
                                disabled={syncBugsMutation.isPending}
                                className="flex items-center gap-2"
                              >
                                <RotateCcw className="h-4 w-4" />
                                {syncBugsMutation.isPending
                                  ? "Syncing..."
                                  : "Sync Bugs"}
                              </Button>
                              <Button
                                variant="outline"
                                onClick={handleLogout}
                                className="flex items-center gap-2"
                              >
                                <Target className="h-4 w-4" />
                                Sign Out
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="settings" className="space-y-4">
                  <SystemSettingsTab />
                </TabsContent>
              </Tabs>
            </div>
          )}

          {activeTab === "betfred-testing" && <BetfredAPITestingTab />}

          {activeTab === "facebook-testing" && <FacebookConnectionTestingTab />}

          {activeTab === "facebook-lead-forms" && <FacebookLeadFormsTab />}

          {activeTab === "questions" && (
            <QuestionManagementTab
              preSelectedVendor={preSelectedVendor}
              onVendorUsed={() => setPreSelectedVendor(null)}
            />
          )}

          {activeTab === "question-categorization" && (
            <QuestionCategorizationTab />
          )}

          {activeTab === "winners" && <WinnerSelectionTab />}

          {activeTab === "competition-coupons" && <CompetitionCouponsTab />}

          {activeTab === "analysis" && <AIAnalysisTab />}

          {activeTab === "journey-builder" && <JourneyBuilder />}

          {activeTab === "crm-templates" && <CRMTemplatesTab />}

          {activeTab === "crm-campaigns" && <CRMCampaignsTab />}

          {activeTab === "crm-criteria" && <CRMCriteriaTab />}

          {activeTab === "crm-journeys" && <CRMJourneysTab />}

          {activeTab === "crm-analytics" && <CRMAnalyticsTab />}

          {activeTab === "crm-conversions" && <CRMConversionsTab />}

          {activeTab === "send-emails" && <SendEmailsTab />}

          {activeTab === "email-reports" && <EmailReports />}

          {activeTab === "sms-config" && <SMSConfigurationTab />}
          {activeTab === "sms-invite-tracking" && <SMSInviteTrackingTab />}
          {activeTab === "sms-journey-tracking" && <JourneySMSTrackingTab />}

          {activeTab === "triggers" && <AdminTriggerManagement />}

          {activeTab === "roadmap" && (
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  Product Roadmap
                </h2>
                <p className="text-gray-600 mt-1">
                  Plan and track development progress
                </p>
              </div>
              <Roadmap />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
